export interface BlogSection {
  type: "h2" | "h3" | "p" | "ul" | "ol" | "callout" | "cta";
  content: string | string[];
  heading?: string;
}

export interface BlogFaq {
  q: string;
  a: string;
}

export interface BlogAuthor {
  name: string;
  role?: string;
  url?: string;
  bio?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  publishedDate: string;
  modifiedDate: string;
  author?: BlogAuthor;
  category: string;
  readTime: string;
  excerpt: string;
  coverImage?: string;
  sections: BlogSection[];
  faqs: BlogFaq[];
  relatedPosts?: { slug: string; title: string }[];
  relatedComparisons?: { slug: string; title: string }[];
  relatedGuides?: { href: string; title: string }[];
}

export function getRelatedPosts(
  post: BlogPost,
  allPosts: BlogPost[] = BLOG_POSTS,
): { slug: string; title: string }[] {
  if (post.relatedPosts && post.relatedPosts.length > 0) {
    return post.relatedPosts;
  }
  return allPosts
    .filter((p) => p.slug !== post.slug && p.category === post.category)
    .slice(0, 3)
    .map((p) => ({ slug: p.slug, title: p.title }));
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "how-to-price-building-jobs",
    title: "How to Price Building Jobs: A Practical Guide for UK Builders",
    metaTitle: "How to Price Building Jobs (UK Guide 2025)",
    metaDescription: "Learn how to price building jobs accurately. A step-by-step guide for UK builders covering labour, materials, overheads and profit margins.",
    publishedDate: "2025-02-10",
    modifiedDate: "2026-03-01",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "9 min read",
    coverImage: "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=1200&q=80",
    excerpt: "Getting your pricing right is the difference between a profitable business and one that runs itself into the ground. Here is a practical, step-by-step guide to pricing building jobs accurately as a UK builder.",
    sections: [
      {
        type: "p",
        content: "Pricing building jobs accurately is one of the most important skills a builder can develop. Price too low and you end up working for nothing; price too high and you lose the work to a competitor. Getting it right consistently is what separates builders who grow a sustainable business from those who are always chasing their next job.",
      },
      {
        type: "p",
        content: "This guide walks through the full process of quoting construction jobs in the UK, from understanding your true costs through to presenting a professional quote that wins work.",
      },
      {
        type: "h2",
        content: "Why accurate pricing matters more than you think",
      },
      {
        type: "p",
        content: "Most builders who struggle financially are not struggling because they lack work. They are struggling because they are not charging enough for the work they have. Underpricing is one of the biggest causes of business failure in the construction trade.",
      },
      {
        type: "p",
        content: "Common consequences of underpricing include: taking on jobs that leave no margin for unexpected problems; not being able to afford to pay yourself properly; having no money left to invest in tools, vehicles or training; and building a reputation as the cheapest option rather than the best option.",
      },
      {
        type: "p",
        content: "The goal of a professional building quote is not to win every job. It is to win the right jobs at prices that allow your business to thrive.",
      },
      {
        type: "h2",
        content: "The five components of any building quote",
      },
      {
        type: "p",
        content: "Every building job, whether it is a rear extension or a bathroom renovation, can be broken down into the same five cost components. Understanding each one is the foundation of accurate pricing.",
      },
      {
        type: "h3",
        content: "1. Labour costs",
      },
      {
        type: "p",
        content: 'Labour is typically the largest single cost on any building job. To price it accurately, you need to know your true <a href="/glossary#labour-rate">labour rate</a> rather than just what you want to earn. Your true day rate includes your take-home pay, national insurance, public liability insurance, tool maintenance, vehicle costs, and a proportion of time spent quoting, travelling and dealing with admin.',
      },
      {
        type: "p",
        content: "As a rough guide, many UK builders calculate their true hourly cost at between 1.5x and 2x their basic hourly rate. So if you want to earn £200 per day as take-home, your charge-out rate needs to reflect a true cost of around £300 to £400 per day to cover all associated costs.",
      },
      {
        type: "p",
        content: "For each job, estimate how many days each trade will be on site. Be realistic and allow extra time for complications. If the job is in a tight space, access is difficult, or the existing structure is unpredictable, factor that in.",
      },
      {
        type: "h3",
        content: "2. Materials",
      },
      {
        type: "p",
        content: "Materials need to be priced from current supplier quotes, not from memory or what you paid last year. Prices for timber, insulation, brickwork and steelwork all move with the market, and building on outdated figures is a reliable way to lose money.",
      },
      {
        type: "p",
        content: "Always add a materials uplift of 10 to 15 percent to cover wastage, delivery costs, and price fluctuations between quote and order. For jobs involving materials you are unfamiliar with, get a firm quote from a supplier before pricing the job.",
      },
      {
        type: "h3",
        content: "3. Plant and equipment",
      },
      {
        type: "p",
        content: "This covers hired plant such as skips, scaffolding, mobile elevated work platforms, concrete mixers and any specialist equipment. Get quotes for each hire item individually and include the full hire period, not just the minimum. Jobs often overrun, and a skip or scaffold left on site longer than planned adds to your cost.",
      },
      {
        type: "h3",
        content: "4. Subcontractors",
      },
      {
        type: "p",
        content: "If you are engaging specialist trades such as electricians, plumbers, structural engineers or decorators, get fixed quotes from them before including a figure in your own quote. Never guess what a subie will charge. Add a management margin of 10 to 15 percent on top of any subcontractor costs to cover the coordination, quality control and liability you are taking on.",
      },
      {
        type: "h3",
        content: "5. Overhead and profit",
      },
      {
        type: "p",
        content: "Overhead covers the costs of running your business that are not tied directly to a single job. This includes your vehicle, office, accounting, insurance, marketing, and any tools and equipment you buy. Divide your annual overhead by the number of billable days you work each year to get a daily overhead rate, then include that in every job quote.",
      },
      {
        type: "p",
        content: "Profit is separate from overhead and is the return you are making for the risk of running a business. A typical net profit margin for a UK builder is between 10 and 20 percent of turnover. Add your profit margin on top of all costs, not as a substitute for overhead.",
      },
      {
        type: "h2",
        content: "Step-by-step: how to estimate a construction job",
      },
      {
        type: "ol",
        content: [
          "Review the drawings, spec or scope of work in detail. If drawings are not available, carry out a full site visit and take measurements yourself.",
          "Break the job into a list of distinct work items (foundations, brickwork, steelwork, roofing, first fix, second fix and so on).",
          "For each item, estimate the quantity of materials required and the number of labour days.",
          "Price materials from current supplier quotes and add a wastage uplift.",
          "Calculate your labour cost for each item using your true day rate.",
          "Get quotes for any plant, scaffolding, skips or specialist equipment.",
          "Get fixed quotes from any subcontractors you will use.",
          "Add overhead and profit on top of total direct costs.",
          "Review the final figure and sense-check it against your experience of similar jobs.",
          'Present the quote professionally in writing, with a clear <a href="/glossary#scope-of-works">scope of works</a>, assumptions and exclusions.',
        ],
      },
      {
        type: "h2",
        content: "Common mistakes builders make when pricing jobs",
      },
      {
        type: "ul",
        content: [
          "Not visiting the site before quoting and missing access or demolition complications.",
          "Forgetting to include site setup, protection and waste disposal.",
          "Using outdated material prices from memory rather than current supplier quotes.",
          "Not accounting for programme risk on jobs with complex phasing or multiple trades.",
          "Pricing labour based on best-case productivity rather than realistic estimates.",
          "Forgetting to include overhead in the job cost, effectively subsidising the client.",
          "Not specifying exclusions clearly, which leads to disputes about what is and is not included.",
        ],
      },
      {
        type: "h2",
        content: "How to present a professional builder quote",
      },
      {
        type: "p",
        content: "A professional quote does more than give a price. It demonstrates that you understand the job, have thought it through carefully, and will deliver the work professionally. Clients use quotes to compare builders, and the quality of your quote often determines whether you win the work before the client has even met you.",
      },
      {
        type: "p",
        content: "A good builder quote includes: a clear description of the works; a detailed line-item breakdown; assumptions you have made; what is excluded; your programme (how long the job will take); your payment terms; how long the quote is valid for; and your company details.",
      },
      {
        type: "h2",
        content: "Using software to speed up and improve your quoting",
      },
      {
        type: "p",
        content: "Quoting manually takes time, and the longer it takes, the more time you spend on admin rather than on site. Many builders are now using quoting software to speed up the process without sacrificing accuracy.",
      },
      {
        type: "p",
        content: "Good builder quoting software lets you upload drawings or photos of a job, generates a structured scope of works automatically, applies UK market rates for labour and materials, and produces a professional PDF quote ready to send to the client. What used to take an evening can now take under 10 minutes.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Generate your first quote from drawings or a simple job description in under 10 minutes.",
        heading: "Generate quotes in minutes, not evenings",
      },
    ],
    faqs: [
      {
        q: "How much profit should a builder make on a job?",
        a: "Most UK builders aim for a net profit margin of 10 to 20 percent after all direct costs and overheads. Some specialist or complex work commands higher margins. The important thing is that profit is calculated on top of a full cost build-up, not as a substitute for correctly pricing your overheads.",
      },
      {
        q: "Should I include VAT in my builder quote?",
        a: "If you are VAT-registered, you must add VAT to your quote. Most residential building work in the UK is standard-rated at 20 percent. Some work qualifies for the reduced 5 percent rate, such as converting non-residential buildings to dwellings or certain energy-saving works. Always confirm the applicable rate with your accountant.",
      },
      {
        q: "How long should a builder quote be valid for?",
        a: "Most builders quote for 30 to 60 days. Beyond that, material and labour prices may have changed enough to affect your cost. State your validity period clearly on the quote so clients cannot hold you to an old price months later.",
      },
      {
        q: "What should I do if a client asks me to reduce my price?",
        a: "Explain clearly what your price includes and why each element is necessary. If you are willing to reduce the price, only do so by removing scope, not by cutting your margins. Never reduce your price just to match a competitor without understanding what they have and have not included in their quote.",
      },
      {
        q: "How do I price jobs I have not done before?",
        a: "Break the unfamiliar work into elements you understand (labour hours, materials quantities, specialist requirements) and price each one carefully. Get quotes from specialist subcontractors for any element outside your experience. Add a risk contingency of 10 to 15 percent on top of your normal price to cover unknowns.",
      },
    ],
    relatedPosts: [
      { slug: "extension-quote-template-uk", title: "Extension Quote Template: What Every UK Builder Should Include" },
      { slug: "loft-conversion-cost-estimate-uk", title: "How to Price a Loft Conversion: A UK Builder's Guide to Accurate Estimates" },
      { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders: Full Comparison" },
      { href: "/auto-quote", title: "Auto Quote: Generate a Quote from a Description in Minutes" },
      { href: "/pricing", title: "The Proper Tool Pricing: Plans from £29/month" },
      { href: "/guides/quoting-for-uk-builders", title: "Quoting for UK Builders: The Complete Guide" },
    ],
  },

  {
    slug: "builder-quote-app-uk",
    title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)",
    metaTitle: "Builder Quote App UK: Generate Professional Quotes Fast (2025)",
    metaDescription: "Find the best builder quote app for UK tradespeople. Compare features, see how to generate professional building quotes in under 10 minutes.",
    publishedDate: "2025-02-24",
    modifiedDate: "2026-03-10",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "7 min read",
    coverImage: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=1200&q=80",
    excerpt: "Managing your quoting from a phone or tablet is now possible with dedicated builder quote apps. Here is what to look for, and how the best options compare for UK builders.",
    sections: [
      {
        type: "p",
        content: "For most builders, quoting is the part of the job nobody enjoys. Sitting at a desk after a full day on site, working through a spreadsheet or trying to remember what you charged last time, is not how anyone wants to spend their evenings. A builder quote app changes that.",
      },
      {
        type: "p",
        content: "The best quoting apps for builders let you generate a professional, properly priced quote from your phone or laptop in a fraction of the time. In this guide we cover what to look for in a builder quote app, how they work, and which features matter most for UK tradespeople.",
      },
      {
        type: "h2",
        content: "What is a builder quote app?",
      },
      {
        type: "p",
        content: "A builder quote app is software designed specifically for tradespeople and construction contractors that helps them produce professional quotes quickly. Unlike generic invoicing software, a good builder quote app understands construction: it knows what items are typically needed on different job types, uses trade-specific pricing, and produces output that looks professional and covers all the right bases.",
      },
      {
        type: "p",
        content: "Builder quote apps range from basic tools that let you type up a list of items and add prices, through to more sophisticated software that can read drawings and generate a complete scope of works automatically.",
      },
      {
        type: "h2",
        content: "The problem with manual quoting",
      },
      {
        type: "p",
        content: "The traditional way to quote a job involves taking measurements on site, transferring them to a spreadsheet, working out quantities, looking up prices, and typing up a description. For a straightforward job this might take two or three hours. For a larger job, it could take a full evening or more.",
      },
      {
        type: "p",
        content: "The hidden cost of manual quoting is significant. If you spend four hours producing a quote that you have a 40 percent chance of winning, you are spending around 10 hours of quoting time for every job you land. At a charge-out rate of £35 per hour, that is £350 of unpaid time per job won. Over a year, this adds up to a substantial amount of lost earnings.",
      },
      {
        type: "p",
        content: "Manual quoting also introduces errors. A missed item, an outdated material price, or a quantity mistake can turn a profitable job into a loss. An app that does the heavy lifting reduces the risk of these costly mistakes.",
      },
      {
        type: "h2",
        content: "What to look for in a builder quote app",
      },
      {
        type: "h3",
        content: "UK-specific pricing",
      },
      {
        type: "p",
        content: "This is non-negotiable for UK builders. Labour rates, material costs and trade pricing vary significantly between the UK and other markets. An app built on US or Australian pricing data will give you figures that are either too high or too low for your local market. Look for software that uses UK market rates and, ideally, lets you adjust those rates to reflect your own costs and local conditions.",
      },
      {
        type: "h3",
        content: "Structured scope of works",
      },
      {
        type: "p",
        content: "A quote that just says 'extension: £45,000' tells the client nothing and creates disputes when the job starts. A good builder quote app generates a structured line-item breakdown with descriptions of each element of the work. This protects you legally, sets clear expectations with the client, and helps you track costs during the job.",
      },
      {
        type: "h3",
        content: "Professional PDF output",
      },
      {
        type: "p",
        content: "The quote you send to a client is often the first impression they have of your business. A professionally formatted PDF with your company details, project information, scope of works, and clear pricing looks completely different from a typed email with a number at the bottom. Clients use the quality of your quote as a proxy for the quality of your work.",
      },
      {
        type: "h3",
        content: "Speed of production",
      },
      {
        type: "p",
        content: "The faster you can produce a quote, the sooner the client has it, and the better your chances of winning the work. Many clients commission the first builder who comes back to them with a professional response. Look for apps that can generate a first draft from minimal input, which you can then review and adjust.",
      },
      {
        type: "h3",
        content: "Drawing and document reading",
      },
      {
        type: "p",
        content: "The most advanced builder quote apps can read architectural drawings, structural engineer reports and site photos to extract the information needed to produce a quote. This removes the need for you to manually transfer measurements and specifications from paper documents to a screen.",
      },
      {
        type: "h2",
        content: "How The Proper Tool works as a builder quote app",
      },
      {
        type: "p",
        content: "The Proper Tool is designed specifically for UK builders working on residential construction projects. You can start a quote in three ways: upload drawings or PDFs, upload site photos, or simply describe the job in plain text.",
      },
      {
        type: "p",
        content: "From that input, the tool generates a complete scope of works with line-item descriptions, quantities, and pricing based on UK market rates across 31 trade categories. The scope is reviewed by a built-in validation system that checks for missing items, quantity inconsistencies, and pricing that is out of range for the type of work.",
      },
      {
        type: "p",
        content: "You can then review and edit every line item before exporting a professionally formatted PDF quote ready to send to your client. The whole process typically takes under 10 minutes.",
      },
      {
        type: "ul",
        content: [
          "Reads structural drawings, plans, site photos and plain text descriptions",
          "Generates structured scope of works with descriptions and quantities",
          "Applies UK market rates across 31 trade categories",
          "Built-in validation checks for scope completeness and pricing accuracy",
          "Professional PDF export with your company branding",
          "Works on any device, including phone and tablet",
          "Three pricing tiers to suit sole traders through to growing firms",
        ],
      },
      {
        type: "h2",
        content: "Quoting on the go: tips for builders using a quote app on site",
      },
      {
        type: "p",
        content: "One of the best uses of a builder quote app is on a site visit. Instead of taking notes to transfer later, you can start building the quote as you walk around the job with the client. This has several advantages.",
      },
      {
        type: "p",
        content: "First, you capture information in context, so you are less likely to miss items. Second, you can give the client an indicative figure before you leave, which shows you are responsive and organised. Third, the quote is ready to finalise when you get back to the office or van rather than waiting until the evening.",
      },
      {
        type: "p",
        content: "When using any quote app on site, keep a few things in mind. Always do a final review before sending. Site visits surface information quickly, but pricing decisions still deserve a few minutes of careful thought. Check that your scope of works is complete and that your prices reflect your current cost base, not what you charged a year ago.",
      },
      {
        type: "cta",
        content: "Start your first quote free. No credit card required. Works on any device.",
        heading: "Ready to quote faster?",
      },
    ],
    faqs: [
      {
        q: "Can I use a builder quote app on my phone on site?",
        a: "Yes. The best builder quote apps are fully web-based and work on any device including smartphones and tablets. This means you can start a quote during or immediately after a site visit rather than waiting until you are back at a desk.",
      },
      {
        q: "Do builder quote apps use UK pricing?",
        a: "Not all of them. Many general quoting or invoicing tools are built for a global market and do not reflect UK labour rates or material costs. When choosing an app for building quotes, look specifically for software built for the UK construction market with UK-specific pricing.",
      },
      {
        q: "How accurate are the prices generated by a builder quote app?",
        a: "Accuracy depends on the quality of the app and how much information you give it. A good builder quote app will get you to a solid starting point quickly, which you then review and adjust based on your knowledge of the specific job, your suppliers and your local market. Think of it as a structured first draft, not a finished quote you send without reviewing.",
      },
      {
        q: "Is there a free builder quote app available in the UK?",
        a: "Some apps offer a free tier with limited functionality. The Proper Tool offers a 14-day free trial with no card required, so you can test the tool before committing to a paid plan. Paid plans start from £29 per month.",
      },
      {
        q: "What types of building jobs can a quote app handle?",
        a: "The best builder quote apps handle a wide range of project types including extensions, loft conversions, bathroom and kitchen renovations, structural alterations, new builds and more. The Proper Tool is built for residential construction across all these job types.",
      },
    ],
    relatedPosts: [
      { slug: "ai-quoting-software-uk-builders", title: "AI Quoting Software for UK Builders: What It Does and Whether It's Worth It" },
      { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedComparisons: [
      { slug: "tradify-alternative", title: "The Proper Tool vs Tradify" },
      { slug: "jobber-alternative", title: "The Proper Tool vs Jobber" },
      { slug: "price-the-job-alternative", title: "The Proper Tool vs Price the Job" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders: Full Comparison" },
      { href: "/auto-quote", title: "Auto Quote: Generate a Quote from a Description in Minutes" },
      { href: "/ai-quoting-software", title: "AI Quoting Software for UK Builders" },
    ],
  },

  {
    slug: "quoting-tool-for-small-builders",
    title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?",
    metaTitle: "Manual vs Automated Builder Quoting: Which Saves More Time and Money?",
    metaDescription: "Still pricing jobs from a spreadsheet or Word doc? This guide compares manual quoting with automated builder quoting software and shows what the switch actually looks like.",
    publishedDate: "2025-03-10",
    modifiedDate: "2026-03-15",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "6 min read",
    excerpt: "Most UK builders still price jobs manually. This guide breaks down the real cost of that approach and shows when automated quoting starts to make sense.",
    sections: [
      {
        type: "p",
        content: "Most builders price jobs the same way they always have: a spreadsheet, a Word document, or in some cases a pad and a pen. It works. You know your rates, you know your jobs, and you can usually get something out the door in an evening. The question is whether that approach is actually serving your business, or whether the habit is costing you more than you realise.",
      },
      {
        type: "p",
        content: "This guide compares manual quoting with automated quoting software for UK builders, not to tell you one is always better, but to help you understand what each approach actually costs and what the realistic trade-offs are.",
      },
      {
        type: "h2",
        content: "What manual quoting actually involves",
      },
      {
        type: "p",
        content: "Manual quoting means building each quote from scratch: reviewing the drawings or site notes, deciding what work is involved, pricing each item against your own rates or experience, and then formatting it into something you can send to a client. For a typical extension or structural job, that process takes anywhere from two to five hours.",
      },
      {
        type: "p",
        content: "The hidden costs of that approach add up quickly:",
      },
      {
        type: "ul",
        content: [
          "Time: two to five hours per quote, usually done in evenings or weekends",
          "Inconsistency: rates vary between quotes depending on how recently you checked them",
          "Missed items: when you are building a scope from memory, something almost always gets left out",
          "Presentation: a Word document or basic spreadsheet rarely looks as professional as a printed quote from a dedicated tool",
          "No record: manual quotes are hard to review, revise or learn from over time",
        ],
      },
      {
        type: "h2",
        content: "What automated quoting does differently",
      },
      {
        type: "p",
        content: "Automated quoting tools take your project information, drawings, photos, or a written description, and generate a structured, priced scope of works automatically. The output includes line items by trade, quantities, unit rates, and a total with VAT. The whole process typically takes under 10 minutes.",
      },
      {
        type: "p",
        content: "The key difference is not just speed. It is consistency and completeness. An automated tool works from a fixed cost library and a structured scope-extraction process, so it does not forget the skip hire, the temporary propping, or the making-good that a manual quote might miss on a busy evening.",
      },
      {
        type: "h2",
        content: "Where manual quoting still makes sense",
      },
      {
        type: "p",
        content: "Manual quoting is not wrong, it is appropriate in certain situations:",
      },
      {
        type: "ul",
        content: [
          "Very small, simple jobs where the scope is obvious and a quick price is all that is needed",
          "Repeat clients who just need a number, not a formal document",
          "Specialist work where your rates and methods are highly bespoke and no software library would cover them accurately",
          "When you quote so infrequently that the time investment in switching tools is not worthwhile",
        ],
      },
      {
        type: "p",
        content: "If most of your work falls outside those categories, if you are regularly quoting extensions, loft conversions, structural alterations, or full renovations, manual quoting is almost certainly costing you more than it saves.",
      },
      {
        type: "h2",
        content: "When automated quoting pays off",
      },
      {
        type: "h3",
        content: "You are quoting more than two or three jobs a month",
      },
      {
        type: "p",
        content: "At two hours per quote, three quotes a week is six hours of unpaid evenings. Automated tools cut that to under 30 minutes for all three. Over a year that is hundreds of hours returned to you.",
      },
      {
        type: "h3",
        content: "You are losing jobs on price without knowing why",
      },
      {
        type: "p",
        content: "Inconsistent manual pricing means you are sometimes too high and sometimes too low. Too high and you do not win the job. Too low and you win it but lose money. An automated tool with a calibrated cost library produces consistent, defensible pricing every time.",
      },
      {
        type: "h3",
        content: "Your quotes are not winning at the rate they should",
      },
      {
        type: "p",
        content: "Presentation matters more than most builders expect. Clients comparing quotes from three builders will notice if one looks professionally formatted and the others look like they were put together quickly. A well-structured quote with clearly listed items, assumptions, exclusions, and payment terms signals that the builder is organised and trustworthy.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload drawings or describe the job, review the output, and send a professional PDF quote in under 10 minutes.",
        heading: "See what automated quoting looks like",
      },
      {
        type: "h2",
        content: "How to make the switch without disrupting your workflow",
      },
      {
        type: "ol",
        content: [
          "Run your first automated quote on a job type you know well, a rear extension, a loft conversion, a structural alteration. Compare the output to what you would have produced manually.",
          "Check the rates against your own cost experience and adjust anything that does not reflect your local market.",
          "Use the tool on a real live enquiry and send the output. Note how long it took versus your usual process.",
          "After a few quotes, review your win rate. Most builders find faster, more professional quotes win more work.",
        ],
      },
    ],
    faqs: [
      {
        q: "Is automated quoting accurate enough for real jobs?",
        a: "Yes, when the tool uses a structured cost library with current UK market rates. You should always review the output before sending, automated quoting produces a solid draft, not a final price. Your judgement and site knowledge still matter, particularly for unusual or complex scope items.",
      },
      {
        q: "Can automated quoting handle complex jobs like structural alterations or extensions?",
        a: "Modern automated quoting tools built for UK construction handle the full range of common residential project types: rear extensions, loft conversions, garage conversions, structural steelwork, full renovations and more. The Proper Tool specifically covers 31 trade categories including structural and groundworks items.",
      },
      {
        q: "How long does it take to set up automated quoting software?",
        a: "Most builders are producing their first quote within the hour. There is no lengthy setup or data migration required. You can start with the tool's default UK rates and adjust over time as you see how the output compares to your own pricing.",
      },
      {
        q: "Will switching to automated quoting mean my quotes look generic?",
        a: "Not if the tool lets you review and edit the output before sending, which any good quoting tool should. Automated quoting gives you a complete, structured starting point. You add your knowledge of the specific job, adjust any items that need it, and the final quote is still yours.",
      },
    ],
    relatedPosts: [
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
      { slug: "ai-quoting-software-uk-builders", title: "AI Quoting Software for UK Builders: What It Does and Whether It's Worth It" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedComparisons: [
      { slug: "buildertrend-alternative", title: "The Proper Tool vs Buildertrend" },
      { slug: "simpro-alternative", title: "The Proper Tool vs simPRO" },
      { slug: "tradify-alternative", title: "The Proper Tool vs Tradify" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/simpro-alternative", title: "simPRO Alternative for Small Builders" },
      { href: "/buildertrend-alternative", title: "Buildertrend Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Full comparison: all 6 quoting tools for UK builders" },
      { href: "/guides/quoting-for-uk-builders", title: "Quoting for UK Builders: The Complete Guide" },
    ],
  },

  {
    slug: "extension-quote-template-uk",
    title: "Extension Quote Template: What Every UK Builder Should Include",
    metaTitle: "Extension Quote Template UK: What to Include in a Building Quote",
    metaDescription: "Use this guide as your extension quote template. Covers every section a UK builder should include: scope, exclusions, provisional sums, VAT and payment terms.",
    publishedDate: "2025-03-24",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "8 min read",
    coverImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200&q=80",
    excerpt: "A house extension quote that wins the job and protects you when the work starts needs to cover far more than a total price. Here is exactly what to include, section by section.",
    sections: [
      {
        type: "p",
        content: "Most builders have a quote template of some kind, a Word document, a spreadsheet they copy, or a previous job they adapt. The problem is not usually the format. It is what gets left out. A quote that wins the job but leaves items undefined is the starting point for a dispute. A quote that covers everything properly protects both sides and sets the project up to run smoothly.",
      },
      {
        type: "p",
        content: "This guide walks through every section a UK builder should include in a house extension quote. Use it as a checklist the next time you are putting together a price.",
      },
      {
        type: "h2",
        content: "Why the scope of works matters more than the price",
      },
      {
        type: "p",
        content: "When a client gets three quotes for a rear extension and yours is the middle price, the first thing they will do is try to understand why the prices differ. A quote with a clear scope of works makes that comparison straightforward and usually favourable to you. You are not just cheaper or more expensive, you have demonstrably thought the job through.",
      },
      {
        type: "p",
        content: "A detailed scope also protects you during the job. If a client later claims that something should have been included, your quote is your evidence. If the scope says 'supply and install one pair of bi-fold doors to rear elevation, opening size 2400 mm x 2100 mm' and the client subsequently wants three pairs, you have a clear basis for a variation. If your quote just says 'doors: £3,500', that conversation is much harder.",
      },
      {
        type: "h2",
        content: "The eight sections every extension quote should contain",
      },
      {
        type: "h3",
        content: "1. Project summary and client details",
      },
      {
        type: "p",
        content: "Start with the basics: the client's name and address, the property address if different, your company details, and a one or two sentence description of the project. Something like 'single-storey rear extension of approximately 20 m², including groundworks, structural steelwork, brick and block walls, flat roof with warm deck build-up, and full internal fit-out to match existing'. This framing sets the context for everything that follows.",
      },
      {
        type: "h3",
        content: "2. Scope of works, line items by trade",
      },
      {
        type: "p",
        content: 'This is the heart of the quote and where most builders underinvest. A proper <a href="/glossary#scope-of-works">scope of works</a> lists every element of the job as a separate line item, with a description, a quantity, a unit, and a price. Grouping items by trade (demolition, groundworks, structural steelwork, brickwork, roofing, carpentry, electrical, plumbing, plastering, decoration, and so on) makes the document easy to follow and easy to check.',
      },
      {
        type: "p",
        content: "The description for each line item should be specific enough that a different builder reading the quote would understand exactly what is included. 'Brickwork to rear elevation: supply and lay facing brickwork in English bond to match existing, 52 m²' is correct. 'Brickwork: £4,200' is not.",
      },
      {
        type: "h3",
        content: "3. Assumptions",
      },
      {
        type: "p",
        content: "Every quote is based on assumptions. State them explicitly. Common assumptions for a rear extension include: foundations to be strip type unless ground investigation reveals otherwise; existing drainage to be capable of accepting additional connections without upsizing; party wall agreement not required or to be arranged by client; planning permission already granted or not required. If any of those assumptions turn out to be wrong, you have the basis to raise a variation.",
      },
      {
        type: "h3",
        content: "4. Exclusions",
      },
      {
        type: "p",
        content: "Exclusions are as important as inclusions. If you are not including fitted kitchen units, landscaping, furniture, curtains, or anything else the client might reasonably expect, say so. Common exclusions for an extension quote include: building control fees, structural engineer fees, party wall surveyor fees, external landscaping, fitted furniture, and any specialist services not listed in the scope.",
      },
      {
        type: "h3",
        content: "5. Provisional sums",
      },
      {
        type: "p",
        content: 'Some elements of a job cannot be priced accurately until work starts. The depth of foundations, for example, depends on the ground conditions uncovered during excavation. A <a href="/glossary#provisional-sum">provisional sum</a> is a placeholder amount that you include in the quote with a clear note that the actual cost will be adjusted up or down when the item is confirmed. Common provisional sums on extension jobs include: foundations (if ground conditions are unknown), drainage connections, and structural steelwork (if the engineer\'s specification is not yet finalised).',
      },
      {
        type: "h3",
        content: "6. Programme",
      },
      {
        type: "p",
        content: "State how long the job will take. Even a rough programme, 'works anticipated to take approximately 14 weeks from commencement, subject to structural engineer sign-off at steelwork stage', is better than nothing. Clients use your programme to plan their own lives. If you do not give one, they will form their own expectation, usually a shorter one than yours.",
      },
      {
        type: "h3",
        content: "7. Payment schedule",
      },
      {
        type: "p",
        content: "Specify when payments are due and how much each stage payment is. A typical extension might be structured as: 10 percent on contract signing, 25 percent on completion of groundworks, 25 percent on completion of structure to wall plate, 25 percent on completion of first fix, and the balance on practical completion. Never rely on a verbal payment agreement, put it in writing.",
      },
      {
        type: "h3",
        content: "8. VAT treatment and quote validity",
      },
      {
        type: "p",
        content: "If you are VAT-registered, state whether your prices are inclusive or exclusive of VAT and at what rate. Most residential extension work is standard-rated at 20 percent. State clearly how long the quote is valid, typically 30 to 60 days. Prices move, and you should not be held to a figure from six months ago if the client finally decides to proceed.",
      },
      {
        type: "h2",
        content: "The line items builders most commonly leave out",
      },
      {
        type: "p",
        content: "Even experienced builders regularly miss items on extension quotes. The most common omissions include:",
      },
      {
        type: "ul",
        content: [
          "Temporary weatherproofing and site protection during the build",
          "Scaffold erection, hire, and dismantling",
          "Skip hire and waste disposal, often underestimated on larger jobs",
          "Temporary propping to existing structure during demolition",
          "Making good to existing internal finishes at the junction with new works",
          "External drainage connections including soakaway or mains connection",
          "Building control inspection fees (if you are including them)",
          "Site setup, hoardings, and welfare facilities for longer jobs",
          "Snagging and practical completion walk-through",
        ],
      },
      {
        type: "h2",
        content: "Why a blank template is only part of the answer",
      },
      {
        type: "p",
        content: "A blank template gives you the structure but not the content. The most time-consuming part of any extension quote is not the format, it is working out what the scope actually contains, quantifying each item, and applying the right rates. A template that you fill in manually still takes two to four hours to complete for a typical extension job.",
      },
      {
        type: "p",
        content: "Purpose-built quoting software handles the content as well as the structure. You describe the job or upload the drawings, and the tool generates a complete, itemised scope of works with UK market rates applied to every line. You review and edit before sending. What used to take an evening takes under 10 minutes.",
      },
      {
        type: "cta",
        content: "Generate a complete, itemised extension quote from drawings or a project description. Free trial, no card required.",
        heading: "Build your next extension quote in under 10 minutes",
      },
    ],
    faqs: [
      {
        q: "What is a provisional sum in a builder's quote?",
        a: "A provisional sum is a placeholder amount included in a quote for work that cannot be accurately priced until more information is available, for example, the depth of foundations before excavation starts, or the weight and specification of structural steelwork before the engineer's drawings are complete. When the actual scope or cost is confirmed, the provisional sum is replaced with the actual cost, which may be higher or lower.",
      },
      {
        q: "Should I include VAT in my extension quote?",
        a: "If you are VAT-registered, you must account for VAT. Most UK builders show prices exclusive of VAT and add a clear line at the bottom showing the VAT amount and gross total. Some builders include VAT in their displayed prices, either approach works, but whichever you choose, state it clearly so there is no ambiguity for the client.",
      },
      {
        q: "How much detail should a builder's extension quote include?",
        a: "More than most builders include. A good extension quote has individual line items for every trade element, with descriptions specific enough that a different builder reading the quote would understand exactly what is included. Vague quotes with a single lump sum or broad trade totals are the source of most client disputes. Detailed quotes win more work and cause fewer problems.",
      },
      {
        q: "How long should I say a rear extension will take?",
        a: "A single-storey rear extension of 15 to 25 m² typically takes 10 to 16 weeks from start to practical completion, assuming no major delays with structural steelwork delivery or ground conditions. Always add a programme qualifier, 'subject to structural engineer sign-off' or 'subject to weather', to protect yourself from being held to a date you cannot guarantee.",
      },
      {
        q: "What happens if I forget to include something in my extension quote?",
        a: "If a significant item is genuinely missing from the scope and you have been awarded the contract, you will typically need to raise a variation to include it. How easily that conversation goes depends on how clearly written your original scope was. A quote with detailed exclusions and assumptions is much easier to vary than one without. For a small omission, most builders absorb the cost rather than raise a variation, another reason to price carefully in the first place.",
      },
    ],
    relatedPosts: [
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
      { slug: "loft-conversion-cost-estimate-uk", title: "How to Price a Loft Conversion: A UK Builder's Guide to Accurate Estimates" },
      { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/guides/quoting-for-uk-builders", title: "Quoting for UK Builders: The Complete Guide" },
    ],
  },

  {
    slug: "loft-conversion-cost-estimate-uk",
    title: "How to Price a Loft Conversion: A UK Builder's Guide to Accurate Estimates",
    metaTitle: "How to Price a Loft Conversion UK: Builder's Cost Estimate Guide (2026)",
    metaDescription: "Learn how to price a loft conversion accurately as a UK builder. Covers cost breakdowns by conversion type, the key variables that move the price, and how to avoid underquoting.",
    publishedDate: "2025-04-07",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "10 min read",
    excerpt: "Loft conversions are one of the highest-value residential jobs a builder can win, and one of the easiest to underquote. This guide covers how to price them accurately, by type.",
    sections: [
      {
        type: "p",
        content: "Loft conversions are attractive work. They are high value, they are relatively clean once the structural phase is done, and clients who want them are usually serious about going ahead. They are also a job where underquoting is painfully common. The structural elements are straightforward to miss or underprice, the programme is longer than many builders allow for, and the finishing scope is easy to underestimate from a brief site visit.",
      },
      {
        type: "p",
        content: "This guide covers how to price a loft conversion accurately, broken down by conversion type, with the key variables that drive the cost and the items most commonly missed on quotes.",
      },
      {
        type: "h2",
        content: "The four main types of loft conversion and how they differ in cost",
      },
      {
        type: "h3",
        content: "Velux (rooflight) loft conversion",
      },
      {
        type: "p",
        content: "The simplest and cheapest type. The roof structure is retained and strengthened, a floor is installed, and rooflights are cut in. No change to the external roofline, which means no planning permission in most cases. The structural scope is largely internal, floor joists, a new staircase, insulation, and first and second fix throughout. Typical cost range for a standard UK property is £25,000 to £40,000 depending on size, finish, and bathroom requirements.",
      },
      {
        type: "h3",
        content: "Dormer loft conversion",
      },
      {
        type: "p",
        content: 'A dormer adds a box-shaped extension to the roof slope, creating full-height headroom across part of the loft space. Most standard rear dormers fall within <a href="/glossary#permitted-development">permitted development</a>, though some require planning permission. The structural scope is more significant: the dormer frame, the flat or pitched roof over the dormer, external cladding or render, windows, and weatherproofing to the junction. Typical cost range is £35,000 to £60,000, with larger or more complex dormers pushing higher.',
      },
      {
        type: "h3",
        content: "Hip-to-gable loft conversion",
      },
      {
        type: "p",
        content: "A hip-to-gable conversion extends the ridge and replaces a hipped roof slope with a vertical gable, substantially increasing the usable loft volume. It is most common on semi-detached and detached properties. The structural scope includes removing the existing hip rafters, extending the ridge beam, building the new gable in blockwork or timber frame, and reroofing the affected slope. Usually combined with a rear dormer to maximise space. Typical cost range is £45,000 to £75,000.",
      },
      {
        type: "h3",
        content: "Mansard loft conversion",
      },
      {
        type: "p",
        content: "A mansard conversion involves a near-vertical rear wall and a low-pitch roof across the top of the space, fundamentally changing the roof structure. It produces the most usable space of any loft type but is the most expensive and complex to build. Usually requires planning permission and is most common in London and urban areas where loft development rights are restricted. Typical cost range is £55,000 to £90,000 or more for larger properties.",
      },
      {
        type: "h2",
        content: "The cost components of a loft conversion quote",
      },
      {
        type: "h3",
        content: "Structural steelwork",
      },
      {
        type: "p",
        content: 'Most loft conversions involve <a href="/glossary#structural-steel">structural steel</a>, at minimum an LVL or steel ridge beam, and often a series of loft beams to support the new floor. The structural engineer\'s drawings specify the sizes and spans; you cannot price the steelwork accurately without them. Allow a <a href="/glossary#provisional-sum">provisional sum</a> if the SE drawings are not yet available, and always get a firm price from a steelwork supplier before committing to a fixed figure. Steelwork costs on a typical dormer conversion range from £1,500 to £5,000 depending on beam sizes and access.',
      },
      {
        type: "h3",
        content: "Roof alterations",
      },
      {
        type: "p",
        content: "For dormers, hip-to-gables and mansards, the roof scope is substantial. Include: removal of existing roof covering over the affected area, structural alterations to rafters and ridge, construction of the dormer or gable frame, new roof covering (typically flat roof with warm deck build-up on a dormer, or tiles to match existing on a hip-to-gable slope), eaves details, guttering, and weatherproofing at all junctions. These items are often bundled too loosely in loft quotes, split them into individual line items so nothing is missed.",
      },
      {
        type: "h3",
        content: "Floor construction",
      },
      {
        type: "p",
        content: "The new loft floor typically involves installing new joists at the correct depth to achieve the required floor-to-ceiling height, with acoustic insulation between joists, and a chipboard or ply deck. Access for joist installation can be tight, and the time to complete this phase is often underestimated. On most semi-detached properties the floor area is 25 to 35 m², price the joists, insulation, and deck as separate items.",
      },
      {
        type: "h3",
        content: "Staircase",
      },
      {
        type: "p",
        content: "The staircase is one of the trickiest elements to price on a loft conversion because the configuration and headroom available varies enormously between properties. A standard straight flight to a rooflight conversion can be as little as £1,200 to £1,800 installed. An alternating tread or space-saver stair in a restricted space can reach £3,000. For complex bespoke configurations, you may need a quotation from a stair manufacturer before you can price accurately.",
      },
      {
        type: "h3",
        content: "Insulation",
      },
      {
        type: "p",
        content: "Loft conversions require insulation to meet Part L of the Building Regulations. For a warm roof dormer, PIR insulation board between and under the rafters is typical. For the new loft floor, acoustic insulation between joists is required for separating floors. Insulation materials are a significant material cost that is often underpriced. Get current prices from your supplier, not from memory, and include the labour to cut and fit everything accurately.",
      },
      {
        type: "h3",
        content: "First and second fix",
      },
      {
        type: "p",
        content: "Do not forget to fully scope the first and second fix on a loft conversion. This includes: running all electrical cables for lighting, sockets, and potentially underfloor heating; installing soil and waste pipes for any bathroom; hanging internal doors; and fitting all second fix items such as switches, sockets, sanitary ware, and finishes. On a loft with an en-suite, the plumbing and electrical scope alone can run to £4,000 to £8,000.",
      },
      {
        type: "h3",
        content: "Plastering, decoration, and finishes",
      },
      {
        type: "p",
        content: "The plastering scope on a loft conversion covers all new walls and ceilings, including the sloping sections and dormer cheeks. Sloping ceilings are more time-consuming and more expensive than flat ceilings, allow for that in your labour rates. Decoration is easily forgotten or underpriced at the quote stage because it is the last thing on the list. Include it.",
      },
      {
        type: "h2",
        content: "Items most commonly missed on loft conversion quotes",
      },
      {
        type: "ul",
        content: [
          "Structural engineer fees, usually £500 to £1,500 and often overlooked",
          "Building control application and inspection fees",
          "Scaffold, both the initial scaffold and any repositioning required mid-job",
          "Temporary weatherproofing of the roof opening during construction",
          "Fire doors, required to every habitable room on the escape route",
          "Mains-interlinked smoke alarms to all floors, as required by Building Control",
          "Making good to existing ceilings below at the floor junction",
          "External rainwater goods where the dormer alters the existing gutter run",
          "Party wall agreement costs if the property is semi-detached or terraced",
        ],
      },
      {
        type: "h2",
        content: "How to get to an accurate loft conversion price quickly",
      },
      {
        type: "p",
        content: "The fastest way to produce an accurate loft conversion estimate is to work from the structural engineer's drawings. If the SE drawings are not available, include a provisional sum for the steelwork and foundations and note that the final price is subject to the SE specification. A quote produced without the SE drawings is always a rough estimate, and presenting it as such protects you.",
      },
      {
        type: "p",
        content: "Modern quoting software can read architectural drawings and SE reports and generate a structured, itemised scope of works for a loft conversion in under 10 minutes. The output covers all trades in the correct sequence, with UK market rates applied, so you are starting from a complete draft rather than a blank page. You still review and adjust every line before sending, your site knowledge and supplier prices should inform the final figures.",
      },
      {
        type: "cta",
        content: "Upload your loft conversion drawings and get a fully itemised quote draft in under 10 minutes. Free trial, no card required.",
        heading: "Quote loft conversions faster and more accurately",
      },
    ],
    faqs: [
      {
        q: "How much does a loft conversion cost in the UK in 2026?",
        a: "A Velux rooflight loft conversion typically costs £25,000 to £40,000. A standard dormer conversion ranges from £35,000 to £60,000. A hip-to-gable conversion typically costs £45,000 to £75,000, and a mansard conversion can range from £55,000 to £90,000 or more. These are contractor prices for the build work only and exclude structural engineer fees, building control, and party wall costs.",
      },
      {
        q: "Do I need planning permission for a loft conversion?",
        a: "Many loft conversions fall within permitted development and do not require planning permission. The main exceptions are mansard conversions, conversions that alter the front roof slope, properties in conservation areas or listed buildings, and conversions that exceed the permitted development volume limits. Always advise clients to confirm their PD rights with the local authority before you start work.",
      },
      {
        q: "How long does a loft conversion take to build?",
        a: "A Velux conversion typically takes 6 to 8 weeks. A standard dormer conversion takes 8 to 12 weeks. A hip-to-gable or mansard conversion can take 12 to 16 weeks or more. These programmes assume continuous access and no significant delays with structural steelwork delivery.",
      },
      {
        q: "What structural work is involved in a loft conversion?",
        a: "At minimum, a loft conversion involves new floor joists to support the new habitable floor and a staircase opening cut into the existing ceiling below. Most conversions also require a new ridge beam or LVL, and dormer or hip-to-gable conversions require roof structural alterations specified by a structural engineer. The exact scope depends on the existing roof construction and the conversion type.",
      },
      {
        q: "Can I include my profit margin in a fixed-price loft conversion quote?",
        a: "Yes, and you should. Price loft conversions as fixed-price contracts with clearly defined scope, assumptions, exclusions, and provisional sums for any genuinely unknown elements. Your overhead and profit margin should be included in the contract price, not treated as optional extras. A typical net margin on a loft conversion for a UK builder is 15 to 20 percent of the contract value.",
      },
    ],
    relatedPosts: [
      { slug: "extension-quote-template-uk", title: "Extension Quote Template: What Every UK Builder Should Include" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
      { slug: "ai-quoting-software-uk-builders", title: "AI Quoting Software for UK Builders: What It Does and Whether It's Worth It" },
    ],
  },

  {
    slug: "ai-quoting-software-uk-builders",
    title: "AI Quoting Software for UK Builders: What It Does and Whether It's Worth It",
    metaTitle: "AI Quoting Software for UK Builders: Is It Worth It? (2026 Guide)",
    metaDescription: "AI quoting software promises to cut estimating time for UK builders. This guide explains how it works, what it can and can't do, and which tools are worth trying.",
    publishedDate: "2025-04-28",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "7 min read",
    excerpt: "AI quoting tools are now being marketed to UK builders as a way to produce professional estimates in minutes. Here is an honest look at what they can actually do, and where human judgement still matters.",
    sections: [
      {
        type: "p",
        content: "The marketing around AI quoting software tends to fall into two camps: breathless promises that AI will replace the estimator entirely, and equally breathless warnings that AI-generated quotes are dangerously inaccurate. Neither is quite right. Understanding what AI quoting tools actually do, and where they fall down, helps you decide whether the technology is worth adopting for your business.",
      },
      {
        type: "p",
        content: "This guide is aimed at UK builders who are evaluating quoting software and want to understand whether the AI angle is genuine or just marketing.",
      },
      {
        type: "h2",
        content: "What AI quoting software actually does",
      },
      {
        type: "p",
        content: "Modern AI quoting tools for builders use large language models to read unstructured input, drawings, PDFs, site photos, or a plain-text job description, and extract a structured scope of works from it. The AI identifies what work is involved (excavation, brickwork, steelwork, roofing, and so on), estimates quantities where they can be inferred from the input, and maps each item to a pricing library to produce a cost estimate.",
      },
      {
        type: "p",
        content: "The result is a structured first draft of a quote, not a finished price, but a comprehensive starting point that covers the full scope of the job. For a typical rear extension, that process takes under 10 minutes. Manually, the same process takes two to four hours.",
      },
      {
        type: "h2",
        content: "What AI quoting software is genuinely good at",
      },
      {
        type: "h3",
        content: "Reading drawings and extracting scope",
      },
      {
        type: "p",
        content: "This is where AI genuinely earns its keep. A good AI quoting tool can read an architectural drawing, identify the project type, extract dimensions and specifications, and produce a structured scope of works from that input without you having to type anything up. It catches items you might have missed when reading the drawing quickly, the external drainage connection, the scaffold specification, the making-good items at the junction between old and new.",
      },
      {
        type: "h3",
        content: "Consistency and completeness",
      },
      {
        type: "p",
        content: "AI tools do not have off days. They do not forget to include the skip hire because they were tired when they put the quote together, and they do not accidentally use last year's material prices because that was what they remembered. A well-built AI quoting tool with a maintained pricing library is more consistent than a builder quoting manually, which is worth something on its own.",
      },
      {
        type: "h3",
        content: "Speed on high-volume quoting",
      },
      {
        type: "p",
        content: "If you quote regularly, three, four, five jobs a week, the time saving from AI quoting compounds quickly. Builders who use AI quoting tools report getting evenings back and responding to enquiries on the same day instead of the next. Being faster than your competitors at getting a professional quote in front of a client directly improves your win rate.",
      },
      {
        type: "h2",
        content: "Where AI quoting tools still need human oversight",
      },
      {
        type: "h3",
        content: "Local market pricing",
      },
      {
        type: "p",
        content: "Labour rates in London are not the same as in the Midlands or the North. Material prices vary by supplier and by region. An AI quoting tool trained on UK market averages will give you a solid baseline, but you should always review and adjust rates to reflect your local market and your actual cost base. The better tools let you set your own rates and override defaults on any line item.",
      },
      {
        type: "h3",
        content: "Site-specific conditions",
      },
      {
        type: "p",
        content: "No AI tool knows that the property has awkward access, that the neighbour is difficult, that the soil on this street is notoriously clay-heavy, or that the existing roof structure is likely to contain asbestos. Those site-specific factors affect your price, and they can only come from a human who has visited the site. AI quoting gives you the structural framework; your site knowledge fills in the gaps.",
      },
      {
        type: "h3",
        content: "Unusual or specialist scope",
      },
      {
        type: "p",
        content: "AI quoting tools are trained on common residential construction project types. They handle extensions, loft conversions, structural alterations, and renovations well. Unusual or highly specialist scope items, heritage work, complex structural solutions, specialist systems, may not be priced accurately by a general construction AI. In those cases, treat the AI output as a checklist rather than a price guide.",
      },
      {
        type: "h2",
        content: "How to evaluate AI quoting software as a UK builder",
      },
      {
        type: "p",
        content: "When you are comparing quoting tools that claim to use AI, ask these questions:",
      },
      {
        type: "ul",
        content: [
          "Does it use UK-specific pricing, or is it a US or Australian tool applied to a UK market?",
          "Can it read PDF drawings and structural engineer reports, or does it only accept text input?",
          "Can you review and edit every line item before sending, or is the output locked?",
          "Does it cover the full range of trades on a typical residential project, including groundworks, structural steelwork, and external envelope?",
          "Does it flag scope items that look incomplete or quantities that appear inconsistent?",
          "What happens when the AI gets something wrong, are there override controls?",
        ],
      },
      {
        type: "p",
        content: "The best AI quoting tools for UK builders are built specifically for the UK construction market, use current trade pricing, and treat their AI output as a draft that you review and refine, not an answer you send directly. Any tool that does not let you edit the output before sending is a liability, not a feature.",
      },
      {
        type: "h2",
        content: "How The Proper Tool approaches AI-assisted quoting",
      },
      {
        type: "p",
        content: "The Proper Tool is built for UK residential construction. You upload drawings, photos, or describe the project in plain text. The AI reads the input, identifies the project scope, extracts quantities from the drawings where available, and generates a fully itemised scope of works with UK market rates applied across 31 trade categories.",
      },
      {
        type: "p",
        content: "The output is then reviewed by a built-in validation system that checks for missing items, flagged quantities, and scope completeness. You review every line, make any adjustments, and export a professional PDF quote. The AI does the heavy lifting on the first draft; you provide the final judgement before anything goes to the client.",
      },
      {
        type: "ul",
        content: [
          "Reads architectural drawings, structural engineer reports, site photos and text descriptions",
          "UK pricing across 31 construction trade categories",
          "Built-in scope validation to flag potential missing items",
          "Full line-item editing before sending",
          "Professional branded PDF output",
          "Free 14-day trial, no card required",
        ],
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload your next set of drawings and see what AI-assisted quoting produces in under 10 minutes.",
        heading: "See AI quoting in action on a real job",
      },
    ],
    faqs: [
      {
        q: "Is AI quoting software accurate enough for real building jobs in the UK?",
        a: "When the tool uses UK-specific pricing data and is built for construction, yes, the output is accurate enough to be a strong starting point. You should always review and adjust the draft before sending, particularly for site-specific cost factors that the AI cannot know about, such as access difficulties, unusual ground conditions, or local supplier pricing.",
      },
      {
        q: "Can AI quoting software read UK architectural drawings?",
        a: "The best AI quoting tools for UK builders can read PDF drawings, including architectural plans, elevations, and structural engineer reports. They extract dimensions, materials specifications, and scope items from the drawings to generate a structured estimate. The quality of the output depends on the quality and detail of the drawings provided.",
      },
      {
        q: "Will AI quoting software replace building estimators?",
        a: "No, and it is not designed to. AI quoting software handles the time-consuming, systematic part of estimating, reading drawings, listing scope items, applying standard rates, and generating a structured document. The judgement-based parts, assessing site risk, understanding unusual scope, negotiating variations, and applying local knowledge, still require an experienced builder or estimator.",
      },
      {
        q: "What is the best AI quoting software for UK builders?",
        a: "The best AI quoting tools for UK builders are built specifically for the UK residential construction market, not adapted from US or Australian products. Key differentiators are: genuinely reading drawings (not just text descriptions), UK trade pricing, coverage of all construction trades including groundworks and steelwork, and the ability to edit every line before sending. The Proper Tool is built specifically for this market.",
      },
      {
        q: "How much does AI quoting software cost for a UK builder?",
        a: "AI quoting tools for UK builders typically range from £20 to £80 per month depending on features and usage limits. The Proper Tool starts from £29 per month with a 14-day free trial and no card required. Given that the average builder spends two to four hours per quote manually, a tool that reduces that to under 30 minutes pays for itself on the first or second quote of the month.",
      },
    ],
    relatedPosts: [
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
      { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
      { slug: "loft-conversion-cost-estimate-uk", title: "How to Price a Loft Conversion: A UK Builder's Guide to Accurate Estimates" },
    ],
    relatedComparisons: [
      { slug: "buildxact-alternative", title: "The Proper Tool vs Buildxact" },
      { slug: "estimatorxpress-alternative", title: "The Proper Tool vs EstimatorXpress" },
      { slug: "price-the-job-alternative", title: "The Proper Tool vs Price the Job" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/buildxact-alternative", title: "Buildxact Alternative for UK Builders" },
      { href: "/estimatorxpress-alternative", title: "EstimatorXpress Alternative for UK Builders" },
      { href: "/best/quoting-software-for-uk-builders", title: "Full comparison: all 6 quoting tools for UK builders" },
      { href: "/guides/quoting-for-uk-builders", title: "Quoting for UK Builders: The Complete Guide" },
    ],
  },

  {
    slug: "quoting-software-for-plumbers-uk",
    title: "Quoting Software for Plumbers UK: Stop Losing Jobs to Slow Quotes",
    metaTitle: "Quoting Software for Plumbers UK (2026 Guide)",
    metaDescription: "Find the best quoting software for UK plumbers. Generate professional plumbing quotes in minutes, with accurate UK labour rates and a clean PDF ready to send.",
    publishedDate: "2025-05-19",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "7 min read",
    excerpt: "Most plumbers lose work not because their price is wrong, but because a competitor sent a professional quote first. The right quoting software changes that. Here is what to look for and how to get your first quote out the door in under 10 minutes.",
    sections: [
      {
        type: "p",
        content: "Plumbing is a trade where speed matters almost as much as price. A homeowner with a failing boiler or a bathroom refit to plan is usually talking to two or three plumbers at the same time. The one who sends a professional, itemised quote first is often the one who wins the job, regardless of who had the sharpest price.",
      },
      {
        type: "p",
        content: "Yet most UK plumbers are still quoting the same way they always have: a typed email with a lump sum, or a handwritten figure on a piece of paper. This guide covers what good quoting software actually looks like for a plumber, why it matters more than you might think, and how to use it without spending an evening on admin.",
      },
      {
        type: "h2",
        content: "Why quoting is one of the biggest problems in the plumbing trade",
      },
      {
        type: "p",
        content: "The average UK plumber quotes three to five jobs for every one they win. If each quote takes an hour to put together, that is three to five unpaid hours per job won. Over a year, a busy plumber can lose two full weeks of billable time just on producing quotes that do not convert.",
      },
      {
        type: "p",
        content: "The second problem is inconsistency. When you are quoting from memory, or copying and pasting from an old email, it is easy to miss items, use outdated rates, or forget to include something that ends up eating into your margin on the job. A structured quoting process solves both problems at once.",
      },
      {
        type: "h2",
        content: "What plumbing quotes need to include",
      },
      {
        type: "p",
        content: "A professional plumbing quote is more than a price. Clients who are spending thousands of pounds on a bathroom renovation or a heating system want to see that you understand the job. A good quote gives them confidence that you have thought it through.",
      },
      {
        type: "ul",
        content: [
          "A clear description of the works to be carried out",
          "Line-item breakdown covering labour, materials, and any specialist equipment",
          "Fixtures and fittings to be supplied (brand and model where agreed)",
          "What is not included (scope exclusions protect you from disputes later)",
          "Programme, how long the job will take and expected start date",
          "Payment terms and what happens if extra work is required",
          "How long the quote is valid for",
          "Your company details, insurance status, and Gas Safe or OFTEC registration if applicable",
        ],
      },
      {
        type: "p",
        content: "Most generic invoicing tools produce something closer to a receipt than a quote. For plumbing work, particularly larger jobs involving bathrooms, kitchens, or heating systems, a detailed, structured document makes a real difference to win rate.",
      },
      {
        type: "h2",
        content: "The problem with generic quoting tools for plumbers",
      },
      {
        type: "p",
        content: "Most quoting software on the market is designed for general service businesses. The item lists, rate cards, and output formats are built around simple trades like cleaning, landscaping, or HVAC maintenance. They work well enough for very small jobs but fall short when you need to quote a full bathroom renovation, a heating system replacement, or a new build fit-out.",
      },
      {
        type: "p",
        content: "The main problems with generic tools for plumbers are: no understanding of trade-specific line items such as first fix pipework, sanitary ware installation, pressure testing, and commissioning; pricing databases that use US or Australian rates rather than UK labour costs; and PDF output that looks more like an invoice template than a professional construction quote.",
      },
      {
        type: "h2",
        content: "What to look for in plumbing quoting software",
      },
      {
        type: "h3",
        content: "UK labour rates built in",
      },
      {
        type: "p",
        content: "Plumbing labour rates vary across the UK, but the national range for a qualified plumber runs from roughly £35 to £60 per hour depending on location and specialisation. Any quoting tool you use should reflect UK rates, not US or Australian figures. The last thing you need is software that prices your work at £20 per hour because it was built for a different market.",
      },
      {
        type: "h3",
        content: "Trade-specific line items",
      },
      {
        type: "p",
        content: "Good plumbing quoting software should understand the structure of plumbing work: rough-in versus finish work, pressure testing, commissioning, drainage runs, isolation valves, and so on. A tool that requires you to build every item from scratch is faster than a spreadsheet, but not by much.",
      },
      {
        type: "h3",
        content: "Professional PDF output",
      },
      {
        type: "p",
        content: "The quote your client receives is a direct reflection of your business. A clean, professionally formatted PDF with a proper scope of works, your company details, and clear pricing looks completely different from a typed email. Clients hiring a plumber for several thousand pounds worth of work notice the difference.",
      },
      {
        type: "h3",
        content: "Fast to use on any device",
      },
      {
        type: "p",
        content: "The best quoting tools for plumbers work on a phone or tablet as well as a laptop. You should be able to start a quote during a site visit and have a draft ready to review before you leave the client's driveway.",
      },
      {
        type: "h2",
        content: "How The Proper Tool works for plumbing quotes",
      },
      {
        type: "p",
        content: "The Proper Tool is built for UK construction and residential trades, including plumbing work. You can describe the job in plain text, upload drawings or photos, and the tool generates a structured scope of works with line items, quantities, and pricing based on current UK trade rates.",
      },
      {
        type: "p",
        content: "For a bathroom renovation, for example, you would describe the room, the fixtures being installed, and any associated work like tiling or flooring. The tool produces a detailed breakdown covering first and second fix plumbing, sanitary ware installation, waste connections, and any associated builder's work. You review every line, edit anything that does not fit your job, and export a professional PDF in minutes.",
      },
      {
        type: "p",
        content: "The same process works for heating system quotes, kitchen plumbing, general domestic plumbing, and new build fit-outs. Because the tool covers all residential construction trades, not just plumbing, it can also capture the builder's work, tiling, and decoration elements that often accompany a bathroom refit, giving you a more complete and competitive quote than the plumbing line items alone.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days. No card required. Generate your first plumbing quote in under 10 minutes.",
        heading: "Quote faster, win more jobs",
      },
    ],
    faqs: [
      {
        q: "What is the best quoting software for UK plumbers?",
        a: "The best quoting software for UK plumbers uses UK-specific labour rates, generates a structured scope of works rather than a simple price list, and produces a professional PDF that reflects well on your business. Generic tools built for other markets often use the wrong pricing data. Look for software designed specifically for UK residential trades.",
      },
      {
        q: "How should a UK plumber price a bathroom renovation quote?",
        a: "Break the job into distinct work stages: strip-out and waste disposal, first fix pipework, sanitary ware supply and installation, second fix, pressure testing and commissioning, and any builder's work or tiling. Price labour at your true day rate including business overhead, not just take-home. Get firm supply quotes for sanitary ware before including fixture costs in your quote.",
      },
      {
        q: "Should plumbers charge for site visits and quoting time?",
        a: "For smaller jobs, most plumbers absorb quoting time. For larger jobs, bathroom renovations, full system replacements, or new builds, it is increasingly common to charge a survey fee that is deducted from the final job cost if the work proceeds. If you are spending more than an hour on a quote, it is worth considering whether a survey fee makes sense.",
      },
      {
        q: "How long should a plumbing quote be valid for?",
        a: "Most plumbers quote for 30 days. Material prices, particularly for copper, fittings, and boilers, can shift over time, so holding prices for longer than 60 days is a risk. State your validity period clearly on the quote to avoid having clients return months later expecting the same price.",
      },
      {
        q: "Do I need to include VAT on plumbing quotes?",
        a: "If you are VAT-registered, yes. Most domestic plumbing work is standard-rated at 20 percent. Some energy efficiency work qualifies for the 0 percent rate following recent changes, so it is worth checking with your accountant if you are doing heat pump or insulation work alongside plumbing.",
      },
    ],
    relatedPosts: [
      { slug: "estimating-software-for-electricians-uk", title: "Estimating Software for Electricians UK: Quote Electrical Jobs Faster" },
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },

  {
    slug: "estimating-software-for-electricians-uk",
    title: "Estimating Software for Electricians UK: Quote Electrical Jobs Faster",
    metaTitle: "Estimating Software for Electricians UK (2026 Guide)",
    metaDescription: "The best estimating software for UK electricians. Generate professional electrical quotes in minutes using UK labour rates, no spreadsheets needed.",
    publishedDate: "2025-06-09",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "7 min read",
    excerpt: "Electrical estimating is time-consuming and easy to get wrong. Here is how dedicated quoting software helps UK electricians produce accurate, professional quotes faster, and win more of the work they quote for.",
    sections: [
      {
        type: "p",
        content: "Estimating electrical work is one of the more technically involved parts of running an electrical contracting business. The scope of a job can vary enormously, a simple consumer unit upgrade is an afternoon; a full rewire on a four-bedroom house is a week or more. Getting the price right matters, and getting it out quickly matters almost as much.",
      },
      {
        type: "p",
        content: "Most UK electricians are still estimating from memory or copying figures from old quotes. It works, until it does not, a missed cable run, underestimated testing time, or a forgotten containment system turns a profitable job into a breakeven. This guide covers what good electrical estimating software looks like and how it saves you time and money.",
      },
      {
        type: "h2",
        content: "The cost of getting electrical estimating wrong",
      },
      {
        type: "p",
        content: "Electrical work has more variable cost components than most trades. Labour hours depend on cable routing, access difficulty, and the condition of the existing installation. Materials, cable, accessories, containment, distribution equipment, vary in price by specification and supplier. A quote that is off on either element, even by 10 percent, can mean working a full week for minimal return.",
      },
      {
        type: "p",
        content: "The hidden cost of manual estimating is also significant. A Part P domestic electrician who spends two hours producing each estimate and quotes four jobs a month before winning two is spending eight hours on estimates alone. That is one full working day of unbillable admin time every month, time that better tools can dramatically reduce.",
      },
      {
        type: "h2",
        content: "What a professional electrical quote should include",
      },
      {
        type: "ul",
        content: [
          "Scope of electrical works in plain English the client can understand",
          "Labour breakdown by activity (installation, first fix, second fix, testing and commissioning)",
          "Materials list covering cable, accessories, distribution equipment, and containment",
          "Certification details (EIC, EICR, Part P notification as applicable)",
          "Any builder's work or making-good included or excluded",
          "Programme, start date and estimated duration",
          "Payment milestones for larger jobs",
          "Quote validity period",
          "Your NICEIC, NAPIT, or other scheme registration details",
        ],
      },
      {
        type: "h2",
        content: "Why generic quoting tools fall short for electricians",
      },
      {
        type: "p",
        content: "Most business quoting software is built for simple service businesses. The item templates, rate structures, and document formats are designed for trades with uncomplicated pricing, a fixed call-out charge plus hourly rate. Electrical work rarely fits that pattern, particularly for larger installation jobs.",
      },
      {
        type: "p",
        content: "Generic tools also tend to use pricing databases calibrated for a global or US market. UK electrical labour rates differ substantially from the US market. An electrician in London or the South East is not working at US-market rates, and a quoting tool that uses those figures will either price you out of jobs or leave you undercharging.",
      },
      {
        type: "h2",
        content: "What to look for in electrical estimating software",
      },
      {
        type: "h3",
        content: "UK electrical trade rates",
      },
      {
        type: "p",
        content: "UK electrician labour rates typically run from £40 to £65 per hour for a qualified installer depending on location and the complexity of the work. Estimating software should reflect these rates, not figures derived from other markets. The tool should also let you override rates to reflect your own cost base and local market.",
      },
      {
        type: "h3",
        content: "Support for installation-type jobs, not just call-outs",
      },
      {
        type: "p",
        content: "A tool designed around call-out + hourly rate works for fault finding and small repairs. For larger installation jobs, rewires, new build fit-outs, commercial first and second fix, you need to be able to build a proper scope of works with line-item quantities and descriptions. Look for software that supports this level of detail.",
      },
      {
        type: "h3",
        content: "Professional document output",
      },
      {
        type: "p",
        content: "Builders, developers, and property owners who commission electrical installation work are often managing a wider project. A professional, structured quote that lists the scope clearly and shows a proper breakdown of costs positions you as a serious contractor rather than a one-man band, which matters when larger contracts are in play.",
      },
      {
        type: "h3",
        content: "Speed without sacrificing detail",
      },
      {
        type: "p",
        content: "The best electrical estimating tools let you describe a job in plain terms and generate a structured starting point automatically. You then review, adjust, and add trade-specific detail before sending. The goal is to cut the estimating time significantly without cutting corners on the output.",
      },
      {
        type: "h2",
        content: "How The Proper Tool supports electrical quotes",
      },
      {
        type: "p",
        content: "The Proper Tool covers electrical installation as one of its 31 UK trade categories. When you describe an electrical job or upload drawings that include electrical specification notes, the tool generates a structured scope of works including first fix wiring, second fix accessories, distribution board installation, testing, and certification items.",
      },
      {
        type: "p",
        content: "Because the tool is built for residential construction across all trades, it is particularly useful for electricians who work as part of a wider building project, extensions, loft conversions, renovations, where the electrical work sits alongside structural, plumbing, and finishing trades. You can quote the electrical scope as a standalone package or as part of a full project estimate.",
      },
      {
        type: "p",
        content: "The output is a professional PDF that lists your scope of works, quantities, and pricing in a format that works for both direct clients and main contractors. The whole process typically takes under 10 minutes for a standard domestic job.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days. No card required. Get your first electrical quote out the door in under 10 minutes.",
        heading: "Estimate faster, quote more professionally",
      },
    ],
    faqs: [
      {
        q: "What is the best estimating software for UK electricians?",
        a: "The best estimating software for UK electricians uses current UK electrical trade rates, supports structured scopes of works for installation jobs (not just call-out sheets), and produces a professional PDF document. Avoid tools built for other markets that use US or Australian pricing data, the rates will not reflect your actual cost base.",
      },
      {
        q: "How do UK electricians price installation jobs?",
        a: "Most electricians estimate labour hours per activity, first fix, second fix, distribution board installation, testing and commissioning, and multiply by their true hourly rate. Materials are priced from current supplier quotes and a markup of 15 to 25 percent is applied to cover purchasing time, delivery, and waste. Overhead and profit are added on top of the total direct cost.",
      },
      {
        q: "Should electricians charge for estimates?",
        a: "For small domestic jobs, most electricians provide free estimates. For larger installation work, rewires, new build fit-outs, commercial projects, charging a fixed survey and estimating fee is increasingly standard practice, particularly when detailed drawings or specifications are required. A fee is often deducted from the job cost if the work proceeds.",
      },
      {
        q: "How long does it take to estimate a full rewire?",
        a: "A thorough estimate for a full domestic rewire typically takes two to three hours when done manually, reviewing the property, counting points, pricing materials, and producing the document. Good estimating software can reduce this significantly by generating a structured starting point from a description of the property, which you then adjust to reflect the specifics.",
      },
      {
        q: "What certifications should an electrician include on a quote?",
        a: "Include your NICEIC, NAPIT, or equivalent approved contractor scheme registration number. State that an Electrical Installation Certificate will be issued on completion and that Part P Building Regulations notification will be made where required. For EICRs, note the applicable standard and the period the certificate covers.",
      },
    ],
    relatedPosts: [
      { slug: "quoting-software-for-plumbers-uk", title: "Quoting Software for Plumbers UK: Stop Losing Jobs to Slow Quotes" },
      { slug: "roofing-quote-software-uk", title: "Roofing Quote Software UK: Produce Accurate Roof Quotes Without the Hassle" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },

  {
    slug: "roofing-quote-software-uk",
    title: "Roofing Quote Software UK: Produce Accurate Roof Quotes Without the Hassle",
    metaTitle: "Roofing Quote Software UK: Generate Roof Quotes Fast (2026)",
    metaDescription: "The best roofing quote software for UK roofers. Produce accurate, professional roofing estimates covering tiles, flat roofs, guttering, and more in minutes.",
    publishedDate: "2025-07-07",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "8 min read",
    excerpt: "Roofing quotes need to cover a lot of variables, roof pitch, material type, access, disposal, and getting them wrong is costly. Here is how the right software helps UK roofers produce accurate, professional quotes faster.",
    sections: [
      {
        type: "p",
        content: "Roofing quotes are more complex than they look from the outside. The visible part, tiles, felt, guttering, is straightforward enough, but the full cost of a roofing job includes scaffolding, waste disposal, ridge and hip work, lead flashing, ventilation, and any associated carpenter's work. A quote that misses any of these can quickly turn a profitable job into a difficult one.",
      },
      {
        type: "p",
        content: "Most UK roofers price from experience, which works well when jobs are familiar. It becomes harder when the scope includes unusual roof geometry, flat roof systems, or work that runs into associated trades. Good roofing quote software handles the systematic parts, scope building, material quantities, pricing, so you can focus on the judgement that only comes from being on the roof.",
      },
      {
        type: "h2",
        content: "Why roofing quotes go wrong",
      },
      {
        type: "p",
        content: "The most common reasons a roofing quote loses money are: underestimating scaffolding duration or cost; forgetting to include waste disposal for stripped materials; missing associated work like fascias, soffits, and guttering that the client expects but did not ask for explicitly; and using material prices from memory rather than current supplier quotes.",
      },
      {
        type: "p",
        content: "Scaffolding in particular can be a significant cost on roofing jobs and is often undercosted. A scaffold that needs to stay up for longer than planned, or that requires an additional lift, can wipe out the margin on the whole job. A good quoting process builds in realistic scaffold hire periods and flags the assumptions clearly in the document.",
      },
      {
        type: "h2",
        content: "What a professional roofing quote should cover",
      },
      {
        type: "ul",
        content: [
          "Roof area in square metres and the scope of works (full replacement, partial repair, overlay)",
          "Roofing material specification, tile type, flat roof system, or other material",
          "Battening, underlay, and counter-batten where applicable",
          "Ridge, hip, valley, and verge detailing",
          "Lead flashings, soakers, and abutment details",
          "Fascias, soffits, and bargeboards if included",
          "Guttering and downpipe replacement or retention",
          "Scaffold supply, erection, and strike (with assumed hire period)",
          "Waste disposal for stripped materials",
          "Any associated structural or carpenter's work",
          "Ventilation and air flow requirements",
          "Programme, duration and expected start date",
          "Payment schedule for larger jobs",
        ],
      },
      {
        type: "h2",
        content: "The difference between a roofing estimate and a roofing quote",
      },
      {
        type: "p",
        content: "Many roofers use the words 'estimate' and 'quote' interchangeably, but they have different legal implications. An estimate is an approximation that can be revised. A quote is a fixed price for a defined scope of work. For most residential roofing jobs, clients want a quote, a fixed price they can rely on. The more clearly you define the scope in writing, the more protected you are if the job turns out to be more complex than it appeared.",
      },
      {
        type: "p",
        content: "Good roofing quote software helps you define the scope precisely and document your assumptions clearly. This protects you commercially and gives the client confidence that you have properly assessed the job.",
      },
      {
        type: "h2",
        content: "What to look for in roofing quoting software",
      },
      {
        type: "h3",
        content: "Coverage of roofing-specific line items",
      },
      {
        type: "p",
        content: "Generic quoting tools treat roofing as a single line item or a simple labour-plus-materials calculation. A roofing-specific tool, or a construction quoting tool that understands roofing work, should cover all the trade categories involved: roof coverings, leadwork, scaffolding, fascias and soffits, guttering, and any structural elements.",
      },
      {
        type: "h3",
        content: "UK roofing trade rates",
      },
      {
        type: "p",
        content: "Roofing labour rates in the UK typically run from £150 to £250 per square metre for a new pitched roof including materials, varying by tile type, roof complexity, and location. Flat roof systems vary more widely by material. Any quoting tool you use should reflect UK market rates, not figures from other markets.",
      },
      {
        type: "h3",
        content: "Photo and drawing input",
      },
      {
        type: "p",
        content: "The best roofing quoting tools can take a photo of the roof, a description of the job, or an architectural drawing and generate a structured scope from it. This is particularly useful for extension roofs and loft conversions where drawings are available and the roofing scope is defined by the architect's specification.",
      },
      {
        type: "h3",
        content: "Scaffold cost integration",
      },
      {
        type: "p",
        content: "Because scaffolding is a major cost on most roofing jobs, look for software that includes scaffolding as a standard scope item rather than leaving it to be added manually after the fact. The option to specify hire duration and scaffold type means you can build a realistic cost model rather than guessing.",
      },
      {
        type: "h2",
        content: "How The Proper Tool handles roofing quotes",
      },
      {
        type: "p",
        content: "The Proper Tool covers roofing work as part of its UK residential construction suite. You can describe a roofing job in plain text or upload drawings, and the tool generates a structured scope covering roof coverings, ridge and hip work, flashings, scaffolding, guttering, and waste disposal, all priced at UK trade rates.",
      },
      {
        type: "p",
        content: "For extension and loft conversion roofs, the tool works particularly well because it understands the full context of the job. The roofing scope is generated alongside the structural, brickwork, and internal finishing elements, giving a coherent project quote rather than isolated trade sections.",
      },
      {
        type: "p",
        content: "You review every line item, adjust quantities or specifications to match your assessment of the job, and export a professional PDF that you can send directly to the client. For a straightforward residential roofing job, the whole process takes under 10 minutes.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days. No card required. Generate your first roofing quote in under 10 minutes.",
        heading: "Professional roofing quotes, fast",
      },
    ],
    faqs: [
      {
        q: "What is the best roofing quote software for UK roofers?",
        a: "The best roofing quote software for UK roofers covers all the trade categories involved in a roofing job, not just the roof covering itself, and uses UK market pricing. It should be quick to use on any device and produce a professional PDF document that clearly sets out the scope, materials, and price. Avoid generic tools built for other markets that use non-UK pricing.",
      },
      {
        q: "How do you price a roofing job in the UK?",
        a: "Start by measuring the roof area accurately and identifying the scope: full replacement, partial repair, or new build. Price materials from current supplier quotes, tile prices, underlay, battens, and flashings all vary. Labour is typically priced per square metre or per day depending on roof complexity. Add scaffold hire, waste disposal, and any associated fascia, soffit, or guttering work. Apply overhead and a profit margin on top.",
      },
      {
        q: "Should scaffolding be included in a roofing quote?",
        a: "Yes. Scaffolding should always be included explicitly in a roofing quote, either as a separate line item or as a clearly stated inclusion. If you exclude it, clients often assume it is included and disputes follow. Specify the assumed scaffold type, the hire period, and what happens if the job overruns the hire period.",
      },
      {
        q: "How long should a roofing quote be valid for?",
        a: "Most roofers quote for 30 to 60 days. Tile prices and lead prices move with the market, so holding a price for longer than 60 days carries real financial risk. State the validity period clearly on the quote and include a provision for what happens if material prices change significantly between quote and order.",
      },
      {
        q: "What is the difference between a full re-roof and a roof repair quote?",
        a: "A full re-roof involves stripping the entire roof covering back to the rafters and relaying from scratch with new underlay, battens, and tiles. A repair quote addresses specific areas of damage or failure while leaving the remaining covering in place. The scope, materials quantities, and programme differ significantly, so treat them as separate quote types rather than scaling one from the other.",
      },
    ],
    relatedPosts: [
      { slug: "groundworks-estimating-software-uk", title: "Groundworks Estimating Software UK: Quote Groundworks Jobs Accurately" },
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },

  {
    slug: "groundworks-estimating-software-uk",
    title: "Groundworks Estimating Software UK: Quote Groundworks Jobs Accurately",
    metaTitle: "Groundworks Estimating Software UK (2026 Guide)",
    metaDescription: "Find the best groundworks estimating software for UK contractors. Generate accurate groundworks quotes covering excavation, foundations, drainage, and more.",
    publishedDate: "2025-08-04",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Tools",
    readTime: "8 min read",
    excerpt: "Groundworks is one of the hardest trades to quote accurately, ground conditions are unpredictable and scope creep is common. Here is how the right estimating software helps UK groundworks contractors build tighter, more defensible quotes.",
    sections: [
      {
        type: "p",
        content: "Groundworks is the trade where more money is lost at the quoting stage than almost anywhere else in construction. The ground is the one element of a job you cannot fully see before you start, and the consequences of getting the estimate wrong, unexpected rock, high water table, contaminated ground, unstable soil, are felt first by the groundworks contractor.",
      },
      {
        type: "p",
        content: "Despite this, most UK groundworks contractors still price jobs from a combination of experience, rule-of-thumb rates, and gut feel. This approach works on familiar ground in known areas, but it is exposed the moment conditions vary from the expected. A proper estimating process, supported by the right software, builds in the assumptions and contingencies that protect your margin.",
      },
      {
        type: "h2",
        content: "What makes groundworks estimating different",
      },
      {
        type: "p",
        content: "Most trades price work on visible, measurable quantities: square metres of tiling, linear metres of plumbing, number of electrical circuits. Groundworks pricing depends heavily on variables that are not visible at the time of quoting, soil type and bearing capacity, depth to formation level, presence of existing services, and ground water level.",
      },
      {
        type: "p",
        content: "This is why groundworks quotes need to state assumptions explicitly and include contingency provisions. A quote that is priced on the assumption of straightforward dig-and-fill in average ground conditions should say so, and should state clearly what will happen to the price if those conditions are not met.",
      },
      {
        type: "h2",
        content: "What a groundworks quote should cover",
      },
      {
        type: "ul",
        content: [
          "Site setup, access, and welfare facilities",
          "Topsoil strip and temporary stockpile or disposal",
          "Bulk excavation, volume, assumed soil classification, and disposal or retention",
          "Foundation type (strip, pad, raft, piled) and specification",
          "Concrete specification and pour volumes",
          "Drainage system, runs, manholes, inspection chambers, and connection points",
          "Hardcore fill, sub-base, and compaction for floor slabs or hard standings",
          "Floor slab construction including DPC and insulation if specified",
          "Retaining walls, edge restraints, or boundary structures if applicable",
          "Plant hire, excavator, dumpers, compactor, pump if required",
          "Disposal of excavated material (skip or muck-away by the load)",
          "Reinstatement of access and site clearance at completion",
          "Contingency provisions for unexpected ground conditions",
        ],
      },
      {
        type: "h2",
        content: "The risk of underquoting groundworks",
      },
      {
        type: "p",
        content: "Underquoting groundworks is particularly painful because the contractor is often first on site and has committed resources, plant, operatives, and material deliveries, before the extent of any problem becomes clear. When ground conditions turn out to be worse than expected, the options are to absorb the cost, argue with the client, or stop work. None of these is good.",
      },
      {
        type: "p",
        content: "A properly structured groundworks quote protects you in this situation. By stating clearly what ground conditions have been assumed, what the contingency allowance covers, and what will trigger a variation, you give yourself the contractual basis to recover legitimate extra cost without it becoming a dispute.",
      },
      {
        type: "h2",
        content: "What to look for in groundworks estimating software",
      },
      {
        type: "h3",
        content: "Quantity takeoff support",
      },
      {
        type: "p",
        content: "Groundworks estimating involves a lot of volume and area calculations, excavation cubic metres, concrete volumes, drainage run lengths, hardcore depths. Software that can read drawings and extract these quantities, or that provides a structured takeoff template, saves significant time and reduces calculation errors.",
      },
      {
        type: "h3",
        content: "UK plant and groundworks rates",
      },
      {
        type: "p",
        content: "Excavator hire, muck-away rates, concrete supply prices, and groundworks operative day rates vary by region and change with market conditions. Good estimating software should use UK-specific rates as a starting point and let you override them with your own costs and supplier quotes. Software using US or Australian rates is not usable for a UK groundworks contractor.",
      },
      {
        type: "h3",
        content: "Drainage and slab coverage",
      },
      {
        type: "p",
        content: "Drainage is a significant cost component on most groundworks packages and is easy to underestimate if done from memory. Good software should cover drainage system design (pipe sizes, falls, manhole depths), as well as sub-base and slab construction as distinct scope items with their own material and labour components.",
      },
      {
        type: "h3",
        content: "Contingency and assumption documentation",
      },
      {
        type: "p",
        content: "The best groundworks estimating tools help you document the assumptions behind your price, ground conditions, disposal assumptions, programme, and include a structured contingency allowance. This output protects you legally and commercially if conditions on site differ from what you priced.",
      },
      {
        type: "h2",
        content: "How The Proper Tool handles groundworks quotes",
      },
      {
        type: "p",
        content: "The Proper Tool covers groundworks and drainage as part of its UK residential construction suite. You can upload architectural drawings or structural engineer reports and describe the scope of the groundworks package, and the tool generates a structured estimate covering excavation, foundations, drainage, slab construction, and plant.",
      },
      {
        type: "p",
        content: "Because most groundworks packages are part of a wider residential project, an extension, a new build, or a garage, the tool generates the full project scope alongside the groundworks elements. This gives you and your client a coherent view of where the groundworks costs sit within the overall project budget.",
      },
      {
        type: "p",
        content: "You review and edit every line item, adjusting quantities and rates to reflect your specific site assessment and supplier quotes. The output is a professional PDF that clearly sets out the scope, assumptions, and pricing, the kind of document that wins work and protects you if conditions on site differ from what you priced.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days. No card required. Generate your first groundworks quote from drawings or a job description in under 10 minutes.",
        heading: "Tighter groundworks quotes, less financial risk",
      },
    ],
    faqs: [
      {
        q: "What is the best groundworks estimating software for UK contractors?",
        a: "The best groundworks estimating software for UK contractors covers all the cost components of a groundworks package, excavation, foundations, drainage, slab, plant, and uses UK market rates as a starting point. It should help you document your assumptions and contingencies clearly, and produce output that protects you commercially if ground conditions differ from what you priced.",
      },
      {
        q: "How do UK groundworks contractors price excavation work?",
        a: "Excavation is typically priced by volume, cubic metres of material removed. The rate varies by soil type, depth, and access. Easy-dig ground in open areas is priced lower than clay, rock, or restricted-access work. Add muck-away costs separately, typically priced per load, and include a contingency for unexpected depth or material type.",
      },
      {
        q: "What is a typical contingency allowance on a groundworks quote?",
        a: "Most groundworks contractors include a contingency of 10 to 20 percent on groundworks packages where ground conditions have not been confirmed by a site investigation. The contingency should be clearly stated as a line item in the quote, along with the conditions under which it would be drawn on, for example, rock encountered below 600mm, groundwater requiring pumping, or unexpected services that require diversion.",
      },
      {
        q: "Should groundworks quotes include drainage design?",
        a: "For most residential projects, the drainage system is specified by the architect or structural engineer and you are pricing against a known scope. Where the drainage layout has not been designed, it is worth flagging this in your quote and either allowing a provisional sum or confirming that the drainage design is excluded. Pricing drainage without a design is a common source of underquoting.",
      },
      {
        q: "How do groundworks contractors handle variations on site?",
        a: "Variations should be agreed in writing before additional work is carried out, wherever possible. A well-structured original quote, with assumptions and exclusions clearly stated, gives you the foundation to raise a legitimate variation instruction when site conditions differ. Keep a daily record of any changed or additional work, including plant hours, materials used, and operatives on site, so you can substantiate any variation claim.",
      },
    ],
    relatedPosts: [
      { slug: "roofing-quote-software-uk", title: "Roofing Quote Software UK: Produce Accurate Roof Quotes Without the Hassle" },
      { slug: "quoting-software-for-plumbers-uk", title: "Quoting Software for Plumbers UK: Stop Losing Jobs to Slow Quotes" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },

  {
    slug: "rear-extension-cost-uk",
    title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)",
    metaTitle: "Rear Extension Cost UK 2026: What Builders and Homeowners Should Know",
    metaDescription: "How much does a rear extension cost in the UK? A detailed breakdown of rear extension costs by size, spec level, and trade. Written for builders quoting extension work.",
    publishedDate: "2025-09-01",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "9 min read",
    excerpt: "A rear extension is one of the most commonly quoted residential building projects in the UK. Getting the pricing right means understanding what drives the cost, from structural work and roofing through to internal fit-out and groundworks. This guide covers the full cost breakdown so you can quote confidently.",
    sections: [
      {
        type: "p",
        content: "Rear extensions remain one of the most popular home improvement projects in the UK. Homeowners planning to extend regularly search for cost guidance, and builders quoting this type of work need to understand exactly what goes into an accurate price. A quote that is too low wins the job and loses money; one that is too high sends the client elsewhere.",
      },
      {
        type: "p",
        content: "This guide breaks down the real costs involved in a typical UK rear extension, by trade, by size, and by specification level, so builders can price accurately and clients can understand what they are paying for.",
      },
      {
        type: "h2",
        content: "What drives the cost of a rear extension?",
      },
      {
        type: "p",
        content: "The cost of a rear extension is driven by four main variables: the size of the extension (floor area and height), the structural specification (steel frames, load-bearing walls, bi-fold openings), the quality of fit-out and finishes, and the location (London and the South East command a significant labour premium).",
      },
      {
        type: "p",
        content: "A basic 4m x 4m single storey rear extension in brick and tile, with standard internal finishes, will cost considerably less than a 6m x 4m extension with a full-width glazed opening, structural steelwork, underfloor heating, and high-end kitchen integration. Both are rear extensions. The cost range between them can be three to one.",
      },
      {
        type: "h2",
        content: "Typical rear extension costs in the UK (2026)",
      },
      {
        type: "p",
        content: "As a general guide, rear extension costs in the UK currently sit in the following ranges:",
      },
      {
        type: "ul",
        content: [
          "Small single storey rear extension (up to 15m²): £25,000 to £45,000",
          "Medium single storey rear extension (15–25m²): £40,000 to £70,000",
          "Large single storey rear extension (25–40m²): £60,000 to £100,000+",
          "Double storey rear extension: add 40 to 60 percent to the equivalent single storey cost",
          "London and South East: add 20 to 30 percent to the figures above",
        ],
      },
      {
        type: "p",
        content: "These figures include all trades from groundworks to decoration and represent a mid-spec build. High-specification finishes, bespoke glazing, underfloor heating, and structural complexity will push costs toward the upper end and beyond.",
      },
      {
        type: "h2",
        content: "Cost breakdown by trade",
      },
      {
        type: "p",
        content: "Understanding which trades contribute what to the total cost helps builders quote accurately and helps clients understand where their money is going.",
      },
      {
        type: "h3",
        content: "Groundworks and foundations",
      },
      {
        type: "p",
        content: "Foundations are a significant cost item and one of the most variable. A standard strip foundation in good ground conditions might cost £4,000 to £8,000 for a medium extension. Ground that requires deeper excavation, a raft, or piled foundations can double or triple this. If a structural engineer specifies anything other than a standard strip, get a specific quote, do not carry a standard allowance.",
      },
      {
        type: "h3",
        content: "Structural work and steelwork",
      },
      {
        type: "p",
        content: "Most rear extensions involve opening up the rear wall of the existing house with a steel beam. The cost of the steel alone can range from £800 to £3,000+ depending on span and section size. Installation, including temporary support, masonry making good, and padstone construction, typically adds a further £1,500 to £3,500. For wider openings or more complex structural arrangements, budget more.",
      },
      {
        type: "h3",
        content: "Masonry and brickwork",
      },
      {
        type: "p",
        content: "External walls in brick and block cavity construction are typically £90 to £140 per square metre of wall face, including materials and labour. For a medium extension this might represent £6,000 to £15,000 depending on wall area and openings. Matching existing brick adds cost if the original brick is discontinued or requires careful sourcing.",
      },
      {
        type: "h3",
        content: "Roofing",
      },
      {
        type: "p",
        content: "The most common roofing options for a rear extension are a flat roof (built-up felt, GRP fibreglass, or a warm deck system) and a pitched roof continuing the house roof line. Flat roofs in GRP typically cost £80 to £130 per square metre installed. Pitched roofs in matching tile are typically £120 to £180 per square metre. Lantern lights and rooflights add £800 to £3,000+ each depending on size and specification.",
      },
      {
        type: "h3",
        content: "Windows and doors",
      },
      {
        type: "p",
        content: "Standard uPVC French doors or bifold doors start at around £1,200 to £2,500 supplied and fitted. Aluminium bifold or sliding systems in wider openings, which are now common on rear extensions, typically run from £3,500 to £8,000 installed depending on width, configuration and glazing specification. High-end systems with thermally broken frames and premium glazing cost more.",
      },
      {
        type: "h3",
        content: "First and second fix carpentry",
      },
      {
        type: "p",
        content: "Internal stud walls, floor structure, insulation, boarding, and skirting and architrave in a medium extension typically cost £4,000 to £9,000 for materials and labour combined. The figure varies significantly with the amount of internal subdivision and the specification of boarding and joinery.",
      },
      {
        type: "h3",
        content: "Plastering",
      },
      {
        type: "p",
        content: "A full skim plaster finish on a medium extension (new walls plus making good to the existing house) typically costs £2,500 to £5,000. Dry-lining and skim finishes are the current standard for most residential work.",
      },
      {
        type: "h3",
        content: "Mechanical and electrical",
      },
      {
        type: "p",
        content: "A typical rear extension requires electrical first and second fix (lighting, sockets, consumer unit alterations), heating extension or underfloor heating installation, and possibly plumbing for a kitchen or utility. Electrical work for a medium extension typically runs from £2,500 to £5,000. Underfloor heating adds £800 to £2,500 depending on floor area and whether a wet or electric system is used.",
      },
      {
        type: "h3",
        content: "Decoration and floor finishes",
      },
      {
        type: "p",
        content: "A full decoration package for a medium extension (walls, ceiling, woodwork, two coats throughout) typically costs £1,500 to £3,500. Floor finishes vary enormously, tiled floors cost more than engineered wood or LVT, and prices for supply and installation vary from £25 to £80+ per square metre depending on specification.",
      },
      {
        type: "h2",
        content: "Costs that builders often underestimate",
      },
      {
        type: "ul",
        content: [
          "Scaffold: a full rear-elevation scaffold for a single storey extension typically costs £1,500 to £3,000 depending on height and duration.",
          "Skip and waste disposal: allow £600 to £1,500 across a medium project for multiple skips.",
          "Making good to the existing house: opening the rear wall and integrating a new extension into an existing property always involves more making-good work than the drawings suggest.",
          "Structural engineer fees: most rear extensions require a structural engineer report and calculations. Budget £500 to £1,500.",
          "Building regulations fees: check the local authority fee schedule, fees are project-specific and range from £200 to £800+ for a residential extension.",
          "Party wall surveyor fees if applicable: can add £700 to £2,500 per adjacent property.",
        ],
      },
      {
        type: "h2",
        content: "How to quote a rear extension accurately",
      },
      {
        type: "p",
        content: "Accurate rear extension quoting starts with architectural drawings and a structural engineer report. If drawings are available, they tell you the floor area, wall areas, opening sizes, structural requirements, and roof specification, the information you need to build an accurate cost plan.",
      },
      {
        type: "p",
        content: "Without drawings, you are working from a site visit and a description. This increases risk. Always state your assumptions clearly in the quote and include a scope of exclusions so that variations can be properly managed if the design changes.",
      },
      {
        type: "p",
        content: "Structure your quote as a full line-item breakdown by trade. A single lump-sum figure gives the client no insight into where the money is going and makes it almost impossible to manage variations properly. A proper scope of works, broken down by trade with descriptions and quantities, protects both you and the client.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload your rear extension drawings and generate a full line-item quote across all trades in under 10 minutes.",
        heading: "Quote rear extensions faster and more accurately",
      },
    ],
    faqs: [
      {
        q: "How much does a rear extension cost per square metre in the UK?",
        a: "In the UK in 2026, a rear extension typically costs between £1,800 and £3,500 per square metre of floor area depending on specification, location, and structural complexity. Mid-spec work in the Midlands or North sits toward the lower end; high-spec work in London or the South East sits at the upper end and above. These figures include all trades from groundworks to decoration.",
      },
      {
        q: "What is not included in most rear extension quotes?",
        a: "Most builder quotes for rear extensions exclude planning permission and building regulations fees, structural engineer fees, architect fees, party wall surveyor costs, and any kitchen units, bathroom fixtures, or furniture. Always read the scope of exclusions carefully before accepting a quote.",
      },
      {
        q: "How long does a rear extension take to build?",
        a: "A medium single storey rear extension typically takes 10 to 16 weeks on site from groundworks start to decoration completion. Complex structural work, large glazed openings, underfloor heating, and high-specification finishes all extend the programme. Double storey extensions typically take 16 to 24 weeks.",
      },
      {
        q: "Do I need planning permission for a rear extension?",
        a: "Many single storey rear extensions fall within Permitted Development rights, provided they meet size and siting criteria. Double storey extensions and extensions in conservation areas typically require planning permission. Always confirm with your local authority or planning consultant before starting work. The builder's quote should always be conditional on planning and building regulations approval.",
      },
      {
        q: "Why do rear extension quotes vary so much between builders?",
        a: "Variation between quotes can reflect genuine differences in scope (some builders include items others exclude), differences in quality specification, different margin levels, and differences in pricing sophistication. Always compare quotes on a like-for-like basis by checking what is and is not included in each one before making a decision on price alone.",
      },
    ],
    relatedPosts: [
      { slug: "extension-quote-template-uk", title: "Extension Quote Template: What Every UK Builder Should Include" },
      { slug: "single-storey-extension-cost-uk", title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedGuides: [
      { href: "/guides/running-a-building-business-uk", title: "Running a Building Business in the UK: The Complete Guide" },
    ],
  },

  {
    slug: "single-storey-extension-cost-uk",
    title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026",
    metaTitle: "Single Storey Extension Cost UK 2026: Builder's Quoting Guide",
    metaDescription: "Single storey extension costs in the UK explained. A builder's guide to quoting kitchen extensions, rear infills, and side returns accurately in 2026.",
    publishedDate: "2025-10-06",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "8 min read",
    excerpt: "Single storey extensions, kitchen extensions, rear additions, side returns, are the bread and butter of UK residential building. Here is a complete guide to pricing them accurately in 2026, covering every trade from groundworks to decoration.",
    sections: [
      {
        type: "p",
        content: "Single storey extensions are the most commonly built type of domestic extension in the UK. Whether it is a rear kitchen extension, a side infill, or a wrap-around addition, the basic construction logic is similar, and the pricing requirements are the same. Get every line item right, or the job costs you money.",
      },
      {
        type: "p",
        content: "This guide is written for UK builders who need to quote single storey extension work accurately. It covers the key cost drivers, typical price ranges by size and spec, the trades you need to account for, and the items most commonly missed in extension quotes.",
      },
      {
        type: "h2",
        content: "Types of single storey extension and how they affect cost",
      },
      {
        type: "h3",
        content: "Rear extension",
      },
      {
        type: "p",
        content: "A straightforward rear extension is the simplest type to quote. It involves new foundations, external walls on three sides, a roof, a rear opening into the existing house (usually with a steel beam), and internal fit-out. Cost is driven primarily by floor area, the size of the glazed opening at the rear, and the roof specification.",
      },
      {
        type: "h3",
        content: "Side return extension",
      },
      {
        type: "p",
        content: "Side return extensions fill the narrow gap between a terraced or semi-detached house and the boundary. They typically add less floor area than a full rear extension but can transform the feel of a ground floor. The party wall situation is often more complex, the structural work around the existing flank wall more fiddly, and access for plant and materials more restricted, all of which pushes cost per square metre above a standard rear extension.",
      },
      {
        type: "h3",
        content: "Wrap-around extension",
      },
      {
        type: "p",
        content: "A wrap-around extension combines a rear and side addition in an L-shape. It is larger and more complex than either type individually, and the junction between the two elements, roofing, walls, drainage, requires careful detailing. Cost per square metre is typically similar to a rear extension on the rear section and higher on the side return section.",
      },
      {
        type: "h2",
        content: "Single storey extension cost guide (UK, 2026)",
      },
      {
        type: "ul",
        content: [
          "Small extension (up to 15m²): £28,000 to £48,000 at mid spec",
          "Medium extension (15–25m²): £42,000 to £72,000 at mid spec",
          "Large extension (25–40m²): £65,000 to £105,000 at mid spec",
          "Side return extension: typically £35,000 to £65,000 for a standard Victorian terraced house",
          "Wrap-around extension: typically £70,000 to £130,000+ depending on footprint",
          "London / South East premium: add 20 to 30 percent to the figures above",
        ],
      },
      {
        type: "p",
        content: "High-specification work, aluminium bifold systems, bespoke structural glazing, underfloor heating, premium kitchen installations, feature rooflight arrays, adds materially to any of the above ranges. Always define the specification clearly in your quote.",
      },
      {
        type: "h2",
        content: "Key cost items in a single storey extension quote",
      },
      {
        type: "h3",
        content: "Foundations",
      },
      {
        type: "p",
        content: "Strip foundations in normal ground are the standard for single storey extensions. Budget £4,000 to £9,000 for a medium extension. Deeper foundations, raft designs, or piled solutions are significantly more expensive and always require a structural engineer to specify. Never price foundations without knowing the ground conditions, a precautionary contingency of £2,000 to £5,000 is prudent on most jobs.",
      },
      {
        type: "h3",
        content: "Steel beam and structural opening",
      },
      {
        type: "p",
        content: "Opening the rear or side wall of an existing house requires a structural beam. Steel section costs vary by span, a 3m to 4m beam in a typical rear opening might cost £1,200 to £2,500 supplied. Installation including temporary support, padstones, and masonry making good adds £1,500 to £3,000. Wider openings or multiple beam arrangements cost more. Always price from the structural engineer drawings.",
      },
      {
        type: "h3",
        content: "External walls",
      },
      {
        type: "p",
        content: "Brick and block cavity wall construction costs approximately £95 to £145 per square metre of wall face including all materials and labour. For a medium extension with a moderate level of openings, external wall costs typically run from £7,000 to £14,000. If the client wants to match existing brickwork and the original brick is discontinued, allow extra for sourcing, matching brick can add £300 to £800 or more depending on the job.",
      },
      {
        type: "h3",
        content: "Roof",
      },
      {
        type: "p",
        content: "The two most common roof types for single storey extensions are flat roofs (typically in GRP fibreglass or a warm deck build-up) and pitched roofs in matching tile. GRP flat roofs typically cost £85 to £130 per square metre installed. Pitched roofs cost £130 to £180 per square metre depending on tile type and pitch. Rooflights add significant cost and should be itemised individually, budget £900 to £2,500+ per rooflight depending on size.",
      },
      {
        type: "h3",
        content: "Windows and doors",
      },
      {
        type: "p",
        content: "Standard uPVC bifold doors in aluminium or PVC start from around £1,500 to £2,500 for a two-panel set. Full-width aluminium bifold systems (3m to 5m) typically cost £4,000 to £9,000 supplied and fitted. Large sliding doors or structural glazing systems cost more. Always get a firm supply quote before including glazing costs, prices move and specifications vary.",
      },
      {
        type: "h3",
        content: "First fix, boarding, and insulation",
      },
      {
        type: "p",
        content: "Internal stud partitions, floor structure and insulation, ceiling board, and wall boarding for a medium extension typically cost £4,500 to £9,000 in combined materials and labour. Floor insulation and a screed for underfloor heating adds a further £1,500 to £3,500 depending on area.",
      },
      {
        type: "h3",
        content: "Plastering and skimming",
      },
      {
        type: "p",
        content: "Skim plaster throughout a medium extension plus making good to the existing house typically costs £2,500 to £5,000 for labour and materials. The existing house making-good element is easy to underestimate, opening up a rear elevation involves significantly more repair and preparation work than the new extension walls alone.",
      },
      {
        type: "h3",
        content: "Electrical and heating",
      },
      {
        type: "p",
        content: "A typical single storey extension requires electrical first and second fix (lighting, sockets, and consumer unit alterations), plus a heating extension or standalone underfloor heating circuit. Electrical work runs from £2,500 to £5,500 for a medium extension. Wet underfloor heating adds £1,000 to £2,500 for the flow area depending on layout and system specification.",
      },
      {
        type: "h3",
        content: "Decoration and finishes",
      },
      {
        type: "p",
        content: "Full decoration of a medium extension including two coats throughout, woodwork, and making good to adjacent rooms typically costs £1,500 to £3,500. Floor finishes are additional and vary by specification, allow £30 to £90 per square metre for supply and installation of tiles, engineered wood, or LVT.",
      },
      {
        type: "h2",
        content: "What most single storey extension quotes miss",
      },
      {
        type: "ul",
        content: [
          "Scaffolding: £1,200 to £2,500 for a standard rear elevation, more if the site has restricted access or unusual height",
          "Skip and waste disposal: £700 to £1,500 over the duration of the project for multiple exchanges",
          "Drainage connections: connecting new drainage from the extension to existing runs can cost £800 to £2,500+ depending on depth and distance",
          "Making good to existing house: always allow more than you think, opening up a rear wall creates significant disruption to the existing structure",
          "Structural engineer fees: £500 to £1,200 for a standard single storey extension",
          "Building regulations fees and inspections: £200 to £600 for a standard extension; check the local authority fee schedule",
          "Provisional sums for unknown ground conditions: state explicitly in the quote that foundations are priced on assumed conditions and that a provisional sum covers unknown depth",
        ],
      },
      {
        type: "h2",
        content: "Getting your single storey extension quote right",
      },
      {
        type: "p",
        content: "The most reliable way to produce an accurate single storey extension quote is from a full set of architectural drawings and a structural engineer report. These tell you the structural specification, opening sizes, roof details, and drainage requirements, the information that determines cost.",
      },
      {
        type: "p",
        content: "When drawings are not yet available, quote from a detailed site visit and state your assumptions clearly. Include a contingency for ground conditions, define your scope exclusions in full, and specify your programme so the client understands what they are buying.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload your extension drawings and generate a full line-item quote across all trades in under 10 minutes.",
        heading: "Quote single storey extensions faster",
      },
    ],
    faqs: [
      {
        q: "How much does a single storey extension cost per m² in the UK in 2026?",
        a: "In 2026, a single storey extension in the UK typically costs between £1,900 and £3,500 per square metre of floor area at mid specification. The range reflects differences in structural complexity, glazing specification, location, and internal fit-out. London and South East jobs sit at the upper end; the Midlands and North typically sit 15 to 25 percent lower.",
      },
      {
        q: "How long does a single storey extension take to build?",
        a: "A standard single storey extension typically takes 10 to 18 weeks on site depending on size and complexity. Groundworks and structural work take the longest to programme; internal trades and decoration follow in sequence. Complex structural arrangements, bespoke glazing, or underfloor heating all add to the programme.",
      },
      {
        q: "What is the difference between quoting a side return and a rear extension?",
        a: "Side return extensions are typically more expensive per square metre than rear extensions because access is more restricted, party wall work is more likely, and the structural interface with the existing flank wall is more complex. Expect to price a side return at 10 to 20 percent above an equivalent-area rear extension on a per-square-metre basis.",
      },
      {
        q: "Should a single storey extension quote include VAT?",
        a: "If you are VAT-registered, yes, you must add VAT at 20 percent to the net price. Some extension work on certain building types qualifies for reduced VAT rates, but standard domestic single storey extensions are liable at 20 percent. Always confirm your VAT position with your accountant if you are unsure.",
      },
      {
        q: "What is a realistic profit margin on a single storey extension?",
        a: "Most UK builders target a net profit margin of 10 to 20 percent on residential extension work. This is on top of a full cost build-up that includes all direct costs, overhead allocation, and any subcontractor management margin. If you are not adding explicit overhead and profit to your extension quotes, you are almost certainly undercharging.",
      },
    ],
    relatedPosts: [
      { slug: "rear-extension-cost-uk", title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)" },
      { slug: "extension-quote-template-uk", title: "Extension Quote Template: What Every UK Builder Should Include" },
      { slug: "loft-conversion-cost-breakdown-uk", title: "Loft Conversion Cost Breakdown UK: Builder's Pricing Guide 2026" },
    ],
  },

  {
    slug: "loft-conversion-cost-breakdown-uk",
    title: "Loft Conversion Cost Breakdown UK: Builder's Pricing Guide 2026",
    metaTitle: "Loft Conversion Cost Breakdown UK 2026: Builder's Pricing Guide",
    metaDescription: "A full loft conversion cost breakdown for UK builders. Understand what drives loft conversion prices by type, dormer, hip-to-gable, Velux, and mansard, so you can quote accurately.",
    publishedDate: "2025-11-10",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "10 min read",
    excerpt: "Loft conversions are complex to price. The cost varies significantly by conversion type, structural requirements, and fit-out specification. This guide breaks down every cost element so UK builders can produce accurate, competitive loft conversion quotes.",
    sections: [
      {
        type: "p",
        content: "Loft conversions are one of the most valuable home improvement projects a UK homeowner can undertake, and one of the most complex for a builder to price. Unlike a straightforward extension, a loft conversion involves significant structural alteration to an existing roof, often in a confined space, with multiple trade packages running in close sequence. Getting the cost breakdown right from the start is essential.",
      },
      {
        type: "p",
        content: "This guide covers the full cost breakdown of a UK loft conversion in 2026, by type and by trade, so builders can quote accurately and clients can understand what they are getting for their money.",
      },
      {
        type: "h2",
        content: "Loft conversion types and how they affect cost",
      },
      {
        type: "h3",
        content: "Velux (rooflight) loft conversion",
      },
      {
        type: "p",
        content: "The simplest and least expensive type. The existing roof structure is retained and Velux or similar rooflights are installed to provide light and ventilation. There is no change to the external roof envelope, which means no planning permission is needed in most cases and the structural work is minimal. Cost is driven primarily by the floor strengthening, staircase, and internal fit-out.",
      },
      {
        type: "p",
        content: "Typical cost range: £25,000 to £45,000 including staircase, floor strengthening, rooflights, insulation, and a basic en-suite bathroom.",
      },
      {
        type: "h3",
        content: "Dormer loft conversion",
      },
      {
        type: "p",
        content: "A dormer extends the rear roof slope to create additional headroom and floor area. It is the most commonly built type of loft conversion in the UK and offers a good balance of cost, floor area gain, and disruption. The structural work involves cutting into the existing roof, forming a new structural box frame with a flat roof, and rebuilding the rear elevation including windows and cladding.",
      },
      {
        type: "p",
        content: "Typical cost range: £40,000 to £65,000 for a standard rear dormer on a two-storey house, including staircase, dormer structure, flat roof, windows, insulation, and a basic en-suite. Larger dormers and higher-specification fit-outs cost more.",
      },
      {
        type: "h3",
        content: "Hip-to-gable loft conversion",
      },
      {
        type: "p",
        content: "This type is used on semi-detached and detached houses with a hipped roof. The sloped hip is squared off to form a new vertical gable wall, creating significantly more usable floor area than a dormer alone. It often involves a substantial amount of masonry and structural work and almost always requires planning permission. Hip-to-gable conversions are typically combined with a rear dormer.",
      },
      {
        type: "p",
        content: "Typical cost range: £55,000 to £85,000 for a hip-to-gable conversion with rear dormer, including all structural work, masonry, staircase, and basic fit-out.",
      },
      {
        type: "h3",
        content: "Mansard loft conversion",
      },
      {
        type: "p",
        content: "A mansard conversion replaces most of the roof structure with near-vertical rear walls and a shallow-pitched roof. It maximises headroom and floor area but is the most structurally complex and expensive type. Mansard conversions almost always require planning permission and are most common in urban areas, particularly London.",
      },
      {
        type: "p",
        content: "Typical cost range: £65,000 to £120,000+ depending on footprint and specification. London jobs are often at or above the upper end of this range.",
      },
      {
        type: "h2",
        content: "Cost breakdown by trade: what goes into a loft conversion",
      },
      {
        type: "h3",
        content: "Structural work and roof alterations",
      },
      {
        type: "p",
        content: "This is the largest single trade cost on most loft conversions. It includes removing or modifying the existing roof structure, installing steel beams (typically two ridge beams plus floor beams), constructing the dormer box frame, and rebuilding any new external walls. Allow £12,000 to £25,000 for the structural package on a standard dormer conversion, significantly more on hip-to-gable or mansard work.",
      },
      {
        type: "h3",
        content: "Flat roof to dormer",
      },
      {
        type: "p",
        content: "The dormer flat roof is typically finished in GRP fibreglass or a warm deck build-up. GRP is the most common choice for durability and low maintenance. Expect £85 to £135 per square metre installed for the flat roof covering itself, plus the structural deck and associated joinery.",
      },
      {
        type: "h3",
        content: "Masonry and external cladding",
      },
      {
        type: "p",
        content: "Dormer cheeks (the sides of the dormer) are typically clad in lead, zinc, slate, or fibre cement panels. Masonry gable walls on a hip-to-gable conversion are priced by area at the same rates as standard brickwork, £90 to £145 per square metre of wall face. Lead or zinc cladding to dormer cheeks typically costs £80 to £140 per square metre installed.",
      },
      {
        type: "h3",
        content: "Windows and dormer glazing",
      },
      {
        type: "p",
        content: "Dormer windows are typically casement or tilt-and-turn in uPVC or aluminium. A standard 1.2m x 1.2m dormer window costs £400 to £900 supplied and fitted. Velux conservation rooflights in the existing roof cost £600 to £1,500 each installed depending on size. Juliet balconies for full-height glazing add £1,500 to £4,000.",
      },
      {
        type: "h3",
        content: "Floor structure and strengthening",
      },
      {
        type: "p",
        content: "Existing loft joists are rarely strong enough to carry floor loads. A new structural floor, typically using engineered timber joists or a steel and timber hybrid, is required on all conversion types. Allow £3,500 to £8,000 for floor structure, insulation, and boarding depending on floor area and structural specification.",
      },
      {
        type: "h3",
        content: "Staircase",
      },
      {
        type: "p",
        content: "A new loft staircase is a significant cost item and one of the most variable. A standard softwood staircase with cut strings and standard balustrade costs £1,800 to £3,500 supplied and installed. Bespoke or feature staircases in hardwood, glass, or steel cost substantially more. The structural opening through the existing ceiling and any making good around it adds further cost.",
      },
      {
        type: "h3",
        content: "Insulation",
      },
      {
        type: "p",
        content: "Building regulations require high levels of thermal insulation in a converted loft. Roof insulation between and below rafters (or a warm deck arrangement on the dormer) plus floor insulation typically costs £2,500 to £5,000 for a standard conversion in materials and installation.",
      },
      {
        type: "h3",
        content: "First and second fix carpentry and boarding",
      },
      {
        type: "p",
        content: "Stud partitions, wall boarding, ceiling board, door lining, skirting, and architrave for a loft conversion bedroom with en-suite typically costs £4,000 to £9,000 in combined materials and labour.",
      },
      {
        type: "h3",
        content: "Plastering",
      },
      {
        type: "p",
        content: "Full skim plaster to all new surfaces in a loft conversion typically costs £2,000 to £4,500 for a standard dormer conversion. The irregular shapes created by raking ceilings and dormer junction details make loft plastering slightly more labour-intensive than a flat-ceilinged extension.",
      },
      {
        type: "h3",
        content: "En-suite bathroom",
      },
      {
        type: "p",
        content: "Most loft conversions include at least a basic en-suite shower room. A compact en-suite with standard sanitary ware typically costs £4,500 to £9,000 for all trade work including plumbing, tiling, electrical, and decoration. High-specification en-suites with premium fixtures and full tilework cost substantially more.",
      },
      {
        type: "h3",
        content: "Electrical and mechanical",
      },
      {
        type: "p",
        content: "Electrical work for a loft conversion includes first and second fix wiring, lighting and sockets in the new room and bathroom, consumer unit alterations, and smoke detector installation (required by building regulations). Allow £2,500 to £5,000 for a standard conversion. If a new heating circuit and radiators are required, add £800 to £2,000.",
      },
      {
        type: "h3",
        content: "Decoration",
      },
      {
        type: "p",
        content: "Full decoration to a converted loft bedroom and en-suite typically costs £1,500 to £3,000. Allow more if the client requires high-specification decoration, feature walls, or if there is significant making-good work required to adjacent rooms and the staircase landing.",
      },
      {
        type: "h2",
        content: "Items commonly missed in loft conversion quotes",
      },
      {
        type: "ul",
        content: [
          "Scaffolding: full scaffold to the roof for a dormer conversion typically costs £2,000 to £4,000 depending on height and access",
          "Skip and waste disposal: roof timbers, insulation, and general waste can require multiple skips, allow £800 to £1,800",
          "Party wall surveyor fees if applicable: common on semi-detached houses, can add £700 to £2,500 per wall",
          "Structural engineer fees: essential for all loft conversions, budget £600 to £1,500",
          "Building regulations fees: £400 to £900 for a standard loft conversion; confirm with the local authority",
          "Making good to the staircase landing and bedroom below: opening the ceiling to insert a staircase always creates significant disruption to the floor below",
          "Temporary roof weathering during structural works: if the conversion involves a period where the roof is open, temporary waterproofing is essential and should be costed",
        ],
      },
      {
        type: "h2",
        content: "How to produce an accurate loft conversion quote",
      },
      {
        type: "p",
        content: "A loft conversion quote should always be based on a proper site visit and, ideally, a set of architectural drawings. Visit the loft space and assess the existing roof structure, the available headroom, the party wall situation, and the access for materials and plant. These factors vary significantly between properties and affect cost materially.",
      },
      {
        type: "p",
        content: "Structure your quote as a full trade-by-trade breakdown. A line-item quote that separates structural work, roofing, carpentry, plastering, electrical, plumbing, and decoration gives the client visibility of the full scope and gives you a clear basis for managing variations if the design changes.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Describe your loft conversion or upload drawings and get a full trade-by-trade cost breakdown in under 10 minutes.",
        heading: "Quote loft conversions with confidence",
      },
    ],
    faqs: [
      {
        q: "How much does a loft conversion cost in the UK in 2026?",
        a: "In 2026, a UK loft conversion costs between £25,000 and £120,000+ depending on type. A Velux conversion starts from around £25,000 to £45,000; a standard rear dormer from £40,000 to £65,000; a hip-to-gable conversion from £55,000 to £85,000; and a mansard conversion from £65,000 to £120,000 or above. London and South East jobs typically sit 20 to 30 percent above these ranges.",
      },
      {
        q: "What is the most cost-effective type of loft conversion?",
        a: "A Velux rooflight conversion is the least expensive type because it requires no changes to the external roof envelope. However, it provides less floor area and headroom than a dormer or mansard. For most two-storey houses, a rear dormer conversion offers the best balance of cost and usable space.",
      },
      {
        q: "How long does a loft conversion take to build?",
        a: "A standard dormer loft conversion typically takes 10 to 16 weeks from start to completion. Hip-to-gable and mansard conversions take longer, typically 14 to 20 weeks, due to the greater volume of structural and masonry work involved. Programme planning is important: the roof is open to the elements during structural works, so weather risk and temporary protection need to be factored in.",
      },
      {
        q: "Does a loft conversion always need building regulations approval?",
        a: "Yes. All loft conversions require building regulations approval regardless of whether planning permission is needed. Building regulations cover structural integrity, fire safety (including fire doors and escape routes), thermal insulation, and electrical installation. Never start a loft conversion without confirming that building regulations approval is in place.",
      },
      {
        q: "What adds the most cost to a loft conversion?",
        a: "The biggest cost drivers in a loft conversion are the type of conversion (mansard > hip-to-gable > dormer > Velux), the structural specification (size and number of steel beams, new gable masonry), the glazing and window specification, and the en-suite fit-out quality. On a fixed footprint, upgrading the kitchen or bathroom specification is the fastest way to increase total project cost.",
      },
    ],
    relatedPosts: [
      { slug: "loft-conversion-cost-estimate-uk", title: "How to Price a Loft Conversion: A UK Builder's Guide to Accurate Estimates" },
      { slug: "single-storey-extension-cost-uk", title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
  },

  {
    slug: "bathroom-renovation-cost-uk",
    title: "How Much Does a Bathroom Renovation Cost in the UK? (2026 Guide)",
    metaTitle: "Bathroom Renovation Cost UK 2026: What Builders and Homeowners Should Expect",
    metaDescription: "How much does a bathroom renovation cost in the UK? A complete cost breakdown by trade and specification level, written for builders quoting bathroom refit work.",
    publishedDate: "2025-12-08",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "8 min read",
    excerpt: "Bathroom renovations are one of the most frequently quoted residential jobs in the UK. Costs vary enormously depending on the size of the room, the quality of fixtures, and the amount of structural or plumbing reconfiguration involved. This guide breaks down what a bathroom renovation actually costs, and what drives that price.",
    sections: [
      {
        type: "p",
        content: "Bathroom renovations generate more online searches than almost any other home improvement project in the UK. Homeowners planning a refit want to know what to budget; builders quoting the work need to price it accurately. The problem is that 'bathroom renovation' can mean anything from a straight swap of sanitaryware in an unchanged room to a complete gut and rebuild with full replumbing, new tiling, underfloor heating, and bespoke joinery.",
      },
      {
        type: "p",
        content: "This guide breaks down the real costs involved in a UK bathroom renovation in 2026, by specification level and by trade, so builders can produce accurate quotes and clients understand what they are paying for.",
      },
      {
        type: "h2",
        content: "What drives the cost of a bathroom renovation?",
      },
      {
        type: "p",
        content: "The single biggest factor in bathroom renovation cost is whether the layout is changing. A like-for-like refit, new sanitaryware in the same positions, same tile format, no structural work, is primarily a labour and materials job that can be completed in under a week. Move the toilet, relocate the shower, or reconfigure the plumbing layout, and costs rise substantially because of the plumbing reconfiguration, floor raising or chasing, and making good required.",
      },
      {
        type: "p",
        content: "Other major cost drivers are: the quality of sanitaryware and fittings selected; the tile specification and area covered; whether underfloor heating is being installed; the condition of the existing plumbing; and whether structural work is required to enlarge the room or improve headroom.",
      },
      {
        type: "h2",
        content: "Typical bathroom renovation costs in the UK (2026)",
      },
      {
        type: "p",
        content: "As a guide, bathroom renovation costs in the UK currently fall into the following ranges. These figures cover all trades from strip-out to decoration, including sanitaryware at the specification level stated, but excluding any structural work.",
      },
      {
        type: "ul",
        content: [
          "Budget refit, standard sanitaryware, mid-range tiles, like-for-like layout: £3,500 to £6,500",
          "Mid-range renovation, good quality sanitaryware, larger format tiles, minor layout changes: £6,500 to £12,000",
          "High-specification renovation, premium sanitaryware, bespoke tiling, freestanding bath, full replumbing: £12,000 to £20,000+",
          "Wet room or walk-in shower conversion, floor tanking, level access drain, frameless glazing: £8,000 to £15,000",
          "London and South East: add 20 to 30 percent to the ranges above",
        ],
      },
      {
        type: "p",
        content: "These figures assume a standard bathroom or shower room of 4 to 8 square metres. En-suite bathrooms at the smaller end cost less; family bathrooms with separate bath and large shower enclosure at the upper end cost more.",
      },
      {
        type: "h2",
        content: "Cost breakdown by trade",
      },
      {
        type: "h3",
        content: "Strip-out and waste disposal",
      },
      {
        type: "p",
        content: "Strip-out of an existing bathroom, removing sanitaryware, tiles, flooring, and boxing, typically costs £400 to £900 including skip hire. If asbestos is present in old floor tiles or ceiling panels (common in pre-1980s properties), budget for specialist removal and disposal at £500 to £1,500 depending on the extent.",
      },
      {
        type: "h3",
        content: "First fix plumbing",
      },
      {
        type: "p",
        content: "First fix covers all pipework runs before the walls are boarded or tiled, supply pipes, waste pipes, and any reconfiguration of the layout. A straightforward like-for-like first fix typically costs £600 to £1,200 in labour. If the layout is changing or the pipes are being re-routed through joists and walls, budget £1,200 to £2,500 depending on complexity. Always allow time and cost for the plumber to commission and test before boarding up.",
      },
      {
        type: "h3",
        content: "Waterproofing and tanking",
      },
      {
        type: "p",
        content: "Shower enclosures and wet rooms require specialist waterproofing before tiling. A standard shower enclosure tanked with a proprietary membrane system typically costs £300 to £600 in materials and labour. A full wet room floor tanked to the correct falls, including a linear drain and level access, is significantly more work and typically costs £800 to £1,800.",
      },
      {
        type: "h3",
        content: "Tiling",
      },
      {
        type: "p",
        content: "Tiling is frequently the largest single cost category in a bathroom renovation after sanitaryware. Labour for wall and floor tiling in a standard bathroom runs from £35 to £60 per square metre. Large format tiles (600x600 and above) or natural stone require more preparation, more precise setting out, and more waste, and carry a labour premium of 20 to 40 percent. A full bathroom tiled floor to ceiling, walls and floor, in a standard bathroom might represent 20 to 30 square metres of tiling at a total cost (materials and labour) of £2,500 to £6,000 depending on tile specification.",
      },
      {
        type: "h3",
        content: "Sanitaryware and second fix plumbing",
      },
      {
        type: "p",
        content: "Second fix plumbing covers the installation of sanitaryware: bath, basin, toilet, shower tray or wet room former, and shower valve. Labour for second fix in a standard bathroom runs £500 to £1,000. The cost of the sanitaryware itself varies enormously, a complete suite from a mid-range supplier might cost £800 to £2,000, while a premium specification with a freestanding bath, wall-hung WC, and designer taps can exceed £5,000 to £10,000 in materials alone.",
      },
      {
        type: "h3",
        content: "Electrical work",
      },
      {
        type: "p",
        content: "Bathroom electrical work is Zone-rated and must be carried out by a qualified electrician. A standard bathroom renovation typically requires: a new extractor fan; lighting to the correct IP rating; shaver socket; heated towel rail connection; and any underfloor heating wiring if applicable. Budget £600 to £1,200 for a standard bathroom electrical package. If the consumer unit needs updating or the bathroom is being added where none previously existed, costs are higher.",
      },
      {
        type: "h3",
        content: "Underfloor heating",
      },
      {
        type: "p",
        content: "Electric underfloor heating is the most common choice for bathroom renovations because it does not require connection to the wet heating system. A standard bathroom electric UFH mat costs £150 to £400 for the mat, plus £200 to £400 for installation and a programmable thermostat. Wet UFH systems cost more to install but are cheaper to run long-term.",
      },
      {
        type: "h3",
        content: "Joinery, boarding, and finishing",
      },
      {
        type: "p",
        content: "A bathroom renovation typically involves boarding out walls with moisture-resistant plasterboard before tiling, constructing boxing around pipework, and fitting any bespoke joinery such as vanity units or storage. These elements together typically cost £500 to £1,500 in a standard bathroom. Bespoke fitted joinery, custom vanity units, integrated storage, or freestanding furniture, adds significantly more.",
      },
      {
        type: "h2",
        content: "What bathroom renovation costs are commonly missed?",
      },
      {
        type: "ul",
        content: [
          "Structural work if the floor joists need strengthening for a freestanding bath or heavy stone floor",
          "Ventilation upgrades, Building Regulations require adequate mechanical ventilation in bathrooms without openable windows",
          "Making good to adjacent rooms when chasing pipework through party walls or ceiling voids",
          "Leak testing, all plumbing should be pressure-tested before boarding. Allow time in the programme for this",
          "Decoration of the ceiling and any unpainted surfaces outside the tiled area",
          "Accessories, towel rails, toilet roll holders, mirrors, and heated bathroom cabinets are often specified late and add more than clients expect",
        ],
      },
      {
        type: "h2",
        content: "How to quote a bathroom renovation accurately",
      },
      {
        type: "p",
        content: "Bathroom renovations are a job type where scope creep is common and profit margins are easy to lose. The most common mistakes are: not allowing enough time for strip-out and making good; underestimating the cost of the chosen sanitaryware; and not visiting the existing bathroom before quoting.",
      },
      {
        type: "p",
        content: "Before quoting, always: inspect the existing plumbing condition and note whether any reconfiguration is proposed; confirm the sanitaryware specification in writing; agree the tile specification and get a supply quote if significant quantities are involved; and identify whether there is asbestos risk in the existing floor or ceiling construction.",
      },
      {
        type: "p",
        content: "For builders quoting multiple bathrooms or dealing with complex specifications, structured quoting software that covers all residential trades, plumbing, electrical, tiling, joinery, and decoration, can dramatically reduce the time spent on each quote while improving consistency and reducing the risk of missed items.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Generate a detailed bathroom renovation quote from a description or drawings in under 10 minutes.",
        heading: "Quote bathroom renovations in minutes, not evenings",
      },
    ],
    faqs: [
      {
        q: "How much does a bathroom renovation cost in the UK in 2026?",
        a: "A budget bathroom refit in the UK costs £3,500 to £6,500. A mid-range renovation with good quality sanitaryware and tiling runs £6,500 to £12,000. A high-specification renovation with premium fixtures, bespoke tiling, and a freestanding bath typically costs £12,000 to £20,000 or more. London and the South East carry a 20 to 30 percent labour premium on top of these figures.",
      },
      {
        q: "How long does a bathroom renovation take?",
        a: "A straightforward bathroom refit with no layout changes typically takes 5 to 10 working days from strip-out to completion. A more complex renovation involving plumbing reconfiguration, wet room tanking, large format tiling, and bespoke joinery can take 2 to 4 weeks. Programme the job carefully: the bathroom is usually out of use during the work, so clients need alternative arrangements.",
      },
      {
        q: "Does a bathroom renovation need building regulations approval?",
        a: "Bathroom renovations do not generally require building regulations approval if you are like-for-like replacing sanitaryware. However, electrical work in a bathroom must comply with Part P of the Building Regulations and should be carried out by a qualified electrician who can self-certify. If the bathroom is being added where none previously existed, or if the floor structure is being altered, a Building Regulations application may be required.",
      },
      {
        q: "What is the most expensive part of a bathroom renovation?",
        a: "For most bathroom renovations, the combined cost of sanitaryware and tiling is the largest spend. In a high-specification renovation, the sanitaryware, particularly freestanding baths, designer taps, and wall-hung WCs, can represent 40 to 50 percent of the total project cost. In budget renovations, tiling labour is often the largest single trade cost.",
      },
      {
        q: "How do I avoid going over budget on a bathroom renovation?",
        a: "Agree the full specification before work starts and get it in writing, changes mid-project are the number one cause of cost overruns. Confirm the tile specification and get a supply quote before finalising the price; tile costs vary from £15 to £200+ per square metre. Set a clear contingency of 10 to 15 percent for unknown conditions discovered once strip-out is complete.",
      },
    ],
    relatedPosts: [
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
      { slug: "quoting-software-for-plumbers-uk", title: "Quoting Software for Plumbers UK: Stop Losing Jobs to Slow Quotes" },
      { slug: "kitchen-renovation-cost-uk", title: "How Much Does a New Kitchen Cost in the UK? (2026 Guide)" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },

  {
    slug: "kitchen-renovation-cost-uk",
    title: "How Much Does a New Kitchen Cost in the UK? (2026 Guide)",
    metaTitle: "New Kitchen Cost UK 2026: Kitchen Renovation Cost Breakdown for Builders",
    metaDescription: "How much does a new kitchen cost in the UK? A full kitchen renovation cost breakdown by trade and specification level, written for builders pricing kitchen refit work.",
    publishedDate: "2026-01-19",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "9 min read",
    excerpt: "Kitchen renovations range from a simple unit and worktop replacement to a full structural reconfiguration with new plumbing, electrics, and bespoke joinery. This guide breaks down what a kitchen renovation really costs in the UK, and what builders need to know to price it accurately.",
    sections: [
      {
        type: "p",
        content: "Kitchen renovations are one of the most frequently commissioned home improvement projects in the UK, and one of the most variable in cost. A like-for-like kitchen unit replacement in the same footprint is a very different project from an open-plan kitchen that involves knocking through walls, installing structural steelwork, rerouting plumbing, and fitting a bespoke kitchen with high-end appliances. Both are kitchen renovations. The cost difference between them can be ten to one.",
      },
      {
        type: "p",
        content: "This guide covers what a kitchen renovation costs in the UK in 2026, broken down by specification level and trade, so builders can quote accurately and homeowners can plan their budget realistically.",
      },
      {
        type: "h2",
        content: "What drives the cost of a kitchen renovation?",
      },
      {
        type: "p",
        content: "The biggest single driver of kitchen renovation cost is the kitchen itself, the units, worktops, and appliances. A standard flat-pack kitchen from a large retailer supplied at £2,000 to £4,000 and a bespoke painted solid-wood kitchen supplied at £15,000 to £40,000 represent entirely different projects in both cost and installation complexity. Understanding the supply cost of the kitchen before quoting is essential.",
      },
      {
        type: "p",
        content: "Beyond the kitchen supply cost, the main cost variables are: whether structural work is involved (wall removals, new steel beams, RSJs); the extent of plumbing reconfiguration; the electrical specification including island sockets, extractor ducting, and appliance connections; the flooring type and area; and tiling specification for splashbacks and utility areas.",
      },
      {
        type: "h2",
        content: "Typical kitchen renovation costs in the UK (2026)",
      },
      {
        type: "p",
        content: "The following cost ranges cover the full kitchen renovation including all trades, builder's work, plumbing, electrics, and decoration, plus kitchen supply at the specification level stated. They exclude structural work such as wall removal or steel installation, which is priced separately.",
      },
      {
        type: "ul",
        content: [
          "Budget kitchen renovation, flat-pack units, laminate worktops, like-for-like layout: £5,000 to £9,000",
          "Mid-range renovation, rigid units, stone or composite worktops, minor layout changes: £10,000 to £20,000",
          "High-specification renovation, painted or bespoke units, quartz or granite worktops, quality appliances: £20,000 to £40,000",
          "Premium bespoke kitchen, handmade cabinetry, stone worktops, integrated appliances, full refit: £40,000 to £80,000+",
          "Structural wall removal and open-plan remodel (additional to the above): £6,000 to £20,000 depending on steelwork and extent",
          "London and South East: add 20 to 30 percent to the figures above for labour",
        ],
      },
      {
        type: "h2",
        content: "Cost breakdown by trade",
      },
      {
        type: "h3",
        content: "Strip-out and preparation",
      },
      {
        type: "p",
        content: "Stripping out an existing kitchen, removing units, worktops, appliances, flooring, and any wall finishes, typically costs £600 to £1,500 including skip hire. If the existing kitchen has asbestos in floor tiles (common in pre-1980s properties), budget for specialist removal at £500 to £2,000 depending on extent.",
      },
      {
        type: "h3",
        content: "Structural work and wall removal",
      },
      {
        type: "p",
        content: "Open-plan kitchen extensions and kitchen-diner conversions frequently involve removing one or more load-bearing walls. The cost of a structural wall removal including temporary support, steelwork, padstones, and making good typically runs from £3,000 to £8,000 for a standard span. Wider openings or more complex structural arrangements cost more. A structural engineer is required before specifying steelwork, budget £500 to £1,200 for the calculations.",
      },
      {
        type: "h3",
        content: "Kitchen supply and installation",
      },
      {
        type: "p",
        content: "Kitchen installation labour is separate from the supply cost of the units and is typically charged as a day rate or fixed price by a specialist kitchen fitter. Installation of a standard kitchen (12 to 16 units, single run or L-shape) typically takes 3 to 5 days for an experienced fitter. At UK kitchen fitter day rates of £250 to £400 per day, installation labour alone is £750 to £2,000. Complex kitchens with islands, tall units, and bespoke configurations take longer.",
      },
      {
        type: "h3",
        content: "Worktops",
      },
      {
        type: "p",
        content: "Worktop costs vary more than almost any other kitchen element. Laminate worktops start at £100 to £200 per linear metre installed. Solid wood runs from £200 to £400 per linear metre. Quartz or engineered stone is typically £350 to £700 per linear metre installed, including templating and cutting. Granite and natural stone carry a similar range. For a medium kitchen with 5 to 6 linear metres of worktop, the difference between laminate and stone can be £1,500 to £3,000+.",
      },
      {
        type: "h3",
        content: "Plumbing",
      },
      {
        type: "p",
        content: "Kitchen plumbing covers the sink connection, dishwasher and washing machine supplies, and any reconfiguration of the boiler or heating. A straightforward like-for-like plumbing installation for a standard kitchen typically costs £500 to £1,000 in labour. If the sink position is moving, the boiler is being relocated, or a water softener or filtration system is being installed, budget £1,200 to £2,500.",
      },
      {
        type: "h3",
        content: "Electrical work",
      },
      {
        type: "p",
        content: "Kitchen electrics are one of the more involved trade packages in a renovation. A typical kitchen requires: dedicated circuits for oven and hob; extraction fan or cooker hood wiring; under-unit lighting; island sockets (which may require floor coring); consumer unit review; and connection of appliances. Budget £1,000 to £2,500 for a standard kitchen electrical package. Open-plan kitchens with islands and more complex lighting schemes cost more.",
      },
      {
        type: "h3",
        content: "Tiling and splashbacks",
      },
      {
        type: "p",
        content: "Kitchen splashback tiling between worktop and wall units is typically £35 to £60 per square metre in labour. The tile specification drives material cost, a standard ceramic tile might cost £20 to £40 per square metre supplied, while a designer large format tile or handmade tile can cost £80 to £200+ per square metre. Full-height kitchen wall tiling for an open-plan kitchen can run to £1,500 to £4,000 in materials and labour depending on specification.",
      },
      {
        type: "h3",
        content: "Flooring",
      },
      {
        type: "p",
        content: "Kitchen flooring is commonly LVT, engineered wood, ceramic or porcelain tile, or polished concrete. LVT flooring in a medium kitchen is typically £800 to £1,500 for supply and installation. Porcelain tile at £50 to £100 per square metre supplied, plus £35 to £60 per square metre installation, is a common mid-spec choice. Underfloor heating beneath tile flooring adds £400 to £1,200 for a standard kitchen floor area.",
      },
      {
        type: "h3",
        content: "Decoration",
      },
      {
        type: "p",
        content: "A kitchen renovation typically involves at minimum painting the ceiling and any exposed wall areas not covered by units or tiles. A full decoration package for a medium kitchen (ceiling, exposed walls, new plaster to making-good areas) typically costs £600 to £1,500 for materials and labour.",
      },
      {
        type: "h2",
        content: "Kitchen renovation costs that builders often miss",
      },
      {
        type: "ul",
        content: [
          "Appliance integration, built-in ovens, induction hobs, and integrated fridge-freezers require precise unit dimensions and cutouts",
          "Extractor ducting, running ductwork from a ceiling-mounted or island extractor to an external outlet can require significant builder's work",
          "Making good after structural work, wall removal and steelwork leave significant making-good requirements to plasterwork, ceilings, and floors",
          "Decoration of the ceiling after recessed lighting and extractor installation",
          "Waste uplift for bulky kitchen carcasses, allow for multiple skip loads or a large builder's skip",
          "Commissioning and testing of all appliances and plumbing before handover",
        ],
      },
      {
        type: "h2",
        content: "How to price a kitchen renovation accurately",
      },
      {
        type: "p",
        content: "Kitchen renovations are vulnerable to scope creep and late specification changes. The best approach is to agree the full kitchen specification, supplier, range, worktop material, appliances, and layout, before finalising the construction quote. A client who changes from a flat-pack kitchen to a bespoke one mid-project will require a full recosting of the installation scope.",
      },
      {
        type: "p",
        content: "Always visit the existing kitchen before quoting. Check the condition of the existing plumbing and electrics, confirm whether the walls are load-bearing, identify the floor substrate, and assess access for delivery of units and appliances. Missed site conditions are the most common cause of margin loss on kitchen renovation projects.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Generate a detailed kitchen renovation quote covering all trades, plumbing, electrical, joinery, tiling, and decoration, in under 10 minutes.",
        heading: "Price kitchen renovations accurately, first time",
      },
    ],
    faqs: [
      {
        q: "How much does a new kitchen cost in the UK in 2026?",
        a: "A budget kitchen renovation in the UK costs £5,000 to £9,000 including all trades and a flat-pack kitchen supply. A mid-range renovation with rigid units and stone worktops typically runs £10,000 to £20,000. A high-specification or bespoke kitchen renovation ranges from £20,000 to £80,000 or more depending on the kitchen supply cost and the extent of structural and electrical work. London and the South East add 20 to 30 percent to these figures.",
      },
      {
        q: "What is not included in a kitchen renovation quote?",
        a: "Standard kitchen renovation quotes typically exclude: structural wall removal (quoted separately once a structural engineer has specified the steelwork); appliance supply where the client is purchasing directly; planning permission fees if the kitchen forms part of a larger extension; and any asbestos removal discovered during strip-out. Always list your exclusions clearly to avoid disputes.",
      },
      {
        q: "How long does a kitchen renovation take?",
        a: "A straightforward kitchen unit replacement in the same layout takes 1 to 2 weeks. A full kitchen renovation with minor layout changes, new tiling, and electrical work typically takes 2 to 4 weeks. An open-plan kitchen-diner that involves wall removal, steelwork, and extensive remodelling can take 4 to 8 weeks from start to completion.",
      },
      {
        q: "Do I need building regulations approval for a kitchen renovation?",
        a: "A straight kitchen refit does not normally require building regulations approval. However, any structural wall removal requires a structural engineer's calculations and a building regulations application. Electrical work must comply with Part P of the Building Regulations and should be carried out by a qualified electrician who can self-certify the installation.",
      },
      {
        q: "How much profit should a builder make on a kitchen renovation?",
        a: "Most UK builders target a net profit margin of 10 to 20 percent on kitchen renovation work, applied on top of a full cost build-up including all direct costs and overhead allocation. Builders managing multiple subcontractors should also add a management margin of 10 to 15 percent on top of subcontractor costs to cover coordination and liability.",
      },
    ],
    relatedPosts: [
      { slug: "bathroom-renovation-cost-uk", title: "How Much Does a Bathroom Renovation Cost in the UK? (2026 Guide)" },
      { slug: "kitchen-extension-cost-uk", title: "Kitchen Extension Cost UK: What Builders and Homeowners Should Expect (2026)" },
      { slug: "rear-extension-cost-uk", title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)" },
    ],
  },

  {
    slug: "kitchen-extension-cost-uk",
    title: "Kitchen Extension Cost UK: What Builders and Homeowners Should Expect (2026)",
    metaTitle: "Kitchen Extension Cost UK 2026: Builder's Pricing Guide",
    metaDescription: "How much does a kitchen extension cost in the UK? A complete cost breakdown covering structural work, glazing, kitchen installation, and all associated trades for 2026.",
    publishedDate: "2026-02-23",
    modifiedDate: "2026-06-14",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },    category: "Guides",
    readTime: "9 min read",
    excerpt: "A kitchen extension is one of the most transformative home improvement projects, and one of the most complex to price. It combines structural building work, a kitchen fit-out, and often bi-fold or sliding doors and open-plan integration. This guide breaks down exactly what a kitchen extension costs in the UK.",
    sections: [
      {
        type: "p",
        content: "Kitchen extensions are consistently among the most popular home improvement projects in the UK. A rear extension that opens up and enlarges the kitchen, often creating an open-plan kitchen-diner with glazed doors onto the garden, adds substantial living space and frequently represents the single largest investment a homeowner makes in their property. For builders, these are complex jobs to price: they combine structural building work, a full kitchen fit-out, glazing, and multiple trade packages running in sequence.",
      },
      {
        type: "p",
        content: "This guide covers the full cost breakdown of a UK kitchen extension in 2026, including the structure, the kitchen installation, and every trade in between.",
      },
      {
        type: "h2",
        content: "What is a kitchen extension?",
      },
      {
        type: "p",
        content: "A kitchen extension typically involves building a rear addition to an existing property to create a larger kitchen, often combined with an open-plan dining or living area. The structural work includes foundations, external walls, roofing, and bi-fold or sliding doors onto the garden. The existing rear wall of the house is then opened up with a structural steel beam to create a seamless connection between the original house and the new extension.",
      },
      {
        type: "p",
        content: "The kitchen is then installed in the enlarged space, often as part of a complete open-plan remodel that also involves reflooring, replastering, electrical redesign, and underfloor heating. A kitchen extension is effectively two projects in one: a building project and a kitchen renovation project, with the combined cost of both.",
      },
      {
        type: "h2",
        content: "Typical kitchen extension costs in the UK (2026)",
      },
      {
        type: "p",
        content: "Kitchen extension costs in the UK depend on the size of the extension, the specification of the kitchen and glazing, and the location. As a guide, total project costs (structure plus kitchen fit-out) currently fall in these ranges:",
      },
      {
        type: "ul",
        content: [
          "Small kitchen extension (up to 15m²) with mid-range kitchen: £35,000 to £55,000",
          "Medium kitchen extension (15–25m²) with mid-range kitchen: £55,000 to £85,000",
          "Large kitchen extension (25–40m²) with premium kitchen and full glazing: £80,000 to £130,000+",
          "Open-plan kitchen-diner extension with bi-fold doors and bespoke kitchen: £90,000 to £160,000+",
          "London and South East: add 20 to 30 percent to the figures above",
        ],
      },
      {
        type: "p",
        content: "These are total project costs. Separating the structural element from the kitchen fit-out: the structure of a medium single storey extension typically costs £40,000 to £65,000 to shell stage. The kitchen installation and associated internal fit-out adds a further £15,000 to £50,000+ depending on kitchen specification.",
      },
      {
        type: "h2",
        content: "Cost breakdown by element",
      },
      {
        type: "h3",
        content: "Structure: foundations and external walls",
      },
      {
        type: "p",
        content: "The structural shell of a kitchen extension covers groundworks, foundations, external brickwork or timber frame walls, and roof. Foundations alone can cost £4,000 to £12,000 depending on ground conditions and structural specification. External cavity brickwork runs £90 to £140 per square metre of wall face. This structural shell cost is largely the same whether the extension is for a kitchen, a playroom, or any other use.",
      },
      {
        type: "h3",
        content: "Roofing",
      },
      {
        type: "p",
        content: "Kitchen extensions commonly use either a flat roof, typically a warm deck GRP fibreglass system at £80 to £130 per square metre, or a glazed or lantern roof to maximise natural light. A structural glazed roof or roof lantern adds significantly to cost: a mid-range roof lantern typically runs £2,500 to £5,000 supplied and installed, while a full structural glazed roof system can cost £8,000 to £20,000+ depending on area and specification.",
      },
      {
        type: "h3",
        content: "Opening up the existing rear wall",
      },
      {
        type: "p",
        content: "Creating the connection between the existing house and the new extension requires removing the existing rear wall and installing a structural steel beam. Costs typically range from £3,500 to £8,000 for the steel, temporary support, padstones, masonry making good, and structural engineer's fees. Wider openings with larger or compound beam arrangements cost more.",
      },
      {
        type: "h3",
        content: "Bi-fold doors, sliding doors, and glazing",
      },
      {
        type: "p",
        content: "Aluminium bi-fold doors are the most popular glazing choice for a kitchen extension. A standard three or four-panel bi-fold door set in aluminium typically costs £3,500 to £6,000 supplied and fitted for a 3m opening. A wider four or five-panel set for a 4m to 5m opening runs £5,000 to £9,000. Sliding door systems are an alternative at similar price points. Premium systems with thermally broken frames and triple glazing cost more.",
      },
      {
        type: "h3",
        content: "Kitchen supply and installation",
      },
      {
        type: "p",
        content: "The kitchen itself is the most specification-sensitive element of the project. A mid-range rigid kitchen with composite stone worktops, integrated appliances, and standard hardware might cost £8,000 to £18,000 supplied. A high-specification painted or bespoke kitchen with quartz worktops and quality appliances typically runs £20,000 to £60,000 or more. Installation is a further £1,500 to £3,500 for a standard kitchen. Confirm the kitchen specification in writing before pricing, specification changes after contract are a major source of disputes and cost overruns.",
      },
      {
        type: "h3",
        content: "Plumbing and heating",
      },
      {
        type: "p",
        content: "A kitchen extension typically requires: relocation or extension of the kitchen sink and appliance plumbing; heating extension or underfloor heating throughout the new space; and any boiler capacity assessment if the existing system is marginal. Wet underfloor heating for a medium extension floor area (15 to 25 square metres) typically costs £2,500 to £5,000 for supply and installation. Electric UFH is a lower-cost alternative at £600 to £1,500 for the same area.",
      },
      {
        type: "h3",
        content: "Electrical work",
      },
      {
        type: "p",
        content: "A kitchen extension electrical package is more involved than a standard kitchen refit because it covers both the extension structure and the enlarged kitchen. Expect: new consumer unit circuits for oven, hob, and extraction; island or peninsular sockets; lighting design covering both the extension and the existing kitchen; and integration with any smart lighting or home automation system. Budget £2,500 to £5,000 for a comprehensive electrical package.",
      },
      {
        type: "h3",
        content: "Flooring",
      },
      {
        type: "p",
        content: "Open-plan kitchen extensions are typically floored throughout in a single material for visual continuity. Large format porcelain tile is the most popular choice, typically costing £40 to £90 per square metre for the tile plus £35 to £60 per square metre to lay. Engineered wood or LVT is a warmer alternative at £25 to £70 per square metre for the material plus £15 to £30 per square metre to install. For a medium open-plan extension, total flooring costs, material and installation, commonly run £3,000 to £8,000.",
      },
      {
        type: "h3",
        content: "Plastering, decoration, and finishing",
      },
      {
        type: "p",
        content: "A kitchen extension requires plastering throughout the new structure plus making good to the existing house where the rear wall has been opened up. A medium extension plastering package typically costs £2,500 to £5,000. Decoration, ceiling, walls, woodwork, adds a further £1,500 to £3,500 depending on the area and finish specification.",
      },
      {
        type: "h2",
        content: "Costs that are frequently underestimated on kitchen extensions",
      },
      {
        type: "ul",
        content: [
          "Scaffold: a rear-elevation scaffold for a single storey extension typically costs £1,500 to £3,000",
          "Structural engineer fees: most kitchen extensions require a structural engineer report. Budget £500 to £1,500",
          "Building regulations fees: check the local authority schedule. Typical residential extension fees are £200 to £800",
          "Party wall surveyor fees if the extension abuts a shared boundary: £700 to £2,500 per adjacent property",
          "Skip and waste disposal: a kitchen strip-out plus construction waste requires multiple skips. Allow £800 to £2,000",
          "Appliances: built-in ovens, induction hobs, integrated fridge-freezers, and dishwashers, confirm who is supplying these and include them explicitly or exclude them clearly",
        ],
      },
      {
        type: "h2",
        content: "How to produce an accurate kitchen extension quote",
      },
      {
        type: "p",
        content: "A kitchen extension quote requires a site visit before pricing. You need to confirm: the proposed extension footprint and any planning constraints; whether the existing rear wall is load-bearing and the structural specification; the proposed glazing type and size; and the kitchen specification. Without the kitchen specification confirmed, your quote should include an allowance clearly described as such, with an instruction to the client to confirm their kitchen before the figure is finalised.",
      },
      {
        type: "p",
        content: "The kitchen element of the project is best treated as a separate cost line in your quote, with the structural shell and trade works itemised independently. This makes it easier to adjust the kitchen allowance when the specification is confirmed, and gives the client a clear view of where their money is going.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload your drawings or describe the project and generate a complete kitchen extension quote, structure, fit-out, and all trades, in under 10 minutes.",
        heading: "Quote kitchen extensions with confidence",
      },
    ],
    faqs: [
      {
        q: "How much does a kitchen extension cost in the UK in 2026?",
        a: "A small kitchen extension (up to 15m²) with a mid-range kitchen typically costs £35,000 to £55,000 in total. A medium extension (15–25m²) runs £55,000 to £85,000. A large or premium open-plan kitchen extension with bi-fold doors and a high-specification kitchen can cost £100,000 to £160,000 or more. London and the South East add 20 to 30 percent to these figures.",
      },
      {
        q: "Do I need planning permission for a kitchen extension?",
        a: "Most single storey rear kitchen extensions in the UK qualify as Permitted Development, meaning no planning permission is required. Permitted Development rights allow a single storey rear extension of up to 3m (for a semi-detached) or 4m (for a detached house) without planning, up to a maximum height of 4m. Extensions beyond these limits, in conservation areas, or on listed buildings require a planning application. Always confirm the PD position with the local planning authority or a planning consultant before work starts.",
      },
      {
        q: "How long does a kitchen extension take to build?",
        a: "A standard single storey kitchen extension typically takes 14 to 20 weeks from groundworks to final decoration and kitchen installation. The programme depends heavily on the structural specification, the glazing lead time (bi-fold doors from premium suppliers often have 6 to 10 week lead times), and the kitchen supply lead time. Plan the programme early and confirm supplier lead times before setting a start date with your client.",
      },
      {
        q: "What is the difference between a kitchen extension and a rear extension?",
        a: "There is no structural difference, a kitchen extension is a rear extension (or sometimes a side return extension) that is used to enlarge and remodel the kitchen. The term 'kitchen extension' is commonly used when the primary purpose is to create a larger kitchen and open-plan living space. The construction cost of the shell is the same regardless of what the new space is used for; the additional cost comes from the kitchen fit-out itself.",
      },
      {
        q: "Can I include a utility room in a kitchen extension quote?",
        a: "Yes, and it is a common addition. Many kitchen extension briefs include a utility room or boot room alongside the enlarged kitchen. Adding a utility room requires an additional plumbing connection, a door, and possibly a window, but the structural cost per square metre is essentially the same. Adding it to the quote is straightforward once the overall footprint is confirmed.",
      },
    ],
    relatedPosts: [
      { slug: "rear-extension-cost-uk", title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)" },
      { slug: "kitchen-renovation-cost-uk", title: "How Much Does a New Kitchen Cost in the UK? (2026 Guide)" },
      { slug: "single-storey-extension-cost-uk", title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026" },
    ],
    relatedGuides: [
      { href: "/guides/running-a-building-business-uk", title: "Running a Building Business in the UK: The Complete Guide" },
    ],
  },

  {
    slug: "garage-conversion-cost-uk",
    title: "How Much Does a Garage Conversion Cost in the UK? (2026 Guide)",
    metaTitle: "Garage Conversion Cost UK 2026: Full Price Breakdown",
    metaDescription: "How much does a garage conversion cost in the UK? Full 2026 price breakdown for integral, attached and detached garages - including building regs, insulation, electrics and finishing.",
    publishedDate: "2026-06-15",
    modifiedDate: "2026-06-15",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },
    category: "Cost Guides",
    readTime: "10 min read",
    excerpt: "Converting a garage into a usable room is one of the most cost-effective ways to add living space in a UK home - but costs vary widely depending on the garage type, specification and location. Here is a complete breakdown of what to expect in 2026.",
    sections: [
      {
        type: "p",
        content: "A garage conversion turns an underused or redundant garage into habitable living space - a home office, bedroom, playroom, gym or utility room. It is one of the most cost-effective home improvements available in the UK because the structure already exists: the walls, roof and floor slab are in place, and no planning permission is usually required.",
      },
      {
        type: "p",
        content: "That said, costs vary significantly depending on whether the garage is integral (built into the house), attached (adjacent with a shared wall), or detached (a separate outbuilding), and on the specification of the internal fit-out. This guide breaks down the typical cost of a garage conversion in the UK in 2026.",
      },
      {
        type: "h2",
        content: "Typical garage conversion costs UK 2026",
      },
      {
        type: "p",
        content: "The table below gives a typical cost range for the three main garage types. All figures are for a single-car garage of approximately 15 to 20 square metres, with a mid-range internal specification.",
      },
      {
        type: "ul",
        content: [
          "Integral garage conversion (built into the house): £8,000 to £15,000",
          "Attached garage conversion (separate structure, shared wall): £10,000 to £18,000",
          "Detached garage conversion (standalone outbuilding): £15,000 to £30,000",
          "Double garage conversion: add 50 to 80 percent to the above figures",
        ],
      },
      {
        type: "p",
        content: "London and South East England add approximately 20 to 30 percent to these figures. A premium specification with underfloor heating, en-suite facilities or bespoke joinery will push costs towards the top of the range or beyond.",
      },
      {
        type: "h2",
        content: "What is included in a garage conversion?",
      },
      {
        type: "p",
        content: "A full garage conversion includes several distinct packages of work. Understanding each one helps you check that nothing is missing from a quote and gives you a basis for comparing builders fairly.",
      },
      {
        type: "h3",
        content: "Insulation",
      },
      {
        type: "p",
        content: "Garages are not built to habitable building standards. Before the space can be used as a room, the walls, floor and roof must be insulated to satisfy current Building Regulations (Part L). For a single-car garage this typically involves: rigid insulation board to the floor slab (40–100mm, depending on the floor build-up); cavity or solid wall insulation or dry-lining the walls with insulated plasterboard; and insulation to the roof structure or underside of the roof covering. Insulation is one of the largest cost elements, typically running £1,500 to £4,000 for a standard single garage depending on the specification.",
      },
      {
        type: "h3",
        content: "Garage door removal and new front wall",
      },
      {
        type: "p",
        content: "The existing garage door opening must be infilled to create a solid wall. The most common approach is to build a new section of brickwork or blockwork matching the existing elevations, with a window and, optionally, a new door. This is usually the most visible element of a garage conversion externally. Cost for the infill wall, window and making good: £1,500 to £4,000 depending on the size of the opening and the window specification.",
      },
      {
        type: "h3",
        content: "Electrical first and second fix",
      },
      {
        type: "p",
        content: "A habitable room needs a proper electrical installation. This typically includes new consumer unit circuits or an extension from the existing house consumer unit, power sockets, lighting points, and any specialist wiring for heating, data or security. For a standard garage conversion, electrical first and second fix costs £800 to £2,000.",
      },
      {
        type: "h3",
        content: "Plumbing and heating",
      },
      {
        type: "p",
        content: "If the conversion is to include a WC, en-suite, kitchenette or utility room, plumbing costs rise significantly. Even for a basic room, you will need to connect a heating circuit. Options include extending the existing central heating system (adding a radiator from the nearest pipework), underfloor heating (more disruptive but comfortable underfoot), or an electric panel heater as a standalone solution. Connecting to the existing central heating typically costs £500 to £1,500. Underfloor heating across a 15–20m² slab adds £1,000 to £2,500. Adding a WC and basin adds £1,500 to £3,500.",
      },
      {
        type: "h3",
        content: "Plastering and dry-lining",
      },
      {
        type: "p",
        content: "Once the insulation and services are in, the walls and ceiling need to be boarded and skimmed. Most garage conversions use insulated dry-lining board (dot-and-dab or on a frame) rather than wet plaster to the existing masonry. A full skim coat finish to a single garage typically costs £800 to £1,800.",
      },
      {
        type: "h3",
        content: "Floor screed or overlay",
      },
      {
        type: "p",
        content: "Once insulation is laid to the floor slab, a screed or raised floor deck is needed to provide a level, flat surface suitable for the final floor finish. A sand and cement screed to a single garage costs £500 to £1,200. A raised timber floor deck is an alternative where headroom allows. The final floor finish (tile, LVT, carpet) is additional.",
      },
      {
        type: "h3",
        content: "Decoration and finishing",
      },
      {
        type: "p",
        content: "Decoration includes painting, skirting boards, door linings, and making good the opening between the garage and the existing house. Budget £700 to £1,500 for a standard single garage room.",
      },
      {
        type: "h2",
        content: "Costs that are frequently missed on garage conversion quotes",
      },
      {
        type: "ul",
        content: [
          "Building regulations application fee: typically £200 to £500 for a local authority full plans application",
          "Structural engineer report: required if removing or modifying any structural element, or if the floor slab needs assessment - typically £300 to £700",
          "Party wall notice: required if the garage shares a wall with a neighbouring property - allow £700 to £1,500 per neighbour if a formal award is needed",
          "Skip hire and waste disposal: removing the old garage door, insulation packaging, and construction debris typically requires two skips - allow £500 to £900",
          "Damp-proof membrane and DPC upgrade: older garages often lack a proper DPM; upgrading it adds £300 to £700",
          "Lintel to the infill wall if the garage opening is wide: a structural lintel over a large opening can add £200 to £500",
        ],
      },
      {
        type: "h2",
        content: "Detached garage conversions cost more - here is why",
      },
      {
        type: "p",
        content: "Converting a detached garage is significantly more complex than an integral or attached conversion. The main reasons are: the building is not connected to the house for services, so all electrical, plumbing and heating connections require new buried runs across the garden; the roof may need more extensive works to achieve the insulation values required for habitable use; and external works (path, drainage connections, making good) are proportionally more significant.",
      },
      {
        type: "p",
        content: "For a detached garage, also consider whether the conversion requires planning permission. Detached garden buildings converted to habitable use can require full planning permission rather than falling under Permitted Development, particularly if the building is in the garden curtilage and is to be used as a separate dwelling. Check with your local planning authority or a planning consultant before proceeding.",
      },
      {
        type: "h2",
        content: "Does a garage conversion add value?",
      },
      {
        type: "p",
        content: "A well-executed garage conversion typically adds between 5 and 10 percent to a property's market value in the UK, equating to £15,000 to £40,000 on an average-priced home. The value uplift is strongest in areas where parking is plentiful (so losing the garage matters less) and where the converted space significantly improves the room count. A conversion that takes a three-bedroom house to four bedrooms often represents the best return.",
      },
      {
        type: "p",
        content: "The conversion must comply with Building Regulations and have a completion certificate from building control to be recognised during a sale. A conversion without sign-off can create problems during conveyancing and may affect the property's value or saleability.",
      },
      {
        type: "h2",
        content: "How to get an accurate garage conversion quote",
      },
      {
        type: "p",
        content: "For builders, garage conversions are often underquoted because certain elements - insulation specification, the extent of the infill wall, the heating connection route - are not confirmed at the time of quoting. A reliable quote requires a site visit to confirm: the existing floor slab condition; the route for services connections; the proposed use (which drives the plumbing and heating spec); the insulation approach; and the internal finish specification.",
      },
      {
        type: "p",
        content: "Homeowners should be wary of very low quotes that do not include building regulations fees, structural engineer input or a heating solution. These are not optional extras - they are requirements for any properly completed habitable-space conversion.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload drawings or describe the garage conversion and generate a complete, itemised quote in under 10 minutes.",
        heading: "Quote garage conversions accurately",
      },
    ],
    faqs: [
      {
        q: "How much does a garage conversion cost in the UK in 2026?",
        a: "A standard single integral garage conversion with a mid-range specification typically costs £8,000 to £15,000. An attached garage runs £10,000 to £18,000. A detached garage conversion is more expensive at £15,000 to £30,000 due to the additional services connections and roofing works required. London and the South East add 20 to 30 percent.",
      },
      {
        q: "Do I need planning permission for a garage conversion?",
        a: "Most integral and attached garage conversions in England fall under Permitted Development and do not need planning permission, as long as the external appearance is not significantly altered. Detached garage conversions used as habitable rooms may require full planning permission, particularly in conservation areas or where the building is in a rear garden. Always confirm with your local planning authority before work starts.",
      },
      {
        q: "Do I need building regulations approval for a garage conversion?",
        a: "Yes. Building regulations approval is required for all garage conversions in England and Wales. The conversion must demonstrate compliance with Part L (thermal performance), Part P (electrical safety), Part B (fire safety), and Part F (ventilation). You will need a completion certificate from building control at the end of the project - this is essential for resale.",
      },
      {
        q: "How long does a garage conversion take?",
        a: "A straightforward integral garage conversion typically takes 3 to 6 weeks on site. A detached garage conversion with more complex services connections takes 5 to 10 weeks. The building regulations process runs alongside and does not usually add to the programme if building control inspections are booked in good time.",
      },
      {
        q: "Can I convert a garage into a bedroom?",
        a: "Yes, and this is one of the most popular uses. Converting a garage into a bedroom adds to the property's room count, which typically increases its value. If the bedroom is to have an en-suite, allow for additional plumbing costs. Confirm with your local planning authority whether a change of use notification is required - in most cases, converting a garage within the residential curtilage does not require a formal change of use application.",
      },
    ],
    relatedPosts: [
      { slug: "rear-extension-cost-uk", title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)" },
      { slug: "single-storey-extension-cost-uk", title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026" },
      { slug: "loft-conversion-cost-breakdown-uk", title: "Loft Conversion Cost Breakdown UK: What Are You Actually Paying For?" },
    ],
    relatedGuides: [
      { href: "/guides/running-a-building-business-uk", title: "Running a Building Business in the UK: The Complete Guide" },
    ],
  },

  {
    slug: "house-extension-planning-uk",
    title: "How to Plan a House Extension in the UK: A Complete Step-by-Step Guide",
    metaTitle: "How to Plan a House Extension UK (2026): Step-by-Step Guide",
    metaDescription: "Planning a house extension in the UK? This step-by-step guide covers planning permission, building regulations, finding a builder, managing costs, and what to expect at every stage.",
    publishedDate: "2026-06-15",
    modifiedDate: "2026-06-15",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },
    category: "Guides",
    readTime: "12 min read",
    excerpt: "Planning a house extension involves far more than choosing a design. From permitted development rules to finding the right builder and managing the build, this step-by-step guide walks you through the entire process in the UK.",
    sections: [
      {
        type: "p",
        content: "A house extension is one of the most significant home improvement projects a homeowner can undertake. Done well, it adds space, value and quality of life. Done poorly - or without proper planning - it can be expensive, stressful and legally complicated. The good news is that with the right approach, the process is manageable. This guide covers every step from initial idea through to completed build.",
      },
      {
        type: "h2",
        content: "Step 1: Define what you want and why",
      },
      {
        type: "p",
        content: "Before contacting an architect or builder, spend time clarifying what you actually need from the extension. The most common drivers are: a larger kitchen or open-plan living space; an additional bedroom; a home office or utility room; or a combination of these across a larger project.",
      },
      {
        type: "p",
        content: "Think about how you use your home now and how you want to use the new space. This informs the design brief and helps you avoid the common mistake of building a large extension that does not quite solve the original problem because the layout was not thought through carefully enough.",
      },
      {
        type: "p",
        content: "At this stage, also be realistic about budget. Extensions typically cost from £1,500 to £2,500 per square metre for a standard single storey build, and considerably more for a double storey extension or a premium specification. Having a budget in mind before you approach professionals means you do not spend money on designs that are beyond what you can afford to build.",
      },
      {
        type: "h2",
        content: "Step 2: Understand permitted development and planning permission",
      },
      {
        type: "p",
        content: "One of the first practical questions on any UK extension is whether you need planning permission. In England, Permitted Development rights allow many common extensions without a planning application, subject to size and location limits.",
      },
      {
        type: "h3",
        content: "What falls under Permitted Development in England",
      },
      {
        type: "ul",
        content: [
          "Single storey rear extension: up to 3m (semi-detached or terraced) or 4m (detached) beyond the original rear wall, maximum 4m high",
          "Under the Larger Home Extension Scheme: up to 6m (semi or terraced) or 8m (detached) rear extension, subject to neighbour notification",
          "Side extension: single storey, maximum 4m high, width no more than half the original house width",
          "Double storey rear extension: up to 3m beyond the original rear wall, more than 7m from the rear boundary, maximum height of 4m",
        ],
      },
      {
        type: "p",
        content: "Permitted Development does not apply to: flats or maisonettes; listed buildings; homes in conservation areas, Areas of Outstanding Natural Beauty, or National Parks where additional restrictions apply; or any extension that changes the overall height beyond the permitted limits. If your home has had previous extensions, the combined footprint of all extensions counts toward the permitted limits, not just the new one.",
      },
      {
        type: "p",
        content: "If you are unsure whether your project falls within Permitted Development, apply to the local planning authority for a Lawful Development Certificate (LDC). This costs around £100 to £200 and gives you written confirmation that no planning permission is required - which is useful when you sell the property.",
      },
      {
        type: "h3",
        content: "When a planning application is required",
      },
      {
        type: "p",
        content: "If your extension exceeds the Permitted Development limits, is in a restricted area, or involves changes to the roof that exceed permitted limits, you will need to submit a householder planning application. In England this currently costs £258. Processing typically takes 8 weeks, though pre-application advice from the planning department (usually £50 to £150) can significantly improve the chances of approval first time.",
      },
      {
        type: "h2",
        content: "Step 3: Appoint an architect or designer",
      },
      {
        type: "p",
        content: "For anything more than a simple single storey rear extension, appointing an architect or architectural designer adds significant value. A good designer will: produce accurate drawings for planning and building regulations; advise on the best layout for the space; help you understand what is structurally possible; and produce the documents that contractors need to quote accurately.",
      },
      {
        type: "p",
        content: "Architectural fees for a residential extension typically range from 5 to 12 percent of the construction cost. For a £60,000 extension, expect architect fees of £3,000 to £7,000 covering design, planning drawings and building regulations drawings. Some architects also offer a contract administration service during the build; this adds cost but provides independent oversight of quality and programme.",
      },
      {
        type: "p",
        content: "Ask to see examples of similar projects. Check that they have experience with residential extensions and local planning applications in your area. A designer who knows your local planning authority's preferences can save you significant time and money.",
      },
      {
        type: "h2",
        content: "Step 4: Engage a structural engineer",
      },
      {
        type: "p",
        content: "Most house extensions require a structural engineer for at least some elements. Common requirements include: the steel beam specification for opening up the rear wall; the foundation design, particularly if the extension is in an area with clay soils prone to shrinkage and heave; padstone and bearing details; and any modifications to the existing structure.",
      },
      {
        type: "p",
        content: "Structural engineer fees for a residential extension typically run £500 to £2,000 depending on the complexity of the structure. Some builders prefer to use their own contacts for structural engineering; others ask the homeowner to commission an SE independently. Either way, having a structural engineer involved before work starts prevents costly errors on site.",
      },
      {
        type: "h2",
        content: "Step 5: Get the building regulations sorted",
      },
      {
        type: "p",
        content: "Building regulations approval is legally required for all house extensions in the UK, regardless of whether planning permission is needed. Building regulations cover: structural safety (Part A); fire safety (Part B); sound insulation (Part E, particularly for party walls); energy efficiency (Part L); ventilation (Part F); drainage (Part H); and electrical safety (Part P).",
      },
      {
        type: "p",
        content: "You have two routes: submit a Full Plans application to the local authority building control before work starts (the safest option - they check your drawings and flag issues before you build), or use a Building Notice, which allows work to start quickly but means inspections happen in arrears. For complex or large extensions, Full Plans is strongly recommended.",
      },
      {
        type: "p",
        content: "Building regulations fees for a typical single storey extension are £300 to £800. A double storey extension or more complex structural project may cost £600 to £1,200. You can also use a government-approved private building inspector instead of the local authority; costs are broadly similar.",
      },
      {
        type: "h2",
        content: "Step 6: Get multiple quotes from builders",
      },
      {
        type: "p",
        content: "Once you have approved drawings and a structural engineer's specification, you can go to tender. Aim for three detailed quotes from builders who have reviewed the same drawings and specification - this is the only way to compare quotes on a like-for-like basis.",
      },
      {
        type: "p",
        content: "Be wary of quotes that arrive without a breakdown. A good quote for an extension includes: site setup and clearance; groundworks and foundations; external walls (blockwork, brickwork or timber frame); structural steelwork; roofing; external joinery (windows and doors); internal first fix (electrical, plumbing, heating); plastering; second fix (electrical, plumbing, heating); flooring; decoration; and any site-specific items such as scaffolding, skips and party wall works.",
      },
      {
        type: "p",
        content: "When comparing quotes, check what is explicitly included and excluded. A £10,000 difference in quotes often comes down to what one builder has left out, not a genuine difference in pricing. Ask each builder to confirm in writing what is included in their quote.",
      },
      {
        type: "h2",
        content: "Step 7: Appoint your builder and agree a contract",
      },
      {
        type: "p",
        content: "Once you have selected your preferred builder, use a written contract. For residential extensions, the JCT Minor Works Building Contract is the standard option; it covers programme, payment, variations, and dispute resolution. A contract that matches the scope of your works protects both you and the builder if something unexpected arises.",
      },
      {
        type: "p",
        content: "Agree a payment schedule tied to defined stages of the work: groundworks complete; structure to wall plate; roof on and watertight; first fix complete; plastering complete; second fix and decoration complete. Never pay large sums in advance. A builder requesting more than 10 to 15 percent upfront is a red flag. Retention of 2.5 to 5 percent held until a snagging period has elapsed is standard and fair to both parties.",
      },
      {
        type: "h2",
        content: "Step 8: Manage the build",
      },
      {
        type: "p",
        content: "Even with a good builder, active management during the build is essential. Visit the site regularly, ideally at the key milestone stages that correspond to your payment schedule. Keep a site diary with dates, progress notes and photos. Record any agreed variations in writing - do not rely on verbal agreements about changes to the scope.",
      },
      {
        type: "p",
        content: "Ensure building control inspections are booked at the right stages. Key inspection points typically include: foundations before concrete is poured; drainage before backfilling; structural frame and steelwork; roof structure; and a final completion inspection. Missing these inspections can cause problems when you apply for the completion certificate.",
      },
      {
        type: "h2",
        content: "Step 9: Snagging and completion certificate",
      },
      {
        type: "p",
        content: "When the builder declares practical completion, carry out a thorough snagging inspection - ideally with your architect if you have one - before releasing retention. Document every item that needs attention. A reasonable snagging list is normal on any construction project; an extensive one is not.",
      },
      {
        type: "p",
        content: "Once building control is satisfied that the work complies with the regulations, they issue a completion certificate. Keep this document in a safe place: it is required when you sell the property and provides evidence that the extension was built to a legal standard.",
      },
      {
        type: "h2",
        content: "How long does planning and building a house extension take?",
      },
      {
        type: "ul",
        content: [
          "Design and planning drawings: 4 to 8 weeks",
          "Planning application (if required): 8 to 13 weeks",
          "Structural engineer and building regulations drawings: 2 to 6 weeks",
          "Tendering and builder selection: 3 to 6 weeks",
          "Single storey extension on site: 12 to 20 weeks",
          "Double storey extension on site: 16 to 28 weeks",
          "Total from initial brief to move-in: typically 9 to 18 months for a planning application project",
        ],
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. If you are a builder, upload the extension drawings and generate a complete, itemised quote in under 10 minutes.",
        heading: "Quoting the extension? Make it fast and accurate",
      },
    ],
    faqs: [
      {
        q: "Do I need planning permission for a house extension in the UK?",
        a: "Not always. In England, many standard rear and side extensions fall under Permitted Development and do not require a planning application. The key limits are 3m (semi or terraced) or 4m (detached) for a single storey rear extension. Extensions beyond these limits, in conservation areas, or on listed buildings need a planning application. Always check with your local planning authority or apply for a Lawful Development Certificate to confirm the position in writing.",
      },
      {
        q: "How much does it cost to plan a house extension?",
        a: "The professional fees for planning a house extension - architect, structural engineer, planning application, and building regulations - typically run £4,000 to £10,000 for a standard single storey extension. This covers design, planning drawings, structural calculations, planning fees (£258 in England) and building regulations fees (£300 to £800). For a double storey or more complex project, fees can run £8,000 to £18,000.",
      },
      {
        q: "How long does it take to get planning permission for an extension?",
        a: "A householder planning application in England typically takes 8 weeks from valid submission to decision. Pre-application advice (usually 3 to 4 weeks) and revisions if the first scheme is refused can extend this. If your extension falls under Permitted Development, you can proceed without planning permission - but a Lawful Development Certificate (2 to 4 weeks) provides written confirmation.",
      },
      {
        q: "What is the Larger Home Extension Scheme?",
        a: "The Larger Home Extension Scheme (also called the prior approval process) allows single storey rear extensions of up to 6m (semi or terraced) or 8m (detached) without a full planning application, subject to a neighbour notification period of 42 days. The local authority can refuse prior approval if neighbouring properties raise valid objections. The scheme requires a formal application to the council and a fee, but is faster and less prescriptive than a full planning application.",
      },
      {
        q: "Do I need building regulations for a house extension?",
        a: "Yes. Building regulations approval is required for all house extensions in England and Wales, regardless of whether planning permission is needed. Building regulations cover structural safety, fire escape, energy efficiency, ventilation, drainage, and electrical installation. You must obtain a completion certificate from building control once the work is finished - this is needed when you sell the property.",
      },
    ],
    relatedPosts: [
      { slug: "rear-extension-cost-uk", title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)" },
      { slug: "single-storey-extension-cost-uk", title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026" },
      { slug: "double-storey-extension-cost-uk", title: "How Much Does a Double Storey Extension Cost in the UK? (2026 Guide)" },
    ],
    relatedGuides: [
      { href: "/guides/running-a-building-business-uk", title: "Running a Building Business in the UK: The Complete Guide" },
    ],
  },

  {
    slug: "double-storey-extension-cost-uk",
    title: "How Much Does a Double Storey Extension Cost in the UK? (2026 Guide)",
    metaTitle: "Double Storey Extension Cost UK 2026: Full Price Breakdown",
    metaDescription: "How much does a double storey extension cost in the UK? Full 2026 price breakdown covering structure, roofing, joinery, internal fit-out and planning - with regional variations.",
    publishedDate: "2026-06-15",
    modifiedDate: "2026-06-15",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },
    category: "Cost Guides",
    readTime: "11 min read",
    excerpt: "A double storey extension adds two floors of new living space and typically costs £80,000 to £160,000 in the UK. Here is a full breakdown of what drives the cost and what to expect at every stage of the project.",
    sections: [
      {
        type: "p",
        content: "A double storey extension - also called a two-storey extension - adds new rooms on both the ground and first floor levels. It is the most structurally complex type of residential extension, but it also offers the best value per square metre because the foundations, external walls, and scaffolding are shared across both floors.",
      },
      {
        type: "p",
        content: "In the UK, double storey extensions are most commonly built to the rear of semi-detached and detached houses, adding a ground floor room (typically kitchen, dining or family room) and a first floor room (typically a bedroom with or without an en-suite). This guide breaks down the typical cost in 2026.",
      },
      {
        type: "h2",
        content: "Typical double storey extension costs UK 2026",
      },
      {
        type: "ul",
        content: [
          "Small double storey rear extension (up to 25m² per floor): £80,000 to £110,000",
          "Medium double storey rear extension (25–35m² per floor): £110,000 to £145,000",
          "Large double storey rear extension (35–50m² per floor): £145,000 to £200,000+",
          "Premium specification (bi-fold doors, underfloor heating, high-end joinery): add 20 to 40 percent",
          "London and South East premium: add 25 to 35 percent to the above",
        ],
      },
      {
        type: "p",
        content: "These figures are for a traditional brick-and-block construction with a pitched roof. A flat roof double storey extension is slightly cheaper to construct (typically 5 to 10 percent less) but carries higher long-term maintenance costs. Timber frame construction is broadly comparable in cost to brick and block but faster to erect.",
      },
      {
        type: "h2",
        content: "Why is a double storey extension better value per square metre?",
      },
      {
        type: "p",
        content: "The cost per square metre for a double storey extension is typically £1,500 to £2,200 per square metre, compared to £1,800 to £2,800 per square metre for a single storey extension of equivalent footprint. The reason is simple: the most expensive elements of any extension - foundations, external walling, scaffolding, roofing and professional fees - are shared across two floors rather than one.",
      },
      {
        type: "p",
        content: "For example, scaffolding for a double storey extension costs around 30 to 50 percent more than for a single storey extension, but you get twice the usable floor area. Similarly, foundations sized for a two-storey wall cost only marginally more than foundations for a single storey wall. This makes the double storey extension the most cost-efficient way to add square footage to a house.",
      },
      {
        type: "h2",
        content: "Cost breakdown: what goes into a double storey extension",
      },
      {
        type: "h3",
        content: "Groundworks and foundations",
      },
      {
        type: "p",
        content: "A double storey extension requires deeper, more heavily reinforced foundations than a single storey build to carry the additional load. Strip foundations for a standard two-storey extension typically cost £8,000 to £15,000. If the site has poor bearing ground, clay soils prone to heave, or tree root proximity, engineer-designed piled or raft foundations will be specified, adding £5,000 to £15,000 to the foundation cost.",
      },
      {
        type: "h3",
        content: "External walling",
      },
      {
        type: "p",
        content: "The external walls of a double storey extension are built in cavity construction: an inner leaf of 100mm blockwork, a 100mm insulated cavity, and an outer leaf of facing brick to match the existing house. For a medium extension (30m² per floor), the external wall package, including brickwork, insulation, wall ties, DPC, and cavity closers, typically costs £15,000 to £25,000.",
      },
      {
        type: "h3",
        content: "First floor structure",
      },
      {
        type: "p",
        content: "The first floor deck of a double storey extension can be constructed in engineered joists (I-joists or open-web joists) or traditional sawn timber, with a structural chipboard deck and sound insulation quilt. The floor structure also includes any steel beams required to span the ground floor opening into the existing house. First floor structure costs typically run £4,000 to £9,000 depending on the floor area and beam specification.",
      },
      {
        type: "h3",
        content: "Roofing",
      },
      {
        type: "p",
        content: "A pitched roof to a double storey rear extension typically uses cut timber rafters or prefabricated trussed rafters, with tile or slate covering to match the existing house. The roof structure, tile, felt, batten, and insulation for a medium extension costs £8,000 to £18,000 depending on the pitch, spanning, and tile specification. A flat roof using a warm deck EPDM or GRP system is £6,000 to £12,000 for the same footprint.",
      },
      {
        type: "h3",
        content: "External joinery",
      },
      {
        type: "p",
        content: "External joinery includes windows and doors on both levels. A typical double storey rear extension has: ground floor French doors or bi-fold doors (£2,500 to £7,000), ground floor windows (£600 to £1,500 each), and first floor windows (£500 to £1,200 each). UPVC is the most economical option; aluminium costs 40 to 70 percent more but offers a slimmer frame and longer lifespan. Timber is available but adds maintenance costs.",
      },
      {
        type: "h3",
        content: "Scaffolding",
      },
      {
        type: "p",
        content: "Scaffolding for a double storey rear extension typically costs £2,500 to £5,000 for a standard four- to six-week hire. If the programme overruns or the scaffold needs to remain for second fix and decoration, the hire cost increases weekly. Include the scaffold in your quote with clarity on the hire period and the cost of extensions.",
      },
      {
        type: "h3",
        content: "First fix: electrical, plumbing and heating",
      },
      {
        type: "p",
        content: "First fix covers the services installed before plastering: electrical cabling, plumbing pipework, and heating pipework. For a double storey extension with a bedroom and en-suite on the first floor, first fix typically costs £6,000 to £12,000. This figure rises if underfloor heating is specified on the ground floor or if the first floor includes a bathroom.",
      },
      {
        type: "h3",
        content: "Plastering",
      },
      {
        type: "p",
        content: "A two-storey extension involves significant plastering: external walls, party wall, staircase enclosure, first floor walls and ceilings, and making good where the existing house is opened up. A medium double storey extension plastering package typically costs £5,000 to £9,000.",
      },
      {
        type: "h3",
        content: "Second fix, flooring and decoration",
      },
      {
        type: "p",
        content: "Second fix covers fitting sockets, switches, sanitaryware, radiators and joinery after plastering is complete. Flooring covers both levels. Decoration covers all new surfaces plus making good to existing rooms affected by the build. Combined, second fix, flooring, and decoration for a medium double storey extension typically run £12,000 to £22,000 depending on the specification.",
      },
      {
        type: "h2",
        content: "Professional fees for a double storey extension",
      },
      {
        type: "ul",
        content: [
          "Architect fees (design, planning and building regulations): £6,000 to £14,000",
          "Structural engineer: £1,000 to £2,500",
          "Planning application fee (England): £258",
          "Building regulations fees: £600 to £1,200",
          "Party wall surveyor (if required): £700 to £2,500 per neighbouring property",
        ],
      },
      {
        type: "h2",
        content: "Does a double storey extension need planning permission?",
      },
      {
        type: "p",
        content: "In England, double storey rear extensions can fall within Permitted Development up to 3m beyond the original rear wall, provided the eaves and ridge height do not exceed those of the existing roof, and the rear wall of the extension is more than 7m from the rear boundary. Many double storey extensions exceed the 3m limit or trigger other restrictions, making a planning application the most common route.",
      },
      {
        type: "p",
        content: "A householder planning application currently costs £258 in England and takes approximately 8 weeks to determine. Pre-application advice from the planning department (typically £50 to £150 for a residential scheme) can flag objections before you commit to full drawings and help tailor the design to maximise the chances of approval first time.",
      },
      {
        type: "h2",
        content: "How to get an accurate double storey extension quote",
      },
      {
        type: "p",
        content: "Accurate quoting for a double storey extension requires approved or at minimum design-stage drawings, a structural engineer's specification, and a clear brief on the internal specification. Without these, builders are guessing, and large variances between quotes are the predictable result.",
      },
      {
        type: "p",
        content: "When reviewing quotes, check that each one includes: foundations, all external walling, first floor structure including steelwork, roofing to the full specification, external joinery both levels, scaffolding for the full programme, electrical and plumbing first fix and second fix, plastering throughout, flooring throughout, and decoration. A quote that omits any of these is incomplete.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free. Upload your drawings or describe the extension and generate a complete, line-item double storey extension quote in under 10 minutes.",
        heading: "Quote double storey extensions with confidence",
      },
    ],
    faqs: [
      {
        q: "How much does a double storey extension cost in the UK in 2026?",
        a: "A small double storey rear extension (up to 25m² per floor) typically costs £80,000 to £110,000. A medium extension (25–35m² per floor) runs £110,000 to £145,000. A large or premium specification extension costs £145,000 to £200,000 or more. London and the South East add 25 to 35 percent.",
      },
      {
        q: "Is a double storey extension cheaper per square metre than a single storey?",
        a: "Yes. A double storey extension typically costs £1,500 to £2,200 per square metre compared to £1,800 to £2,800 per square metre for a single storey extension, because the most expensive elements - foundations, scaffolding, external walls and roofing - are shared across two floors. If you are planning to add significant space, a double storey extension usually represents better value.",
      },
      {
        q: "Do I need planning permission for a double storey extension?",
        a: "In England, a double storey rear extension may fall within Permitted Development if it extends no more than 3m beyond the original rear wall, the roof height does not exceed the existing ridge, and the rear wall is more than 7m from the rear boundary. Many projects exceed these limits, requiring a householder planning application (£258). Check with your local planning authority or apply for a Lawful Development Certificate to confirm.",
      },
      {
        q: "How long does a double storey extension take to build?",
        a: "On site, a double storey rear extension typically takes 18 to 28 weeks. Allow an additional 3 to 6 months for design, planning, and tendering before work starts. In total, from initial brief to completion, a double storey extension involving a planning application typically takes 12 to 18 months.",
      },
      {
        q: "Does a double storey extension add value to a house?",
        a: "Yes. A well-executed double storey extension that adds a bedroom and en-suite typically increases a property's value by 10 to 20 percent, often more than recovering the build cost in higher-value housing markets. The value uplift is strongest when the extension improves the property's bedroom count - for example, taking a three-bedroom house to four or five bedrooms.",
      },
    ],
    relatedPosts: [
      { slug: "house-extension-planning-uk", title: "How to Plan a House Extension in the UK: A Complete Step-by-Step Guide" },
      { slug: "rear-extension-cost-uk", title: "How Much Does a Rear Extension Cost in the UK? (2026 Builder's Guide)" },
      { slug: "single-storey-extension-cost-uk", title: "Single Storey Extension Cost UK: Builder's Pricing Guide 2026" },
    ],
  },
  {
    slug: "what-to-include-in-builders-quote",
    title: "What to Include in a Builder's Quote: A Complete Checklist for UK Builders",
    metaTitle: "What to Include in a Builder's Quote (UK Checklist 2026)",
    metaDescription: "Exactly what to include in a builder's quote in the UK. A complete checklist covering line items, exclusions, payment terms, VAT and more - so nothing is missed.",
    publishedDate: "2026-06-17",
    modifiedDate: "2026-06-17",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },
    category: "Guides",
    readTime: "8 min read",
    coverImage: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=1200&q=80",
    excerpt: "A builder's quote is more than a price on a page. Getting the content right protects you legally, reduces disputes, and helps you win work at the right margin. Here is everything a professional UK builder quote should include.",
    sections: [
      {
        type: "p",
        content: "Most builders know how to price a job. Fewer know how to write a quote that protects them when things go wrong. A quote is a legal document - once a client accepts it, you are bound by what it says. If key information is missing, you end up working from a gentleman's agreement, which usually favours the client.",
      },
      {
        type: "p",
        content: "This checklist covers every element a professional builder's quote should include. Whether you are sending a one-page estimate for a bathroom renovation or a detailed breakdown for a full rear extension, the same principles apply.",
      },
      {
        type: "h2",
        content: "Why the content of your quote matters more than the price",
      },
      {
        type: "p",
        content: "When two builders quote the same job, the client rarely compares like for like. One quote might include scaffolding; another might not. One might include waste removal; another prices it as an extra. If your quote is vague, you will either lose on price (because the client thinks you are expensive when you are just more thorough) or lose money on the job (because you have to absorb costs you forgot to include).",
      },
      {
        type: "p",
        content: "A detailed, well-structured quote also signals professionalism. Clients who are spending £30,000 or £100,000 on a home improvement want confidence that the builder they choose will manage their project carefully. The quality of your quote is often the first evidence they get of how you work.",
      },
      {
        type: "h2",
        content: "The 10 essential elements of a builder's quote",
      },
      {
        type: "h3",
        content: "1. Your company details",
      },
      {
        type: "p",
        content: "Include your full business name, address, phone number, email address, and website if you have one. If you are VAT-registered, include your VAT registration number. If you are a limited company, include your company registration number. This information establishes you as a professional business and is required for the document to function as a valid commercial quote.",
      },
      {
        type: "h3",
        content: "2. The client's details and project address",
      },
      {
        type: "p",
        content: "Include the client's full name or company name, their contact address, and the address of the works if it is different. This prevents any ambiguity about which project the quote relates to - important if you are quoting multiple jobs for the same client.",
      },
      {
        type: "h3",
        content: "3. A clear description of the scope of works",
      },
      {
        type: "p",
        content: "The scope of works is the most important section of your quote. It should describe, in plain language, exactly what you will be doing. Do not just write 'extension as discussed' - write out every element: groundworks and foundations, masonry, structural steelwork, roof, windows and doors, first fix, second fix, finishes, and external works. The more specific you are, the less room there is for the client to claim something was included when it was not.",
      },
      {
        type: "h3",
        content: "4. A line-item cost breakdown",
      },
      {
        type: "p",
        content: "Break your price down by work item rather than presenting a single lump sum. A line-item breakdown shows the client that you have thought through the job properly, makes it easier to discuss changes or scope reductions, and protects you if any element is disputed. Group items by trade or work stage: preliminaries, groundworks, structure, roofing, first fix MEP, second fix MEP, plastering, finishes, and so on.",
      },
      {
        type: "h3",
        content: "5. Materials specification",
      },
      {
        type: "p",
        content: "Where relevant, state the specification of key materials. If you are pricing timber frame, specify the grade. If you are pricing insulation, specify the U-value target and product type. This protects you from being asked to upgrade materials at no extra charge and helps the client understand what they are getting for their money.",
      },
      {
        type: "h3",
        content: "6. Assumptions",
      },
      {
        type: "p",
        content: "Every quote is based on information available at the time of pricing. State your assumptions clearly. For example: 'This quote is based on the architectural drawings dated 15 June 2026. It assumes no unforeseen ground conditions and that existing drainage can be connected without diversion.' If those assumptions turn out to be wrong, you have a clear basis for raising a variation.",
      },
      {
        type: "h3",
        content: "7. Exclusions",
      },
      {
        type: "p",
        content: "Exclusions are just as important as inclusions. State explicitly what is not in your price: structural engineer's fees, building regulations applications, party wall surveys, specialist subcontractors, kitchen and bathroom fittings, decoration, or anything else the client might reasonably assume is included. A clear exclusions list prevents disputes and makes your quote easier to compare with competitors whose scope may be narrower than yours.",
      },
      {
        type: "h3",
        content: "8. Programme and timeline",
      },
      {
        type: "p",
        content: "Tell the client how long the work will take and, if possible, your anticipated start date. This does not need to be a full construction programme - a simple statement such as 'works are anticipated to take 14 weeks from start on site' is enough. Clients almost always want to know how long they will be living in a building site, and giving them a clear answer builds confidence.",
      },
      {
        type: "h3",
        content: "9. Payment terms and stage payments",
      },
      {
        type: "p",
        content: "Never leave payment terms unspecified. State your deposit requirement (typically 10 to 25 percent), your stage payment schedule linked to completion milestones, and your payment period (for example, net 14 days from invoice). If you are working under a JCT or other construction contract, reference the contract terms. Clearly defined payment terms reduce late payment and give you a legal basis for stopping work if payments are not made.",
      },
      {
        type: "h3",
        content: "10. VAT, quote validity, and signature block",
      },
      {
        type: "p",
        content: "State whether your prices are exclusive or inclusive of VAT, and if you are VAT-registered, show the VAT amount and total including VAT separately. State how long your quote is valid (typically 30 days). Include a simple acceptance section - a line for the client's name, signature, and date - so the quote can double as a basic letter of intent when signed.",
      },
      {
        type: "h2",
        content: "What to leave out of a builder's quote",
      },
      {
        type: "p",
        content: "A quote should be thorough but not overwhelming. Leave out internal costings and margin calculations - the client does not need to see your labour rates or markup percentages. Leave out speculative scope that is not confirmed - if the client might want an additional bathroom, quote that separately when instructed. And leave out conditional prices that depend on too many unknowns, which can make clients suspicious.",
      },
      {
        type: "h2",
        content: "Common mistakes builders make in their quotes",
      },
      {
        type: "ul",
        content: [
          "Presenting a single lump sum with no breakdown, which makes it impossible to discuss changes",
          "Not stating exclusions, leading to disputes about what was and was not included",
          "Forgetting to include preliminaries such as site setup, skip hire, scaffolding and protection",
          "Omitting VAT or failing to state whether prices are VAT-inclusive",
          "Not specifying a validity period, leaving you exposed to price increases months later",
          "Failing to include payment terms, which makes chasing overdue payments harder",
          "Using vague scope descriptions like 'extension as agreed' that give no protection when scope is disputed",
        ],
      },
      {
        type: "h2",
        content: "How quoting software makes this easier",
      },
      {
        type: "p",
        content: "Writing a thorough, well-structured quote manually takes time - often two to three hours for a mid-sized project. Builder quoting software automates the structure so every quote comes out consistently formatted, with line items, scope descriptions, exclusions and payment terms included by default.",
      },
      {
        type: "p",
        content: "The best tools for UK builders let you upload drawings or describe the job in plain text and generate a structured quote automatically, with UK market rates applied to each line item. What used to take an evening can be done in under 10 minutes, with nothing missed.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days. Generate a complete, line-item builder's quote from drawings or a job description - with scope, exclusions and payment terms included as standard.",
        heading: "Stop missing items. Start quoting professionally.",
      },
    ],
    faqs: [
      {
        q: "Does a builder's quote have to be itemised?",
        a: "There is no legal requirement for a builder's quote to be itemised, but it is strongly recommended. An itemised quote protects you by making the scope clear, helps clients understand what they are paying for, and makes it easier to manage variations and avoid disputes. Clients are also more likely to trust and accept a detailed quote than a single lump sum.",
      },
      {
        q: "Should I show labour and materials separately in my quote?",
        a: "You do not have to, but showing them separately can help clients understand your pricing and can be useful for VAT purposes on certain types of work. Some builders prefer to show an all-in rate per item to avoid clients trying to supply their own materials at reduced prices. Either approach is acceptable - just be consistent and make your assumptions clear.",
      },
      {
        q: "What is the difference between a builder's quote and an estimate?",
        a: "A quote is a fixed price offer - if the client accepts it, you are bound by the figure unless the scope changes. An estimate is an approximate price that may change as the project is defined more clearly. For most building work, clients expect a quote. Only use the word 'estimate' if you genuinely cannot commit to a fixed price, and make the distinction clear in your documentation.",
      },
      {
        q: "How long should a builder's quote be valid for?",
        a: "Most UK builders quote for 30 days. Some extend to 60 days for larger projects where the client needs more time to make decisions. Beyond that, material and labour costs may have moved enough to affect your margin. Always state the validity period clearly on the quote so clients cannot hold you to an old price months later.",
      },
      {
        q: "Do I need to include VAT on a builder's quote?",
        a: "If you are VAT-registered, yes. You must show prices exclusive of VAT, the VAT rate applied, the VAT amount, and the total inclusive of VAT. Most domestic building work in the UK is standard-rated at 20 percent, though some work qualifies for the 5 percent reduced rate. If you are not VAT-registered, state that prices do not include VAT so there is no ambiguity.",
      },
    ],
    relatedPosts: [
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
      { slug: "extension-quote-template-uk", title: "Extension Quote Template: What Every UK Builder Should Include" },
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/guides/quoting-for-uk-builders", title: "Quoting for UK Builders: The Complete Guide" },
    ],
  },
  {
    slug: "simpro-too-complicated",
    title: "Is simPRO Too Complicated for Smaller Builders? (And What to Use Instead)",
    metaTitle: "Is simPRO Too Complicated? Alternatives for Smaller UK Builders (2026)",
    metaDescription: "Many smaller UK builders find simPRO too complex and expensive for their needs. Here is an honest look at who simPRO suits, where it struggles, and what to use instead.",
    publishedDate: "2026-06-17",
    modifiedDate: "2026-06-17",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },
    category: "Software",
    readTime: "7 min read",
    coverImage: "https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=1200&q=80",
    excerpt: "simPRO is a powerful field service management platform, but builders consistently report that it is too complex, too expensive, and too time-consuming to set up for small to medium-sized building businesses. Here is what you actually need and where to find it.",
    sections: [
      {
        type: "p",
        content: "If you have ever looked at simPRO and thought 'this is a lot' - you are not alone. simPRO is one of the most comprehensive field service management platforms on the market. It is used by large service contractors with dozens of engineers, complex scheduling requirements, and enterprise-level reporting needs. For a builder running a small to medium-sized residential construction business, that same comprehensiveness can quickly become a burden.",
      },
      {
        type: "p",
        content: "This article looks honestly at what simPRO does well, where it struggles for smaller building businesses, and what alternatives are worth considering.",
      },
      {
        type: "h2",
        content: "What simPRO is actually built for",
      },
      {
        type: "p",
        content: "simPRO started as a job management platform for trade service businesses - primarily plumbing, electrical and HVAC contractors running maintenance and service contracts alongside project work. It is built around the concept of a job booking system with engineer scheduling, asset management, and compliance tracking.",
      },
      {
        type: "p",
        content: "At its core, simPRO is designed for businesses that need to: schedule multiple field engineers across recurring service contracts; manage compliance certificates and asset registers; track parts and stock; and run detailed job costing against pre-built rate cards. These are legitimate enterprise needs - but they are not the needs of most residential builders.",
      },
      {
        type: "h2",
        content: "The most common complaints from builders using simPRO",
      },
      {
        type: "p",
        content: "Builders who have tried simPRO and moved away from it consistently report the same issues.",
      },
      {
        type: "ul",
        content: [
          "Implementation takes months. simPRO is not a tool you set up in a weekend. The onboarding process requires configuring rate cards, catalogue items, workflows, and user permissions - often with the help of a paid implementation partner.",
          "The learning curve is steep. Staff need formal training to use it effectively, and the interface can be overwhelming for builders who just want to produce a quote quickly.",
          "It is expensive for what most small builders need. simPRO's pricing is typically in the range of £100 to £300 per user per month depending on the tier, making it a significant overhead for a sole trader or small team.",
          "It was not designed around construction scopes of works. simPRO's quoting is built around service rate cards and parts catalogues, not around producing detailed scope-based construction estimates from drawings.",
          "Support is good for enterprise features but less helpful for residential construction specifics. The platform's best-practice documentation is oriented around service contracts, not project-based building work.",
          "Integration with UK construction pricing data is limited. Users typically have to build and maintain their own rate library from scratch.",
        ],
      },
      {
        type: "h2",
        content: "Who simPRO is genuinely right for",
      },
      {
        type: "p",
        content: "None of this means simPRO is a bad product. For the right business, it is excellent. simPRO makes sense if you: manage a team of 10 or more field engineers; run a large volume of service and maintenance contracts; need asset management and compliance tracking as a core part of your workflow; or operate across multiple depots or regions. If your business looks like this, simPRO's complexity is a feature, not a bug.",
      },
      {
        type: "p",
        content: "But if you are a sole trader or a small building business doing primarily project-based residential work - extensions, loft conversions, renovations - simPRO is probably more platform than you need.",
      },
      {
        type: "h2",
        content: "What smaller builders actually need from software",
      },
      {
        type: "p",
        content: "For a residential builder doing 10 to 50 projects per year, the software requirements are genuinely simpler than simPRO's feature set suggests.",
      },
      {
        type: "ul",
        content: [
          "Fast, accurate quoting that works from drawings, photos, or a job description",
          "UK-specific pricing for construction trades - not a generic rate card you have to populate yourself",
          "A professional PDF output that impresses clients and includes a proper scope of works",
          "Client management to keep contact details and job history organised",
          "Simple enough to use without training or an implementation partner",
          "Priced for a small business, not an enterprise",
        ],
      },
      {
        type: "p",
        content: "These are not enterprise requirements. They are the practical needs of a builder who wants to spend more time on site and less time on admin.",
      },
      {
        type: "h2",
        content: "How The Proper Tool compares",
      },
      {
        type: "p",
        content: "The Proper Tool is built specifically for UK residential builders who need fast, accurate quoting without the overhead of enterprise field service software. You can upload architectural drawings or describe a job in plain text and generate a complete, line-item quote with UK pricing in under 10 minutes. There is no implementation project, no rate card configuration, and no training required.",
      },
      {
        type: "p",
        content: "Plans start from £29 per month for a sole trader, which is a fraction of the cost of a simPRO subscription. You get a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes so you can see whether it works for your business before committing.",
      },
      {
        type: "p",
        content: "It does not replace simPRO for the businesses simPRO is right for. But for smaller residential builders who tried simPRO and found it too complex, it covers the quoting workflow cleanly and without the overhead.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days. Generate your first quote from drawings or a job description in under 10 minutes - no setup, no training, no implementation partner required.",
        heading: "Quoting software that works on day one",
      },
    ],
    faqs: [
      {
        q: "Is simPRO suitable for sole traders or small builders?",
        a: "simPRO can be used by sole traders, but it is generally considered overbuilt for that use case. The platform's strengths - engineer scheduling, asset management, service contract tracking, and enterprise reporting - are most valuable for businesses with 10 or more field staff. Sole traders and small building businesses typically find it too complex and expensive relative to what they need.",
      },
      {
        q: "How much does simPRO cost in the UK?",
        a: "simPRO does not publish its pricing publicly, but independent reviews and user reports suggest UK pricing is typically in the range of £100 to £300 per user per month depending on the tier and modules required, with additional costs for implementation and training. For a small building business, this represents a significant overhead compared to purpose-built quoting tools.",
      },
      {
        q: "What is the best alternative to simPRO for a residential builder?",
        a: "For a UK residential builder focused on quoting projects rather than managing service contracts, The Proper Tool is purpose-built for that workflow. It generates detailed construction quotes from drawings or job descriptions in under 10 minutes, uses UK residential pricing rates, and produces professional PDF output. Other alternatives include Tradify and Jobber for general job management, though neither is construction-specific.",
      },
      {
        q: "Can I migrate my data out of simPRO?",
        a: "simPRO allows data exports for contacts, jobs, and catalogue items in various formats. The difficulty of migration depends largely on how extensively you have built out your simPRO configuration. Most builders switching to simpler tools find the most practical approach is to export their client list and start fresh with quoting, rather than attempting to migrate historical job data.",
      },
    ],
    relatedPosts: [
      { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
      { slug: "ai-quoting-software-uk-builders", title: "AI Quoting Software for UK Builders: What It Does and Whether It's Worth It" },
    ],
    relatedComparisons: [
      { slug: "simpro-alternative", title: "The Proper Tool vs simPRO" },
      { slug: "tradify-alternative", title: "The Proper Tool vs Tradify" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/simpro-alternative", title: "simPRO Alternative for Small Builders" },
      { href: "/buildxact-alternative", title: "Buildxact Alternative for UK Builders" },
      { href: "/estimatorxpress-alternative", title: "EstimatorXpress Alternative for UK Builders" },
      { href: "/best/quoting-software-for-uk-builders", title: "Full comparison: all 6 quoting tools for UK builders" },
      { href: "/guides/running-a-building-business-uk", title: "Running a Building Business in the UK: The Complete Guide" },
    ],
  },
  {
    slug: "free-quoting-software-builders-uk",
    title: "Free Quoting Software for UK Builders: What You Actually Get (And What You Don't)",
    metaTitle: "Free Quoting Software for UK Builders (2026): What's Worth Using",
    metaDescription: "Looking for free quoting software as a UK builder? Here is an honest guide to what free tools exist, what their real limitations are, and whether a free trial beats free forever.",
    publishedDate: "2026-06-17",
    modifiedDate: "2026-06-17",
    author: { name: "James Hartley", role: "Construction Industry Writer", bio: "James Hartley is a construction industry specialist with over a decade of experience working alongside UK builders on estimating, pricing, and project delivery." },
    category: "Software",
    readTime: "6 min read",
    coverImage: "https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=1200&q=80",
    excerpt: "The appeal of free quoting software is obvious - no monthly fee, no commitment. But the reality for UK builders is that truly free tools come with significant limitations that cost you in other ways. Here is what you actually get.",
    sections: [
      {
        type: "p",
        content: "Every builder searches for free quoting software at some point. The appeal is obvious: zero monthly cost, no contract, no risk. The reality is more complicated. What passes as 'free' builder quoting software in the UK usually falls into one of three categories - free tiers of general invoicing tools, spreadsheet templates, or permanently free software with features stripped back to the point of limited usefulness.",
      },
      {
        type: "p",
        content: "This guide looks honestly at what is available for free, what the real limitations are, and whether a free trial of purpose-built software might be a better starting point than free-forever tools.",
      },
      {
        type: "h2",
        content: "What free builder quoting tools actually exist in the UK",
      },
      {
        type: "h3",
        content: "Free tiers of general invoicing software",
      },
      {
        type: "p",
        content: "Tools like Wave, Zoho Invoice, and the free tier of QuickBooks let you create basic estimates and invoices at no cost. These work well for simple service businesses with short price lists. For a builder needing a structured scope of works with trade-specific line items, quantities, labour and materials breakdowns, and a professional construction PDF, they fall short. There is no construction pricing database, no ability to read drawings, and the output is a generic invoice template rather than a construction document.",
      },
      {
        type: "h3",
        content: "Spreadsheet templates",
      },
      {
        type: "p",
        content: "A well-built Excel or Google Sheets template can produce a professional-looking quote. Many builders use them successfully for years. The limitations are that the pricing library is only as good as the rates you have entered yourself, there is no AI assistance for scope generation, version control becomes difficult as templates evolve, and producing a consistent, branded PDF requires additional formatting work. Templates are free to use but cost you in time.",
      },
      {
        type: "h3",
        content: "Free tiers of construction-specific tools",
      },
      {
        type: "p",
        content: "Some construction-specific platforms offer a permanently free tier - but these typically limit you to one or two active quotes, remove branded PDF output, and exclude the pricing database and AI features. You get a taste of the interface but not enough functionality to run your quoting workflow on it day to day.",
      },
      {
        type: "h2",
        content: "The real limitations of free quoting tools",
      },
      {
        type: "p",
        content: "The limitations of free tools rarely show up in feature comparisons - they show up in the work. Here is what builders consistently find when they rely on free tools for construction quoting.",
      },
      {
        type: "ul",
        content: [
          "No UK construction pricing database. You have to look up and enter every rate yourself. This adds 30 to 60 minutes to every quote, and if you have not quoted a particular type of work recently, your rates may be out of date.",
          "No scope generation. Free tools do not read drawings or help you identify what needs to be included. The risk of missing items - prelims, structural connections, finishes, external works - is entirely on you.",
          "Generic PDF output. A quote that looks like an invoice does not inspire confidence in a client spending £50,000 to £150,000 on their home. Professional presentation matters and free tools rarely deliver it.",
          "No learning from your jobs. Purpose-built quoting software can learn from your previous jobs and rate adjustments over time. Free tools start from scratch every time.",
          "Limited support. Free tools typically mean community forums or documentation only. When you are under pressure to get a quote out, waiting for a forum reply is not helpful.",
        ],
      },
      {
        type: "h2",
        content: "Why 'free forever' often means 'limited forever'",
      },
      {
        type: "p",
        content: "Software companies that offer permanent free tiers have to make money somewhere. Usually that means the free tier is deliberately limited to encourage upgrades, and the features that make quoting fast and accurate - AI generation, pricing databases, professional PDF output - are locked behind a paywall. You end up with a tool that demonstrates what the product could do for you but does not actually let you do it.",
      },
      {
        type: "p",
        content: "The time cost of a free tool is also often underestimated. If using a free spreadsheet-based approach adds two hours to every quote, and you produce 30 quotes per year, that is 60 hours of admin time annually. At a builder's time value of £30 to £50 per hour, that is £1,800 to £3,000 in lost productive time - far more than the cost of a monthly software subscription.",
      },
      {
        type: "h2",
        content: "The case for a free trial over a free tool",
      },
      {
        type: "p",
        content: "A 14-day free trial of purpose-built software gives you access to the full product - every feature, every pricing database, the AI generation, the branded PDF output. You can run real quotes for real clients and see whether the tool actually saves you time before you spend a penny.",
      },
      {
        type: "p",
        content: "The difference in outcome between a free tier and a full-access trial is significant. On a free tier, you are often evaluating a deliberately crippled version of the tool. On a full trial, you are evaluating whether the real product genuinely fits your workflow.",
      },
      {
        type: "h2",
        content: "What The Proper Tool's free trial includes",
      },
      {
        type: "p",
        content: "The Proper Tool offers a 14-day free trial with one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. Every feature is available from day one: upload drawings or describe a job in plain text; generate a complete, line-item scope of works with UK pricing; produce a professional branded PDF; manage your clients; and use the 31-category cost library. No credit card is required to start.",
      },
      {
        type: "p",
        content: "Plans after the trial start at £29 per month for a sole trader, rising to £59 and £99 for growing businesses. There is no setup fee and no minimum contract. If it does not work for your business, you cancel and pay nothing.",
      },
      {
        type: "cta",
        content: "Try The Proper Tool free for 14 days - all features, one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes, no credit card required. See for yourself whether it saves you time on your next job.",
        heading: "Start your free trial today",
      },
    ],
    faqs: [
      {
        q: "Is there genuinely free quoting software for builders in the UK?",
        a: "Yes, but with significant limitations. Free tiers of general invoicing tools (Wave, Zoho Invoice) let you create basic estimates at no cost but lack construction-specific features. Spreadsheet templates are free to use but require you to maintain your own pricing. Construction-specific tools tend to offer free trials rather than permanently free plans, because the underlying pricing databases and AI features have real operating costs.",
      },
      {
        q: "What are the main limitations of free builder quoting tools?",
        a: "The most significant limitations are: no UK construction pricing database (you enter every rate yourself); no AI scope generation (you identify every line item manually, risking missed items); generic invoice-style PDF output rather than a professional construction document; and usually no support beyond documentation. Free tools tend to cost you in time what they save you in subscription fees.",
      },
      {
        q: "Is The Proper Tool free?",
        a: "The Proper Tool offers a 14-day free trial with full access to all features and one shared allowance of 5 total quote creations across Auto, Template, and Manual quotes. After the trial, plans start at £29 per month. There is no permanently free tier, but the trial gives you enough time to generate real quotes for real clients and decide whether the tool earns its keep before you pay anything.",
      },
      {
        q: "How much does builder quoting software typically cost in the UK?",
        a: "Purpose-built construction quoting software for UK builders typically costs between £29 and £100 per month for a small business. General field service management platforms like simPRO or Buildertrend cost significantly more - often £100 to £300 per user per month. For a sole trader or small team focused on residential building work, a purpose-built quoting tool in the £29 to £59 per month range typically provides the best value.",
      },
    ],
    relatedPosts: [
      { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
      { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
      { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    ],
    relatedGuides: [
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders: Full Comparison" },
      { href: "/auto-quote", title: "Auto Quote: Generate a Quote from a Description in Minutes" },
      { href: "/pricing", title: "The Proper Tool Pricing: Free Trial, Then from £29/month" },
      { href: "/guides/quoting-for-uk-builders", title: "Quoting for UK Builders: The Complete Guide" },
    ],
  },
];

export interface AlternativePage {
  slug: string;
  href?: string;
  competitor: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  publishedDate: string;
  modifiedDate: string;
  intro: string;
  competitorOverview: string;
  competitorLimitations: string[];
  comparisonRows: { feature: string; competitor: string; us: string }[];
  pricingComparison: string;
  switchReasons: string[];
  conclusion: string;
  relatedBlogPost?: { slug: string; title: string };
  relatedAlternatives?: { slug: string; competitor: string }[];
  relatedGuides?: { href: string; title: string }[];
}

export const ALTERNATIVE_PAGES: AlternativePage[] = [
  {
    slug: "tradify-alternative",
    href: "/tradify-alternative",
    competitor: "Tradify",
    title: "The Best Tradify Alternative for UK Builders (2026)",
    metaTitle: "Tradify Alternative UK: Builder Quoting Software Comparison",
    metaDescription: "Compare verified Tradify pricing, trial, mobile apps, AI limits, drawing takeoff and cost-library features with The Proper Tool for UK builders.",
    publishedDate: "2025-03-10",
    modifiedDate: "2026-09-19",
    intro: "Tradify is a popular job management platform for tradespeople. It covers scheduling, invoicing and some quoting functionality. But for UK builders who need detailed, structured construction quotes with line-item breakdowns and UK-specific pricing, Tradify has real limitations. This page compares Tradify with The Proper Tool so you can decide which is right for your business.",
    competitorOverview: "Tradify is a New Zealand-based job management platform that has expanded into the UK market. It is designed primarily for service tradespeople such as electricians, plumbers and HVAC engineers who need to schedule jobs, track time, and invoice clients. Its quoting features are straightforward and work well for smaller, simpler jobs.",
    competitorLimitations: [
      "Designed primarily for service trades rather than construction and building work",
      "Quoting is based on simple item lists rather than structured scopes of works with quantities and descriptions",
      "No ability to read drawings or extract specifications automatically",
      "Pricing database is not tailored to UK residential construction labour and material rates",
      "Limited support for complex structural jobs involving steelwork, foundations or groundworks",
      "No built-in validation of scope completeness or quantity checking",
      "PDF output is functional but not optimised for construction quotes with detailed line items",
    ],
    comparisonRows: [
      { feature: "Reads structural drawings and PDFs", competitor: "No", us: "Yes" },
      { feature: "UK construction pricing database", competitor: "Limited", us: "31 trade categories" },
      { feature: "Structured scope of works generation", competitor: "Manual entry only", us: "Automatic from drawings or description" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in review" },
      { feature: "Professional construction PDF output", competitor: "Basic", us: "Full itemised breakdown" },
      { feature: "Job scheduling and management", competitor: "Yes", us: "No (quote focus only)" },
      { feature: "Invoicing", competitor: "Yes", us: "Quote-focused (export to invoice separately)" },
      { feature: "Free trial available", competitor: "Trial only", us: "Yes: 14-day free trial" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Tradify lists Lite at £34, Pro at £37 and Plus at £44 per user/month, with a 14-day free trial. It supports iPhone, iPad, Android and web. Tradify has SmartWrite and SmartRead for bills, but no evidence of AI generating priced quotes from job scopes or drawings. The Proper Tool starts at £29 per account with a 14-day free trial.",
    switchReasons: [
      "You are doing complex residential construction work including extensions, loft conversions or structural alterations",
      "You need to produce detailed, line-item building quotes rather than simple job sheets",
      "You want to be able to quote from drawings without manually transferring all the information",
      "Your clients expect a structured scope of works rather than a simple price",
      "You want UK-specific pricing that reflects real construction costs in your region",
    ],
    conclusion: "Tradify is a well-built tool for service trades who need scheduling and invoicing alongside basic quoting. For UK builders who need detailed construction quotes with proper scopes of works, UK pricing, and the ability to work from drawings, The Proper Tool is a better fit. The two tools serve different primary needs, so the right choice depends on whether your priority is job management or construction quoting.",
    relatedBlogPost: { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
    relatedAlternatives: [
      { slug: "jobber-alternative", competitor: "Jobber" },
      { slug: "simpro-alternative", competitor: "simPRO" },
      { slug: "price-the-job-alternative", competitor: "Price the Job" },
    ],
    relatedGuides: [
      { href: "/tradify-alternative", title: "Tradify Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "buildertrend-alternative",
    competitor: "Buildertrend",
    title: "The Best Buildertrend Alternative for UK Builders (2025)",
    metaTitle: "Buildertrend Alternative UK: Construction Quoting Software",
    metaDescription: "Looking for a Buildertrend alternative in the UK? Compare Buildertrend vs The Proper Tool for builder quoting, estimating and project management.",
    publishedDate: "2025-03-17",
    modifiedDate: "2026-03-20",
    intro: "Buildertrend is a comprehensive construction management platform used by builders worldwide. It covers everything from lead management to project scheduling, client communication, and financial reporting. But for UK small builders and sole traders who primarily need fast, accurate construction quotes, Buildertrend can be overwhelming and expensive. This page compares the two so you can decide which is the better fit.",
    competitorOverview: "Buildertrend is a US-based construction management platform designed for residential builders. It is feature-rich, covering the full project lifecycle from sales lead through to project completion and warranty. In the UK it is used primarily by larger residential builders and developers. It offers estimating functionality alongside its broader suite of tools.",
    competitorLimitations: [
      "Designed for the US market with pricing databases that do not reflect UK labour rates and material costs",
      "Very high price point (typically £300 to £500 per month) that is not viable for small UK builders",
      "Complex to set up and requires significant time investment to configure before it adds value",
      "Better suited to project management than fast construction quoting",
      "Estimating module requires extensive manual configuration before use",
      "Learning curve of several weeks before the tool pays for itself",
      "Overkill for sole traders or small teams who primarily need a quoting solution",
    ],
    comparisonRows: [
      { feature: "UK-specific construction pricing", competitor: "No (US-based)", us: "Yes: 31 UK trade categories" },
      { feature: "Reads drawings automatically", competitor: "No", us: "Yes" },
      { feature: "Time to first quote", competitor: "Weeks of setup", us: "Under 10 minutes" },
      { feature: "Suited to small builders / sole traders", competitor: "No: enterprise focus", us: "Yes: built for this" },
      { feature: "Free trial available", competitor: "No", us: "Yes: 14-day free trial" },
      { feature: "Typical monthly cost", competitor: "£300 to £500+", us: "£29 to £99" },
      { feature: "Project management features", competitor: "Comprehensive", us: "Quote-focused" },
      { feature: "Client portal", competitor: "Yes", us: "Shared quote links" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Buildertrend typically costs between £300 and £500 per month depending on the plan and number of users. For most UK small builders, this is simply not viable. The Proper Tool plans start from £29 per month with a 14-day free trial, making professional quoting accessible for sole traders and small teams.",
    switchReasons: [
      "You are a small builder or sole trader and do not need a full project management platform",
      "You want to start quoting professionally without weeks of setup and configuration",
      "UK construction pricing is important to you and US-based pricing databases do not work for your business",
      "Your budget for software is modest and you need clear value from day one",
      "Quoting is your primary need rather than full project lifecycle management",
    ],
    conclusion: "Buildertrend is a powerful tool for larger residential builders who need to manage the full project lifecycle. For UK small builders who need fast, accurate construction quotes with UK pricing, it is expensive and complex relative to the value it delivers. The Proper Tool is built specifically for the quoting needs of UK builders, making it the better choice for most small and medium-sized building businesses.",
    relatedBlogPost: { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
    relatedAlternatives: [
      { slug: "buildxact-alternative", competitor: "Buildxact" },
      { slug: "simpro-alternative", competitor: "simPRO" },
      { slug: "servicetitan-alternative", competitor: "ServiceTitan" },
    ],
    relatedGuides: [
      { href: "/buildertrend-alternative", title: "Buildertrend Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "simpro-alternative",
    href: "/simpro-alternative",
    competitor: "simPRO",
    title: "The Best simPRO Alternative for UK Builders (2025)",
    metaTitle: "simPRO Alternative UK: Builder Quoting and Estimating Software",
    metaDescription: "Comparing simPRO vs The Proper Tool for UK builders. Find a simpler, more affordable alternative for construction quoting and estimating.",
    publishedDate: "2025-03-24",
    modifiedDate: "2026-09-19",
    intro: "simPRO is a field service management platform that includes quoting and estimating functionality. It is widely used by trade businesses in Australia and New Zealand and has expanded into the UK. For builders who need a complex, integrated business management system, simPRO offers a lot. For those who primarily need accurate construction quotes quickly, it can feel like a lot of software for a specific need. This page compares both options.",
    competitorOverview: "simPRO is an Australian field service management platform covering quoting, job management, scheduling, invoicing and inventory. It is particularly popular with trade businesses that manage large numbers of recurring service jobs. In the UK, it is used by larger trade businesses across electrical, plumbing and HVAC sectors.",
    competitorLimitations: [
      "Designed primarily for field service businesses, not residential construction",
      "Pricing database is not calibrated for UK residential construction rates",
      "Complex system with a steep learning curve, often requiring formal implementation support",
      "Quoting module is built around service job quoting rather than construction scope of works",
      "High cost relative to the quoting functionality alone",
      "Implementation time of several weeks or months before full value is realised",
      "Better suited to businesses with recurring service work than project-based residential builders",
    ],
    comparisonRows: [
      { feature: "Built for residential construction", competitor: "No: field service focus", us: "Yes" },
      { feature: "UK residential pricing", competitor: "Limited", us: "31 UK trade categories" },
      { feature: "Reads construction drawings", competitor: "No", us: "Yes" },
      { feature: "Setup time", competitor: "Weeks to months", us: "Minutes" },
      { feature: "Free trial available", competitor: "No", us: "Yes: 14-day free trial" },
      { feature: "Typical cost", competitor: "£100 to £300+ per month", us: "£29 to £99 per month" },
      { feature: "Scope of works generation", competitor: "Manual entry", us: "Automatic" },
      { feature: "Service job management", competitor: "Excellent", us: "Not the focus" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "simPRO does not publish a standard UK price. Prospective customers request pricing and receive a guided personalised demo rather than a self-serve trial. Its optional Takeoffs add-on supports plan upload, manual measure/markup and symbol counting or recognition. The Proper Tool starts at £29 per month with a 14-day free trial.",
    switchReasons: [
      "You are a residential builder, not a field service business",
      "You want a tool that generates construction scopes and pricing automatically",
      "You need to be up and running immediately, not after weeks of training and setup",
      "Your budget does not justify a full field service management platform",
      "Construction-specific features matter more to you than scheduling and inventory management",
    ],
    conclusion: "simPRO is a capable platform for field service businesses with complex scheduling and inventory needs. For UK residential builders who primarily need to produce accurate, professional construction quotes quickly, it is more software than the job requires. The Proper Tool focuses specifically on construction quoting for UK builders, delivering what most builders actually need without the complexity or cost of a full field service platform.",
    relatedBlogPost: { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
    relatedAlternatives: [
      { slug: "tradify-alternative", competitor: "Tradify" },
      { slug: "jobber-alternative", competitor: "Jobber" },
      { slug: "buildertrend-alternative", competitor: "Buildertrend" },
    ],
    relatedGuides: [
      { href: "/simpro-alternative", title: "simPRO Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "jobber-alternative",
    competitor: "Jobber",
    title: "The Best Jobber Alternative for UK Builders (2025)",
    metaTitle: "Jobber Alternative UK: Builder Quoting Software Comparison",
    metaDescription: "Looking for a Jobber alternative for UK construction quoting? Compare Jobber vs The Proper Tool for builders who need detailed, accurate building estimates.",
    publishedDate: "2025-03-31",
    modifiedDate: "2026-03-20",
    intro: "Jobber is a popular job management platform for home service businesses. It works well for trades that manage recurring service appointments and invoicing. But for builders who need to produce detailed construction quotes from drawings with proper scopes of works and UK-specific pricing, Jobber has notable gaps. This page compares the two platforms.",
    competitorOverview: "Jobber is a Canadian job management platform designed for home service businesses including landscapers, cleaners, HVAC contractors and similar trades. It has expanded into the UK market and is popular for its clean interface and ease of use. Its quoting functionality is designed for simple service quotes rather than detailed construction estimates.",
    competitorLimitations: [
      "Designed for home service businesses, not residential construction",
      "Quoting is based on simple line items without construction-specific scope of works",
      "No ability to read drawings or extract construction specifications",
      "Pricing is generic and not calibrated for UK construction trades",
      "No validation of scope completeness for building projects",
      "Better suited to recurring service work than project-based construction",
      "PDF output is clean but not structured for detailed building quotes",
    ],
    comparisonRows: [
      { feature: "Built for construction", competitor: "No: home service focus", us: "Yes" },
      { feature: "Reads construction drawings", competitor: "No", us: "Yes" },
      { feature: "UK construction pricing", competitor: "Generic rates", us: "31 UK trade categories" },
      { feature: "Scope of works generation", competitor: "Manual entry", us: "Automatic" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes" },
      { feature: "Scheduling and CRM", competitor: "Strong", us: "Quote-focused" },
      { feature: "Free trial available", competitor: "Trial only", us: "Yes: 14-day free trial" },
      { feature: "Typical cost", competitor: "£29 to £149 per month", us: "£29 to £99 per month" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Jobber plans range from around £29 to £149 per month in the UK. The Proper Tool plans start from £29 per month with a 14-day free trial. For builders whose primary need is construction quoting rather than job scheduling, The Proper Tool delivers more relevant functionality at a comparable price.",
    switchReasons: [
      "You need to quote construction projects, not just book service appointments",
      "Your clients expect a detailed scope of works, not a simple service quote",
      "You want to be able to work from drawings without manual data entry",
      "UK construction pricing matters to you",
      "You are doing project-based work rather than recurring service jobs",
    ],
    conclusion: "Jobber is a solid platform for home service businesses with recurring work and scheduling needs. For UK builders who need detailed construction quotes from drawings, with UK pricing and proper scope of works, it does not deliver what is needed. The Proper Tool is built specifically for construction quoting and is the better choice for builders focused on winning and delivering project-based work.",
    relatedBlogPost: { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    relatedAlternatives: [
      { slug: "tradify-alternative", competitor: "Tradify" },
      { slug: "simpro-alternative", competitor: "simPRO" },
      { slug: "marketsharp-alternative", competitor: "MarketSharp" },
    ],
    relatedGuides: [
      { href: "/jobber-alternative", title: "Jobber Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "servicetitan-alternative",
    competitor: "ServiceTitan",
    title: "The Best ServiceTitan Alternative for UK Builders (2025)",
    metaTitle: "ServiceTitan Alternative UK: Construction Quoting Software",
    metaDescription: "Looking for a ServiceTitan alternative for UK builder quoting? Compare ServiceTitan vs The Proper Tool for construction estimating and quoting.",
    publishedDate: "2025-04-07",
    modifiedDate: "2026-03-20",
    intro: "ServiceTitan is an enterprise field service management platform primarily used by large HVAC, plumbing and electrical businesses. It is expensive, complex and designed for businesses with large field service operations. For UK builders who need professional construction quoting, it is almost certainly not the right tool. This page explains why and what to consider instead.",
    competitorOverview: "ServiceTitan is a US-based enterprise software platform for large field service businesses. It covers dispatching, technician management, marketing, financing and business intelligence alongside quoting and invoicing. It is used primarily by large residential service companies, not construction builders.",
    competitorLimitations: [
      "Enterprise pricing, typically tens of thousands of pounds per year",
      "Designed for large field service operations, not construction businesses",
      "US-centric with pricing and workflows not designed for the UK market",
      "Extremely complex to implement, requiring dedicated software consultants",
      "Not designed for project-based residential construction",
      "Quoting is based on service job templates, not construction scopes of works",
      "Requires months of implementation before delivering value",
    ],
    comparisonRows: [
      { feature: "Suited to small/medium builders", competitor: "No: enterprise focus", us: "Yes" },
      { feature: "UK construction pricing", competitor: "No: US-based", us: "Yes" },
      { feature: "Construction quoting", competitor: "Service job focus", us: "Built for construction" },
      { feature: "Time to first quote", competitor: "Months", us: "Under 10 minutes" },
      { feature: "Free trial available", competitor: "No", us: "Yes: 14-day free trial" },
      { feature: "Typical annual cost", competitor: "£10,000 to £50,000+", us: "£348 to £1,188" },
      { feature: "Reads drawings", competitor: "No", us: "Yes" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "ServiceTitan is priced at enterprise level, typically costing tens of thousands of pounds per year and requiring significant implementation investment on top. This is designed for large businesses with hundreds of field technicians. The Proper Tool plans start from £29 to £99 per month with a 14-day free trial, making it accessible for any size of building business.",
    switchReasons: [
      "You need a quoting tool, not an enterprise management platform",
      "Your budget for software is realistic for a small or medium builder",
      "You need to be up and quoting quickly, not after months of implementation",
      "UK construction is your market and you need UK-relevant pricing and workflows",
    ],
    conclusion: "ServiceTitan is an enterprise platform for large field service operations. For UK builders of any size who need professional construction quoting, it is the wrong tool entirely. The Proper Tool is built specifically for the UK construction market and can have you producing professional quotes on day one.",
    relatedBlogPost: { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
    relatedAlternatives: [
      { slug: "buildertrend-alternative", competitor: "Buildertrend" },
      { slug: "marketsharp-alternative", competitor: "MarketSharp" },
      { slug: "simpro-alternative", competitor: "simPRO" },
    ],
    relatedGuides: [
      { href: "/servicetitan-alternative", title: "ServiceTitan Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "marketsharp-alternative",
    competitor: "MarketSharp",
    title: "The Best MarketSharp Alternative for UK Builders (2025)",
    metaTitle: "MarketSharp Alternative UK: Builder Quoting and CRM Software",
    metaDescription: "Looking for a MarketSharp alternative in the UK? Compare MarketSharp vs The Proper Tool for construction quoting and lead management for builders.",
    publishedDate: "2025-04-14",
    modifiedDate: "2026-03-20",
    intro: "MarketSharp is a CRM and business management tool used by home improvement contractors in the US. It focuses on lead management, sales follow-up and job scheduling rather than detailed construction quoting. For UK builders who need accurate, detailed building quotes, MarketSharp is not designed for that purpose. This page covers what to look for instead.",
    competitorOverview: "MarketSharp is a US-based CRM and business management platform designed for home improvement contractors. It focuses on lead management, sales pipeline tracking, appointment scheduling and marketing automation. Its quoting functionality is basic and not designed for detailed construction estimates.",
    competitorLimitations: [
      "US-based product with no UK-specific pricing or market data",
      "Primary focus is CRM and lead management, not construction quoting",
      "Quoting module is not designed for detailed, line-item building quotes",
      "No ability to work from drawings or architectural documents",
      "Not widely used or supported in the UK market",
      "Better suited to sales-led home improvement businesses than construction builders",
    ],
    comparisonRows: [
      { feature: "UK market focus", competitor: "No: US only", us: "Yes: built for UK" },
      { feature: "Construction quoting", competitor: "Basic", us: "Full scope of works" },
      { feature: "Reads drawings", competitor: "No", us: "Yes" },
      { feature: "CRM and lead management", competitor: "Strong", us: "Basic" },
      { feature: "UK construction pricing", competitor: "No", us: "31 trade categories" },
      { feature: "Free trial available", competitor: "No", us: "Yes: 14-day free trial" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "MarketSharp is priced for the US market and typically costs several hundred dollars per month. For UK builders, there are better-value options. The Proper Tool plans start from £29 per month with a 14-day free trial and go up to £99 per month for the Pro plan.",
    switchReasons: [
      "You are a UK builder and need UK-specific construction pricing",
      "Your priority is accurate quoting rather than CRM and sales pipeline",
      "You need to work from drawings and produce detailed scopes of works",
      "You want a tool built and supported for the UK market",
    ],
    conclusion: "MarketSharp serves a different need to construction quoting, focusing primarily on CRM and lead management for US home improvement businesses. For UK builders who need detailed, accurate construction quotes, it is not the right tool. The Proper Tool is purpose-built for UK construction quoting and will deliver immediate value for builders focused on winning and pricing building work.",
    relatedBlogPost: { slug: "how-to-price-building-jobs", title: "How to Price Building Jobs: A Practical Guide for UK Builders" },
    relatedAlternatives: [
      { slug: "servicetitan-alternative", competitor: "ServiceTitan" },
      { slug: "jobber-alternative", competitor: "Jobber" },
      { slug: "tradify-alternative", competitor: "Tradify" },
    ],
    relatedGuides: [
      { href: "/marketsharp-alternative", title: "MarketSharp Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "price-the-job-alternative",
    competitor: "Price the Job",
    title: "The Best Price the Job Alternative for UK Builders (2025)",
    metaTitle: "Price the Job Alternative: Builder Quoting Software UK",
    metaDescription: "Looking for a Price the Job alternative? Compare Price the Job vs The Proper Tool for UK builder quoting, estimating and professional quote generation.",
    publishedDate: "2025-04-21",
    modifiedDate: "2026-03-20",
    intro: "Price the Job is a UK-based quoting tool for tradespeople. It is straightforward to use and produces clean quote output. For builders who need simple quoting for smaller jobs, it works well. For those dealing with complex residential construction involving structural work, extensions and detailed scopes, there are areas where The Proper Tool offers more depth. This page compares both options honestly.",
    competitorOverview: "Price the Job is a UK-based quoting and invoicing tool designed for tradespeople including builders, plumbers, electricians and decorators. It offers a clean interface, PDF quote generation and basic job management. It is popular with sole traders and small trade businesses looking for a simple, affordable solution.",
    competitorLimitations: [
      "Quoting is based on manual item entry rather than automatic scope generation",
      "No ability to read drawings, PDFs or photos to extract job specifications",
      "Pricing database covers general trade items but is not deep on construction-specific scope",
      "No built-in validation of scope completeness for complex building jobs",
      "Better suited to simpler job types than complex structural or extension work",
      "Limited support for jobs involving steelwork, foundations and structural engineering requirements",
    ],
    comparisonRows: [
      { feature: "UK market", competitor: "Yes", us: "Yes" },
      { feature: "Reads drawings and PDFs", competitor: "No", us: "Yes" },
      { feature: "Automatic scope generation", competitor: "No: manual entry", us: "Yes" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes" },
      { feature: "Construction-specific pricing", competitor: "Basic trade rates", us: "31 trade categories with UK rates" },
      { feature: "Professional PDF output", competitor: "Yes", us: "Yes" },
      { feature: "Free trial available", competitor: "Yes: limited", us: "Yes: 14-day free trial" },
      { feature: "Handles complex structural work", competitor: "Limited", us: "Strong" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Price the Job and The Proper Tool are broadly comparable on price. The Proper Tool plans run from £29 to £99 per month with a 14-day free trial. The key difference is in the depth of construction-specific functionality rather than price.",
    switchReasons: [
      "You are doing complex residential construction including extensions, structural alterations or loft conversions",
      "You want to be able to quote from drawings without manual data entry",
      "Scope completeness checking matters to you on complex jobs",
      "You need more construction-specific depth than a general trade quoting tool provides",
    ],
    conclusion: "Price the Job is a solid, affordable tool for UK tradespeople doing simpler work. For builders who regularly take on complex residential construction projects involving structural work, extensions and detailed specifications, The Proper Tool offers a level of construction-specific functionality that a general quoting tool does not. If you primarily do straightforward jobs, either will work. If your work involves complexity, The Proper Tool is the stronger choice.",
    relatedBlogPost: { slug: "ai-quoting-software-uk-builders", title: "AI Quoting Software for UK Builders: What It Does and Whether It's Worth It" },
    relatedAlternatives: [
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
      { slug: "buildxact-alternative", competitor: "Buildxact" },
      { slug: "tradify-alternative", competitor: "Tradify" },
    ],
    relatedGuides: [
      { href: "/price-the-job-alternative", title: "Price the Job Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "buildxact-alternative",
    href: "/buildxact-alternative",
    competitor: "Buildxact",
    title: "The Best Buildxact Alternative for UK Builders (2026)",
    metaTitle: "Buildxact Alternative UK: Construction Estimating Software",
    metaDescription: "Looking for a Buildxact alternative in the UK? Compare Buildxact vs The Proper Tool for construction estimating, quoting and scope generation for UK builders.",
    publishedDate: "2026-04-07",
    modifiedDate: "2026-09-19",
    intro: "Buildxact has UK plans and covers digital takeoffs, estimating and project management for residential builders. UK monthly list pricing is £119 Foundation, £209 Pro and £349 Master, with annual-equivalent prices of £89, £160 and £262. This page compares those plans with The Proper Tool.",
    competitorOverview: "Buildxact offers Digital Takeoffs, supplier catalogues and live pricing. Blu Estimate Generator is listed as a £99/month add-on on Foundation and included on higher tiers where indicated; Blu Takeoff Assistant is a £99/month add-on or included with Master. Its supplier pricing is not a universal UK construction cost book.",
    competitorLimitations: [
      "Supplier catalogues and live pricing are not the same as a universal built-in UK construction cost book",
      "Digital takeoff requires clean architectural drawings, it does not interpret PDFs or descriptions automatically",
      "No evidence of AI turning a plain-language project description into a complete priced scope",
      "Setup requires significant time to configure templates and rate schedules before the first quote",
      "Better suited to new-build and custom home builders than residential alteration and extension work",
      "Higher cost relative to the value delivered for UK small builders and sole traders",
      "The available supplier catalogues and pricing should be checked against the builder's UK workflow",
    ],
    comparisonRows: [
      { feature: "UK-specific construction pricing", competitor: "Supplier catalogues/live pricing", us: "Yes: 31 UK trade categories" },
      { feature: "AI scope generation from description", competitor: "No", us: "Yes" },
      { feature: "Reads structural drawings and PDFs", competitor: "Takeoff only", us: "Yes: full interpretation" },
      { feature: "Time to first quote", competitor: "Hours to days of setup", us: "Under 10 minutes" },
      { feature: "Suited to extension and alteration work", competitor: "Partial", us: "Yes: built for this" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in review" },
      { feature: "Free trial available", competitor: "14-day trial", us: "Yes: 14-day free trial" },
      { feature: "Typical monthly cost", competitor: "£119/£209/£349 monthly; £89/£160/£262 annual-equivalent", us: "£29 to £99" },
      { feature: "Project management features", competitor: "Yes", us: "Quote-focused" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Buildxact UK monthly list pricing is £119 Foundation, £209 Pro and £349 Master. Annual-equivalent prices are £89, £160 and £262. Feature pages advertise a 14-day trial. Pro and Master include the Onsite app, and Buildxact offers Digital Takeoffs.",
    switchReasons: [
      "You need a universal built-in UK construction cost book rather than supplier catalogues and live pricing",
      "You want to generate a full quote from a description or drawing without a manual takeoff process",
      "Your work is primarily extensions, loft conversions and structural alterations rather than ground-up new builds",
      "You want to be quoting professionally within minutes, not after days of template configuration",
      "Your budget suits a tool priced for UK small builders rather than enterprise-scale estimating software",
    ],
    conclusion: "Buildxact is a capable estimating platform for residential builders who need Digital Takeoffs, supplier catalogues, live pricing and project management. UK list pricing is £119/£209/£349 monthly or £89/£160/£262 annual-equivalent, with advanced Blu features dependent on plan or add-on. For builders who want a universal built-in UK cost book and AI-assisted quoting, The Proper Tool is a different fit.",
    relatedAlternatives: [
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
      { slug: "buildertrend-alternative", competitor: "Buildertrend" },
      { slug: "price-the-job-alternative", competitor: "Price the Job" },
    ],
    relatedGuides: [
      { href: "/buildxact-alternative", title: "Buildxact Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "estimatorxpress-alternative",
    href: "/estimatorxpress-alternative",
    competitor: "EstimatorXpress",
    title: "The Best EstimatorXpress Alternative for UK Builders (2026)",
    metaTitle: "EstimatorXpress Cloud Alternative UK: Construction Estimating Software",
    metaDescription: "Looking for an EstimatorXpress cloud alternative for UK building quotes? Compare EstimatorXpress vs The Proper Tool for faster, AI-assisted construction estimating.",
    publishedDate: "2026-05-12",
    modifiedDate: "2026-09-19",
    intro: "EstimatorXpress has been a staple of UK construction estimating for many years, particularly for house builders and residential contractors. It is thorough and UK-specific, but it carries a traditional desktop-era workflow that can feel slow for builders who want to quote quickly on the go. This page compares EstimatorXpress with The Proper Tool for builders thinking about their options in 2026.",
    competitorOverview: "EstimatorXpress is a UK-based construction estimating software package, originally developed as a desktop application and now available in a cloud version. It is particularly popular with house builders, housing associations and residential developers for producing detailed material and labour estimates. Its pricing libraries are UK-specific and cover a broad range of construction work.",
    competitorLimitations: [
      "Designed primarily for house builders and developers, not extensions or residential alterations",
      "Cloud version still follows a desktop-era workflow that requires significant manual input",
      "No AI-assisted scope generation, every line item must be entered or selected manually",
      "Pro+Take-Off supports partial/manual plan takeoff, not autonomous drawing interpretation",
      "Learning curve is steep, with formal training often recommended before productive use",
      "Quote output style is traditional and can be difficult to adapt for client-facing presentation",
      "Higher cost and ongoing support requirements compared with modern cloud tools",
    ],
    comparisonRows: [
      { feature: "UK construction pricing", competitor: "Yes: UK-specific", us: "Yes: 31 UK trade categories" },
      { feature: "AI scope generation", competitor: "No: manual entry only", us: "Yes: from description or drawings" },
      { feature: "Reads drawings and PDFs", competitor: "Pro+Take-Off: partial/manual takeoff", us: "Yes" },
      { feature: "Cloud-native workflow", competitor: "Partial: ported from desktop", us: "Yes: built for the browser" },
      { feature: "Suited to extensions and alterations", competitor: "Limited: new-build focus", us: "Yes" },
      { feature: "Time to first quote", competitor: "Days of setup and training", us: "Under 10 minutes" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in review" },
      { feature: "Free trial available", competitor: "Yes; duration not published", us: "Yes: 14-day free trial" },
      { feature: "Typical monthly cost", competitor: "£899/£1,099/£1,299 annual + VAT; perpetual options available", us: "£29 to £99 per month" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "EstimatorXpress annual subscriptions are £899 Essential, £1,099 Pro and £1,299 Pro+Take-Off plus VAT. Perpetual licences are £1,599, £2,199 and £2,799 plus £29.99/month support and updates. A free trial is offered, but its duration is not published. Pro+Take-Off supports partial/manual plan takeoff.",
    switchReasons: [
      "You want to generate quotes from drawings or a project description without lengthy manual input",
      "You are doing extension, loft conversion or alteration work rather than ground-up house building",
      "You want a genuinely cloud-native tool that works on any device, not a ported desktop application",
      "You need to be quoting immediately, not after a training course",
      "You want a client-friendly PDF output that presents your quote professionally",
    ],
    conclusion: "EstimatorXpress is a well-established UK estimating package with strong pricing libraries, particularly suited to house builders doing volume residential work. For builders working on extensions, loft conversions and alterations who want fast, AI-assisted quoting without a steep learning curve, The Proper Tool is the stronger choice. It delivers UK-specific construction pricing with the speed and simplicity of a modern cloud platform.",
    relatedAlternatives: [
      { slug: "buildxact-alternative", competitor: "Buildxact" },
      { slug: "price-the-job-alternative", competitor: "Price the Job" },
      { slug: "buildertrend-alternative", competitor: "Buildertrend" },
    ],
    relatedGuides: [
      { href: "/estimatorxpress-alternative", title: "EstimatorXpress Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "xero-alternative",
    competitor: "Xero",
    title: "Xero for Builders UK: What It Does and What You Still Need (2026)",
    metaTitle: "Xero Alternative for Builders UK: Construction Quoting vs Accounting",
    metaDescription: "Using Xero for quoting as a UK builder? See why Xero alone is not enough for construction quotes and how The Proper Tool fills the gap with AI-powered building estimates.",
    publishedDate: "2026-06-15",
    modifiedDate: "2026-06-15",
    intro: "Xero is one of the most widely used accounting platforms among UK small businesses, including builders and contractors. It handles invoicing, bank reconciliation, VAT returns and payroll well. But when builders try to use Xero to create construction quotes, they quickly hit the limits of what an accounting platform is designed to do. This page explains exactly where Xero falls short for building quotes and how The Proper Tool covers the gap.",
    competitorOverview: "Xero is a cloud-based accounting platform founded in New Zealand, now used by millions of businesses worldwide. It is particularly popular among UK small businesses, sole traders and contractors for its clean interface and strong integration with bank feeds and HMRC Making Tax Digital. Many builders already use Xero for bookkeeping and invoicing, making it a natural starting point when they look for software to handle their quoting as well.",
    competitorLimitations: [
      "Quotes are created as simple line-item lists with no concept of construction scope, trade categories or quantities",
      "No UK residential construction pricing database - rates must be entered manually every time",
      "Cannot read architectural drawings, PDFs or project photos to extract specifications",
      "No structured scope of works generation for extensions, loft conversions or structural alterations",
      "No validation of scope completeness, missing items, or quantity consistency checks",
      "Quoting in Xero is essentially a formatted invoice template, not a construction estimate",
      "No breakdown of labour, materials and plant costs per line item",
      "Client-facing PDF output is generic and not formatted for construction quotes with detailed scopes",
    ],
    comparisonRows: [
      { feature: "Construction-specific quoting", competitor: "No: accounting tool only", us: "Yes: built for builders" },
      { feature: "UK residential pricing database", competitor: "No: manual entry only", us: "31 UK trade categories" },
      { feature: "Reads drawings and PDFs", competitor: "No", us: "Yes" },
      { feature: "AI scope generation", competitor: "No", us: "Yes: from description or drawings" },
      { feature: "Labour / materials / plant breakdown", competitor: "No", us: "Yes: per line item" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in review" },
      { feature: "Professional construction PDF output", competitor: "Generic invoice template", us: "Itemised construction quote" },
      { feature: "VAT handling on quotes", competitor: "Yes", us: "Yes" },
      { feature: "Bank reconciliation and bookkeeping", competitor: "Yes", us: "No (quote-focused)" },
      { feature: "Free trial available", competitor: "14-day trial", us: "Yes: 14-day free trial" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Xero plans for UK small businesses range from around £15 to £55 per month depending on the tier. Many builders already pay for Xero for their bookkeeping and assume it covers their quoting needs too. The Proper Tool plans start from £29 per month and handle everything Xero cannot do for construction quotes - so most builders run both tools together, each doing what it does best.",
    switchReasons: [
      "You are currently writing building quotes manually in Xero and want to produce them faster and more accurately",
      "You need a structured scope of works with quantities and trade-specific line items, not just a price list",
      "You want to quote from drawings or a project description without re-entering every item by hand",
      "Your clients expect a professional construction quote, not a formatted invoice",
      "You need UK residential pricing built in so you are not guessing rates from scratch on every job",
    ],
    conclusion: "Xero is an excellent accounting tool and most UK builders who use it should keep using it for bookkeeping, invoicing and VAT. But Xero was never designed for construction quoting. It has no pricing database, no scope generation, and no ability to read drawings. The Proper Tool is purpose-built for the quoting side of a builder's workflow - producing detailed, professional construction quotes with UK pricing and AI assistance. Used together, the two tools cover the full financial workflow from first quote to final invoice.",
    relatedBlogPost: { slug: "builder-quote-app-uk", title: "The Best Builder Quote App for UK Tradespeople (2025 Guide)" },
    relatedGuides: [
      { href: "/xero-alternative", title: "Xero for Builders UK: What It Does and What You Still Need" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "quickbooks-alternative",
    competitor: "QuickBooks",
    title: "QuickBooks Alternative for UK Builders: Construction Quoting Software (2026)",
    metaTitle: "QuickBooks Alternative UK Builders: Construction Quoting vs Accounting",
    metaDescription: "Looking for a QuickBooks alternative for builder quotes in the UK? See how The Proper Tool compares for construction estimating, scopes of works and UK pricing.",
    publishedDate: "2026-06-15",
    modifiedDate: "2026-06-15",
    intro: "QuickBooks is one of the best-known accounting platforms in the UK, used by sole traders and small businesses across all sectors. Its invoicing and quoting features work well for service businesses with simple price lists. But for UK builders who need detailed construction estimates with structured scopes of works, trade-specific pricing and the ability to work from drawings, QuickBooks quickly shows its limits. This page explains what QuickBooks can and cannot do for builder quoting, and where The Proper Tool fits.",
    competitorOverview: "QuickBooks Online is a cloud accounting platform from Intuit, widely used in the UK by small businesses, self-employed tradespeople and contractors. It handles invoicing, VAT returns, expense tracking and payroll. Its quoting feature lets users create estimates and convert them to invoices, which works well for straightforward service work. In the construction sector it is often used by electricians, plumbers and general contractors for basic financial administration.",
    competitorLimitations: [
      "Estimate feature is designed for simple service quotes, not structured construction scopes of works",
      "No UK residential construction pricing database - every rate must be entered manually",
      "Cannot read architectural drawings, structural PDFs or planning documents",
      "No trade-category breakdown for labour, materials and plant across construction trades",
      "No scope completeness validation or quantity checking for construction projects",
      "Quote output is a standard invoice-style layout, not a professional construction document",
      "No AI-assisted generation of scopes from project descriptions or uploaded drawings",
      "Better suited to recurring service work than project-based residential construction",
    ],
    comparisonRows: [
      { feature: "Built for construction quoting", competitor: "No: accounting tool", us: "Yes" },
      { feature: "UK residential pricing database", competitor: "No: manual entry only", us: "31 UK trade categories" },
      { feature: "Reads drawings and PDFs", competitor: "No", us: "Yes" },
      { feature: "AI scope generation", competitor: "No", us: "Yes: from description or drawings" },
      { feature: "Labour / materials / plant breakdown", competitor: "No", us: "Yes: per line item" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in review" },
      { feature: "Professional construction PDF", competitor: "Generic estimate template", us: "Itemised construction quote" },
      { feature: "VAT handling", competitor: "Yes", us: "Yes" },
      { feature: "Accounting and bookkeeping", competitor: "Yes", us: "No (quote-focused)" },
      { feature: "Convert quote to invoice", competitor: "Yes (native)", us: "Export-based" },
      { feature: "Free trial available", competitor: "14-day trial", us: "Yes: 14-day free trial" },
      { feature: "Typical monthly cost", competitor: "£12 to £35 per month", us: "£29 to £99 per month" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "QuickBooks Online plans for UK users range from around £12 to £35 per month. The entry-level plan covers basic invoicing and estimates, which is where most builders start. The Proper Tool plans start from £29 per month and are built entirely around the construction quoting workflow. Most builders who use QuickBooks for bookkeeping find The Proper Tool handles everything QuickBooks cannot do on the quoting side, with both tools complementing each other.",
    switchReasons: [
      "You are building quotes manually in QuickBooks and want trade-specific pricing built in",
      "You need a proper scope of works with quantities and categories, not a simple price list",
      "You want to quote directly from drawings without transferring everything by hand",
      "Your clients expect a professional construction document, not a generic estimate template",
      "You want AI to help generate the scope so you spend less time on admin and more time on site",
    ],
    conclusion: "QuickBooks is a solid accounting platform and UK builders who use it for bookkeeping, VAT and invoicing should continue to do so. But its quoting feature was built for service businesses, not construction. It has no construction pricing, no scope generation, and no ability to read drawings. The Proper Tool fills exactly this gap - purpose-built for UK builder quoting with AI assistance, UK pricing, and professional PDF output. The two tools are complementary: QuickBooks for the numbers, The Proper Tool for the quotes.",
    relatedBlogPost: { slug: "quoting-tool-for-small-builders", title: "Manual Quoting vs Automated Quoting for Builders: Which Is Right for You?" },
    relatedGuides: [
      { href: "/quickbooks-alternative", title: "QuickBooks Alternative for UK Builders: Construction Quoting Software" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "fergus-alternative",
    href: "/fergus-alternative",
    competitor: "Fergus",
    title: "The Best Fergus Alternative for UK Residential Builders (2025)",
    metaTitle: "Fergus Alternative UK: Construction Quoting Software Comparison",
    metaDescription: "Looking for a Fergus alternative for UK residential builders? Compare Fergus vs The Proper Tool for construction quoting, AI scope generation, and UK pricing.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-06-29",
    intro: "Fergus is a field service management platform built for trades with multiple field technicians. For UK residential builders who need fast, accurate construction quoting from drawings or project descriptions, it is a poor fit. This page compares Fergus with The Proper Tool so you can decide which is right for your business.",
    competitorOverview: "Fergus is a New Zealand-origin field service management platform designed for trades managing multiple field technicians, such as electricians, plumbers, and HVAC engineers. It covers job scheduling, time tracking, and invoicing. Its quoting features are based on service job templates rather than construction scopes of works.",
    competitorLimitations: [
      "Designed for multi-technician field service businesses, not residential construction",
      "Quoting is based on service job templates, not structured construction scopes of works",
      "No AI scope generation from project descriptions or architectural drawings",
      "Pricing is not calibrated for UK residential construction labour and material rates",
      "Enterprise-quoted pricing with no self-serve trial",
      "Designed for businesses with 5 or more field technicians, not sole traders",
      "No scope completeness validation or quantity checking",
    ],
    comparisonRows: [
      { feature: "Built for residential construction", competitor: "No: field service focus", us: "Yes" },
      { feature: "AI scope generation from description", competitor: "No", us: "Yes" },
      { feature: "Reads structural drawings and PDFs", competitor: "No", us: "Yes: AI interpretation" },
      { feature: "UK residential construction pricing", competitor: "Not calibrated for construction", us: "31 UK trade categories" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in expert review" },
      { feature: "Multi-technician scheduling", competitor: "Yes (core feature)", us: "No (quote-focused)" },
      { feature: "Free trial available", competitor: "Limited", us: "Yes: 14-day free trial" },
      { feature: "Typical monthly cost", competitor: "Enterprise-quoted", us: "£29 to £99 per month" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Fergus pricing in the UK is enterprise-quoted on most tiers, scaling with the number of technicians. For small builders and sole traders who primarily need quoting, the cost is difficult to justify. The Proper Tool plans start from £29 per month with a 14-day free trial and no credit card required.",
    switchReasons: [
      "You are a residential builder, not a field service business managing multiple technicians",
      "You need to produce detailed construction scopes from drawings, not service job templates",
      "You want UK-calibrated construction pricing built in without configuration",
      "Your clients expect a structured scope of works rather than a simple service quote",
      "You want to trial the software immediately on real jobs without a sales process",
    ],
    conclusion: "Fergus is a well-built platform for multi-technician field service businesses with scheduling and invoicing at its core. For UK residential builders who need fast, accurate construction quoting from drawings or descriptions, it is not designed for their workflow. The Proper Tool is purpose-built for UK residential construction quoting, making it the better choice for builders doing extension, loft conversion, and structural alteration work.",
    relatedAlternatives: [
      { slug: "tradify-alternative", competitor: "Tradify" },
      { slug: "simpro-alternative", competitor: "simPRO" },
      { slug: "jobber-alternative", competitor: "Jobber" },
    ],
    relatedGuides: [
      { href: "/fergus-alternative", title: "Fergus Alternative for UK Residential Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "estimage-alternative",
    href: "/estimage-alternative",
    competitor: "esti-mate",
    title: "The Best esti-mate Alternative for UK Builders (2026)",
    metaTitle: "esti-mate Alternative UK: Smart Quoting Software",
    metaDescription: "Looking for an esti-mate alternative with browser access and Smart Quoting? Compare esti-mate vs The Proper Tool for UK residential builders.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-09-19",
    intro: "esti-mate is current UK construction estimating software at estimate.co.uk. The UK version starts at £1,515 excluding VAT, subject to an annual licence fee, and includes a 20-day free trial.",
    competitorOverview: "esti-mate provides traditional Windows estimating and takeoff. Optional BCIS Wessex pricing libraries start at £410, and optional On-Screen Take Off costs £455 for assisted PDF measurement and quantity linking.",
    competitorLimitations: [
      "Windows-based software; no native iOS or Android support is advertised",
      "No Smart Quoting or evidence of AI scope-to-priced-quote generation",
      "On-Screen Take Off is optional assisted/manual PDF measurement, not autonomous interpretation",
      "Optional BCIS Wessex pricing libraries start at £410",
      "Base licence starts at £1,515 excluding VAT and is subject to an annual licence fee",
    ],
    comparisonRows: [
      { feature: "UK construction pricing built in", competitor: "Optional BCIS Wessex libraries from £410", us: "Yes: 31 UK trade categories" },
      { feature: "Smart Quoting", competitor: "No: traditional estimating", us: "Yes" },
      { feature: "Reads structural drawings and PDFs", competitor: "Partial: optional £455 On-Screen Take Off", us: "Yes: AI interpretation" },
      { feature: "Cloud-native (no install required)", competitor: "No: Windows software", us: "Yes: browser-based" },
      { feature: "Mobile-friendly", competitor: "Partial: Windows-device access; no native iOS/Android advertised", us: "Yes" },
      { feature: "Free trial without credit card", competitor: "20-day free trial", us: "Yes: 14-day free trial" },
      { feature: "All features in one subscription", competitor: "No: optional libraries/add-on", us: "Yes" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "The UK version starts at £1,515 excluding VAT and is subject to an annual licence fee, with a 20-day free trial. Optional BCIS Wessex pricing libraries start at £410 and On-Screen Take Off costs £455. The Proper Tool starts at £29 per month with a 14-day free trial.",
    switchReasons: [
      "You want browser access and broad device compatibility so you can quote from anywhere",
      "You want to generate quotes from drawings or a description without manually selecting every item",
      "You want all core features in a single predictable monthly subscription, not optional add-ons",
      "You want Smart Quoting built around the UK residential construction market",
    ],
    conclusion: "esti-mate is a current UK construction estimating product for teams that value traditional Windows workflows, optional BCIS Wessex rates and assisted takeoff. For builders who want browser access, Smart Quoting and a single subscription covering core features, The Proper Tool is a stronger fit. The two tools serve different estimating approaches.",
    relatedAlternatives: [
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
      { slug: "buildxact-alternative", competitor: "Buildxact" },
      { slug: "easyestimate-alternative", competitor: "EasyEstimate" },
    ],
    relatedGuides: [
      { href: "/estimage-alternative", title: "esti-mate Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "pricejob-alternative",
    href: "/pricejob-alternative",
    competitor: "PriceAJob",
    title: "The Best PriceAJob Alternative for UK Builders (2025)",
    metaTitle: "PriceAJob Alternative UK: Complete Construction Quoting Software",
    metaDescription: "Looking for a PriceAJob alternative for complete construction quoting? Compare PriceAJob vs The Proper Tool for UK builders who need a full scope-of-works quote.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-09-19",
    intro: "Price A Job lists Estimating from £35.95/month and All Included at £89.75/month. It offers a free demo with no card, works on every device, and includes drawing/document management, quantity and material takeoff, blueprint import and 5,000+ material prices with nightly updates.",
    competitorOverview: "Price A Job provides cloud estimating with drawing/document management, blueprint import and quantity/material takeoff. These are useful estimating aids, though the takeoff workflow is partly manual and there is no evidence of AI turning a job scope into a priced quote.",
    competitorLimitations: [
      "Estimating includes drawing/document management and quantity/material takeoff",
      "No evidence of AI turning project descriptions or drawings into priced scopes",
      "Blueprint import and takeoff remain partly manual",
      "Published pricing is £35.95/month Estimating or £89.75/month All Included",
      "5,000+ material prices are updated nightly",
      "Free demo is offered with no card",
    ],
    comparisonRows: [
      { feature: "Complete construction quote generation", competitor: "Estimating plans available", us: "Yes: full scope of works" },
      { feature: "AI scope generation from description", competitor: "No", us: "Yes" },
      { feature: "Reads structural drawings and PDFs", competitor: "Blueprint import/takeoff (partial/manual)", us: "Yes: AI interpretation" },
      { feature: "Branded PDF quote output", competitor: "No", us: "Yes" },
      { feature: "UK-specific construction pricing", competitor: "5,000+ prices; nightly updates", us: "31 UK trade categories" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in expert review" },
      { feature: "Transparent published pricing", competitor: "£35.95/£89.75 monthly", us: "Yes: from £29/month" },
      { feature: "Free trial/demo", competitor: "Free demo, no card", us: "Yes: 14-day free trial" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Price A Job lists Estimating from £35.95/month and All Included at £89.75/month, with a free demo and no card. The Proper Tool starts at £29 per month with a 14-day free trial.",
    switchReasons: [
      "You need a complete, professional scope-of-works quote to send to a client, not a reference calculator",
      "You want to generate a quote from drawings or a description without manual calculation",
      "You need a branded PDF output that presents your business professionally",
      "You want UK-specific pricing across all 31 residential construction trade categories",
      "You want transparent pricing and a self-serve free trial before committing",
    ],
    conclusion: "PriceAJob provides useful element-level cost reference calculators for builders who want a quick ballpark on a single construction item. For builders who need a complete, professional scope-of-works quote they can send to a client, The Proper Tool is a fundamentally different and more complete solution. The two tools serve different purposes: PriceAJob for reference estimates, The Proper Tool for client-facing professional quotes.",
    relatedAlternatives: [
      { slug: "easyestimate-alternative", competitor: "EasyEstimate" },
      { slug: "estimage-alternative", competitor: "esti-mate" },
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
    ],
    relatedGuides: [
      { href: "/pricejob-alternative", title: "PriceAJob Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "buildern-alternative",
    href: "/buildern-alternative",
    competitor: "Buildern",
    title: "The Best Buildern Alternative for UK Builders (2025)",
    metaTitle: "Buildern Alternative UK: Fast Construction Quoting for Small Builders",
    metaDescription: "Looking for a Buildern alternative for UK residential builders? Compare Buildern vs The Proper Tool for small builders who need quoting without the project management overhead.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-06-29",
    intro: "Buildern is a cloud-based construction project management and estimating platform with strong Gantt chart and scheduling features. For UK small builders and sole traders who primarily need fast, accurate construction quoting rather than full project lifecycle management, this page compares both options.",
    competitorOverview: "Buildern is a US-origin cloud construction management platform designed for residential builders and project managers. It covers estimating, scheduling, budget tracking, and client communication across multi-month projects. Its estimating module is not calibrated for UK residential construction rates.",
    competitorLimitations: [
      "US-origin platform with pricing not calibrated for UK residential construction rates",
      "No AI scope generation from project descriptions or architectural drawings",
      "Built for project managers at larger firms, not small builders or sole traders",
      "Requires significant configuration before the estimating module produces UK-market output",
      "Setup and learning curve before first productive use",
      "Better suited to full project lifecycle management than fast construction quoting",
      "Cost increases with team size and feature access",
    ],
    comparisonRows: [
      { feature: "UK residential construction pricing", competitor: "Not calibrated for UK", us: "Yes: 31 UK trade categories" },
      { feature: "AI scope generation from description", competitor: "No: manual entry only", us: "Yes" },
      { feature: "Reads structural drawings and PDFs", competitor: "No", us: "Yes: AI interpretation" },
      { feature: "Suited to small builders and sole traders", competitor: "Partial: project-manager focus", us: "Yes: built for this" },
      { feature: "Gantt chart and project scheduling", competitor: "Yes (strong)", us: "No (quote-focused)" },
      { feature: "Free trial available", competitor: "Limited", us: "Yes: 14-day free trial" },
      { feature: "Time to first quote", competitor: "Setup and configuration required", us: "Under 10 minutes" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Buildern pricing varies by plan and scales with team size. For small UK builders who primarily need quoting, paying for a full project management platform is a significant overhead. The Proper Tool offers transparent per-account pricing from £29/month with a 14-day free trial and no credit card required.",
    switchReasons: [
      "You are a small builder or sole trader and your primary need is fast, accurate construction quoting",
      "You want UK-calibrated pricing built in without weeks of manual configuration",
      "You want to generate a complete quote from drawings or a description in under 10 minutes",
      "You want to trial the software on real jobs immediately without a learning curve",
      "You want transparent monthly pricing with no per-seat fees",
    ],
    conclusion: "Buildern is a capable platform for project managers and larger building firms that need integrated scheduling, Gantt views, and budget tracking across multi-month projects. For UK small builders and sole traders who primarily need fast, accurate construction quoting with UK pricing, it is more platform than the job requires. The Proper Tool is built specifically for construction quoting for UK builders.",
    relatedAlternatives: [
      { slug: "buildxact-alternative", competitor: "Buildxact" },
      { slug: "buildertrend-alternative", competitor: "Buildertrend" },
      { slug: "simpro-alternative", competitor: "simPRO" },
    ],
    relatedGuides: [
      { href: "/buildern-alternative", title: "Buildern Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "cubit-alternative",
    href: "/cubit-alternative",
    competitor: "Cubit",
    title: "The Best Cubit Alternative for UK Builders (2025)",
    metaTitle: "Cubit Alternative UK: Cloud Quoting Without the Desktop Install",
    metaDescription: "Looking for a Cubit (BSS Software) alternative for UK builders? Compare Cubit vs The Proper Tool for cloud-based AI quoting with UK pricing built in.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-06-29",
    intro: "Cubit is an Australian desktop-based estimating and takeoff software product sold in the UK via BSS Software. For UK builders who want cloud-based quoting with AI scope generation, UK-calibrated pricing, and a self-serve trial without speaking to a reseller, this page compares both options.",
    competitorOverview: "Cubit is an Australian desktop estimating and takeoff software product. In the UK it is distributed via BSS Software (bsssoftware.com), which acts as a reseller and implementation partner. Cubit requires installation on a Windows PC and has a cost library built around Australian construction rates.",
    competitorLimitations: [
      "Desktop software only: requires Windows installation, no cloud or browser access",
      "Australian pricing database not calibrated for UK residential construction rates",
      "No AI scope generation from project descriptions or uploaded drawings",
      "Sold via reseller: no self-serve trial and no published pricing",
      "Significant configuration required to adapt Australian pricing for UK use",
      "No mobile access or cross-device capability",
      "Reseller layer adds cost and reduces direct support quality",
    ],
    comparisonRows: [
      { feature: "Cloud-based (no installation)", competitor: "No: desktop software only", us: "Yes: browser-based" },
      { feature: "UK residential construction pricing", competitor: "Australian rates by default", us: "Yes: 31 UK trade categories" },
      { feature: "AI scope generation from description", competitor: "No: manual entry only", us: "Yes" },
      { feature: "Self-serve trial", competitor: "No: via reseller only", us: "Yes: 14-day free trial" },
      { feature: "Mobile-friendly", competitor: "No: desktop only", us: "Yes" },
      { feature: "Transparent pricing", competitor: "No: reseller-quoted", us: "Yes: from £29/month" },
      { feature: "Time to first quote", competitor: "Installation and UK config required", us: "Under 10 minutes" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Cubit pricing in the UK is not published. It is quoted through the BSS Software reseller and varies by licence type and support level. There is no self-serve trial. The Proper Tool starts at £29 per month with all features included and a 14-day free trial with no credit card required.",
    switchReasons: [
      "You want cloud-based quoting that works on any device without installation",
      "You want UK-calibrated pricing built in from day one, not an Australian database to rebuild",
      "You want to trial the software immediately on real jobs without going through a reseller",
      "You want transparent monthly pricing without installation costs or reseller margins",
      "You want AI scope generation from descriptions or drawings without hours of manual takeoff",
    ],
    conclusion: "Cubit is a capable desktop estimating and takeoff tool for builders who are deeply embedded in the BSS Software ecosystem. For UK builders who want cloud access, UK-calibrated pricing, a self-serve trial, and AI-assisted scope generation, The Proper Tool is a fundamentally more accessible and modern alternative.",
    relatedAlternatives: [
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
      { slug: "evaluate-alternative", competitor: "EValuate" },
      { slug: "buildxact-alternative", competitor: "Buildxact" },
    ],
    relatedGuides: [
      { href: "/cubit-alternative", title: "Cubit Alternative for UK Builders: Cloud Quoting Without the Desktop Install" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "evaluate-alternative",
    href: "/evaluate-alternative",
    competitor: "EValuate",
    title: "The Best EValuate Alternative for Small UK Builders (2025)",
    metaTitle: "EValuate Alternative UK: Self-Serve Construction Quoting for Small Builders",
    metaDescription: "Looking for an EValuate (eque2) alternative for small UK builders? Compare EValuate vs The Proper Tool for self-serve construction quoting without enterprise pricing.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-06-29",
    intro: "EValuate is enterprise estimating and cost management software developed by eque2 for large UK contractors and housebuilders. For small builders and sole traders who need self-serve construction quoting with transparent pricing and no enterprise onboarding process, this page compares both options.",
    competitorOverview: "EValuate by eque2 is an enterprise estimating platform aimed at large UK main contractors, housebuilders, and project managers with dedicated estimating departments. It supports NEC and JCT contract compliance and is used by firms such as Lovell and Kingerlee. It is demo-gated with no self-serve trial and no published pricing.",
    competitorLimitations: [
      "Enterprise software designed for 50+ person firms, not small builders or sole traders",
      "No self-serve trial: requires arranged demonstration through the eque2 sales team",
      "No published pricing: enterprise procurement budget assumed",
      "No AI scope generation from project descriptions or uploaded drawings",
      "Not suited to extension, loft conversion, or residential alteration work",
      "Requires enterprise onboarding before first productive use",
      "NEC/JCT contract focus does not match the needs of residential builders",
    ],
    comparisonRows: [
      { feature: "Suitable for small builders and sole traders", competitor: "No: enterprise focus", us: "Yes: built for this" },
      { feature: "Self-serve trial (no demo required)", competitor: "No: demo-gated only", us: "Yes: 14-day free trial" },
      { feature: "Transparent published pricing", competitor: "No: enterprise-quoted", us: "Yes: from £29/month" },
      { feature: "AI scope generation from description", competitor: "No: manual entry only", us: "Yes" },
      { feature: "Suited to extensions and alterations", competitor: "No: large contractor focus", us: "Yes: built for this work" },
      { feature: "NEC/JCT contract support", competitor: "Yes (enterprise feature)", us: "No (residential focus)" },
      { feature: "Time to first quote", competitor: "Enterprise onboarding required", us: "Under 10 minutes" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "EValuate does not publish its pricing. It is enterprise-quoted through the eque2 sales team and assumes enterprise procurement budgets. For small builders, this lack of pricing transparency makes it essentially inaccessible. The Proper Tool starts at £29 per month with transparent, published pricing and a 14-day free trial.",
    switchReasons: [
      "You are a small builder or sole trader and enterprise estimating software is not designed for you",
      "You want to trial software immediately on real jobs without going through a sales demonstration",
      "You need transparent pricing so you can make an informed decision",
      "Your work is residential extensions, loft conversions, and structural alterations, not large commercial schemes",
      "You want AI-powered scope generation from drawings or a project description",
    ],
    conclusion: "EValuate is a capable enterprise estimating platform for large UK main contractors and housebuilders. For small builders and sole traders doing residential work, it is categorically not designed for them. The Proper Tool is purpose-built for small UK builders, with self-serve access, transparent pricing, and AI-powered quoting from day one.",
    relatedAlternatives: [
      { slug: "cubit-alternative", competitor: "Cubit" },
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
      { slug: "buildxact-alternative", competitor: "Buildxact" },
    ],
    relatedGuides: [
      { href: "/evaluate-alternative", title: "EValuate Alternative for Small UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "quintadena-alternative",
    href: "/quintadena-alternative",
    competitor: "Quintadena",
    title: "The Best Quintadena / QuoteWerks Alternative for UK Builders (2025)",
    metaTitle: "Quintadena Alternative UK: Construction-Specific Quoting Software",
    metaDescription: "Looking for a Quintadena or QuoteWerks alternative for UK residential builders? Compare Quintadena vs The Proper Tool for construction-specific quoting with UK pricing built in.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-06-29",
    intro: "Quintadena is a UK agency that resells and implements QuoteWerks, a US-origin generic quoting and CRM platform. QuoteWerks is used across multiple industries, not specifically for construction. For UK residential builders who need construction-specific quoting with UK pricing, AI scope generation, and self-serve access, this page compares both options.",
    competitorOverview: "Quintadena (quintadena.co.uk) is a UK implementation agency for QuoteWerks, a US-origin generic quoting and CRM platform. QuoteWerks is used across IT, manufacturing, and professional services. It has no UK residential construction cost library, no AI scope generation, and no understanding of construction scope-of-works structure.",
    competitorLimitations: [
      "QuoteWerks is a generic quoting platform, not a construction-specific tool",
      "No UK residential construction cost library",
      "No AI scope generation from project descriptions or architectural drawings",
      "US-origin product with no UK construction pricing built in",
      "Agency-mediated access: no self-serve trial or direct signup",
      "Pricing quoted through the Quintadena agency, not published",
      "Reseller layer adds cost and reduces direct support quality",
    ],
    comparisonRows: [
      { feature: "Built for construction quoting", competitor: "No: generic quoting/CRM", us: "Yes: UK residential construction" },
      { feature: "UK construction cost library", competitor: "No", us: "31 UK trade categories" },
      { feature: "AI scope generation from description", competitor: "No: manual entry only", us: "Yes" },
      { feature: "Self-serve cloud trial", competitor: "No: agency-mediated", us: "Yes: 14-day free trial" },
      { feature: "Transparent published pricing", competitor: "Limited: agency-quoted", us: "Yes: from £29/month" },
      { feature: "US-origin product", competitor: "Yes: QuoteWerks is US-based", us: "No: UK-built" },
      { feature: "Time to first quote", competitor: "Agency engagement needed", us: "Under 10 minutes" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "Quintadena's pricing for QuoteWerks is agency-quoted and not published on their website. Getting started requires engaging with the agency. The Proper Tool starts at £29 per month with transparent, published pricing and a 14-day free trial with no credit card required.",
    switchReasons: [
      "You need a tool built specifically for UK residential construction, not a generic cross-industry quoting platform",
      "You want UK construction pricing built in, not a blank slate you configure from scratch",
      "You want direct self-serve cloud access with no agency engagement required",
      "You want transparent pricing without going through an agency quotation process",
      "You want direct support from the product team rather than a reseller",
    ],
    conclusion: "Quintadena and QuoteWerks serve businesses that need a generic quoting and CRM platform across multiple industries. For UK residential builders who need construction-specific quoting with UK pricing, AI scope generation, and self-serve access, The Proper Tool is a purpose-built alternative that delivers more relevant value from day one.",
    relatedAlternatives: [
      { slug: "tradify-alternative", competitor: "Tradify" },
      { slug: "fergus-alternative", competitor: "Fergus" },
      { slug: "simpro-alternative", competitor: "simPRO" },
    ],
    relatedGuides: [
      { href: "/quintadena-alternative", title: "Quintadena / QuoteWerks Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
  {
    slug: "easyestimate-alternative",
    href: "/easyestimate-alternative",
    competitor: "EasyEstimate",
    title: "The Best EasyEstimate Alternative for UK Builders (2025)",
    metaTitle: "EasyEstimate Alternative UK: AI Quoting with a Deeper Cost Library",
    metaDescription: "Looking for an EasyEstimate alternative with AI quoting and a deeper UK cost library? Compare EasyEstimate vs The Proper Tool for UK builders who are growing their business.",
    publishedDate: "2026-06-29",
    modifiedDate: "2026-06-29",
    intro: "EasyEstimate is positioned as the simplest UK estimating tool for sole traders who want to get started quickly with basic quoting. For builders who are growing their business, taking on more complex jobs, or wanting AI-powered quoting from drawings, this page compares EasyEstimate with The Proper Tool.",
    competitorOverview: "EasyEstimate is a UK estimating tool designed for sole traders who want the simplest possible entry into digital quoting. Its cost library is basic and its feature set is deliberately minimal. It does not offer AI scope generation, drawing upload, scope validation, or a Pricing Accuracy Index.",
    competitorLimitations: [
      "No AI scope generation from project descriptions or uploaded drawings",
      "Basic cost library with limited trade category depth",
      "No scope completeness validation or quantity checking",
      "No Pricing Accuracy Index or margin learning over time",
      "Designed for sole traders doing simple jobs, not growing businesses with complex work",
      "No labour/materials/plant split per line item",
      "Limited scalability as business complexity increases",
    ],
    comparisonRows: [
      { feature: "AI scope generation from description", competitor: "No", us: "Yes" },
      { feature: "Reads structural drawings and PDFs", competitor: "No", us: "Yes: AI interpretation" },
      { feature: "UK construction cost library depth", competitor: "Basic", us: "519 items, 31 categories" },
      { feature: "Scope completeness validation", competitor: "No", us: "Yes: built-in expert review" },
      { feature: "Labour / materials / plant split", competitor: "No", us: "Yes: per line item" },
      { feature: "Pricing Accuracy Index", competitor: "No", us: "Yes" },
      { feature: "Suited to growing businesses", competitor: "Limited: sole-trader focus", us: "Yes: scales with the business" },
      { feature: "Free trial available", competitor: "Yes", us: "Yes: 14-day free trial" },
      { feature: "Referral programme", competitor: "No", us: "Yes: earn up to £20/month per referral" },
    ],
    pricingComparison: "EasyEstimate is positioned as a low-cost entry for sole traders. The Proper Tool starts at £29 per month with a 14-day free trial and delivers significantly more capability: AI scope generation, drawing upload, a 519-item UK cost library, scope validation, and a Pricing Accuracy Index that learns from completed jobs.",
    switchReasons: [
      "You want to generate quotes from drawings or a description without manually entering every item",
      "Your jobs are becoming more complex and you need a deeper UK cost library with more trade categories",
      "You want scope completeness validation to catch items you may have missed",
      "You want pricing that learns from your actual margins and improves over time",
      "You want a 14-day free trial with no credit card to properly evaluate the tool on real jobs",
    ],
    conclusion: "EasyEstimate is the right starting point for sole traders doing simple, repetitive jobs who want the most minimal possible digital quoting tool. For builders who are growing their business, taking on more complex residential work, or wanting AI-powered quoting from drawings, The Proper Tool delivers meaningfully more value. The two tools represent different points on the capability spectrum.",
    relatedAlternatives: [
      { slug: "pricejob-alternative", competitor: "PriceAJob" },
      { slug: "estimage-alternative", competitor: "esti-mate" },
      { slug: "estimatorxpress-alternative", competitor: "EstimatorXpress" },
    ],
    relatedGuides: [
      { href: "/easyestimate-alternative", title: "EasyEstimate Alternative for UK Builders: Full Comparison" },
      { href: "/best/quoting-software-for-uk-builders", title: "Best Quoting Software for UK Builders (2026 Guide)" },
      { href: "/free-trial", title: "Start Your Free 14-Day Trial" },
      { href: "/quoting-app-for-builders", title: "Quoting App for Builders: Features, Pricing & How to Choose" },
    ],
  },
];
